(() => {
  const retainedCompletedArtwork = async runtime => {
    if (!runtime?.scene || typeof runtime.authorizeCompleted !== 'function') return null;
    const header = runtime.scene.readHeader();
    if (header.status !== 'ready' || header.payload?.editorDirty !== false || !header.payload.imageKey) return null;
    const key = header.payload.imageKey;
    const record = await runtime.scene.getImageRecord(key);
    const current = () => {
      const latest = runtime.scene.readHeader();
      return latest.status === 'ready' && latest.revision === header.revision && latest.payload?.imageKey === key
        && latest.payload.editorDirty === false && JSON.stringify(latest.payload) === JSON.stringify(header.payload);
    };
    // Historical context is a validation anchor for saved pixels, never a new
    // operational admission. A paused page can have only provisional context.
    const savedRuntime = { runtime: runtime.runtime, scene: runtime.scene, context: record?.completedRecovery?.context };
    if (!current() || !runtime.canRecoverCompleted?.(record?.completedRecovery, record)
      || record.id !== key || record.parentImageKey !== header.payload.originalImageKey
      || !window.DreamItArtworkProfiles?.exactIdentity(record.resolution, header.payload.resolution)
      || record.width !== header.payload.resolution?.editor_output_size_px?.width || record.height !== header.payload.resolution?.editor_output_size_px?.height
      || !window.DIMSceneIdentity?.matches(savedRuntime, header.payload, record, window.DIMPrintableScene)) return null;
    return { record, header, current, savedRuntime };
  };
  const offerRetainedCompletedRecovery = async (studio, runtime) => {
    const saved = await retainedCompletedArtwork(runtime);
    if (!saved) return;
    const { record } = saved;
    let departed = false;
    const current = () => {
      try { return !departed && saved.current() && !runtime.paid?.pending?.(); } catch (_) { return false; }
    };
    if (!current()) return;
    const panel = document.createElement('div');
    panel.dataset.dimCompletedRecovery = '';
    panel.className = 'ux-editor__restored';
    const note = document.createElement('p');
    note.className = 'ux-editor__help';
    note.textContent = `Your completed artwork is saved at ${record.width} × ${record.height} pixels, including your wording and layout.`;
    const button = document.createElement('button');
    button.type = 'button'; button.textContent = 'Download image';
    button.className = 'button button--secondary button--full ux-editor__download';
    const status = document.createElement('p'); status.setAttribute('role', 'status');
    status.dataset.completedRecoveryStatus = '';
    status.className = 'ux-editor__help'; status.setAttribute('aria-live', 'polite'); status.setAttribute('aria-atomic', 'true');
    panel.append(note, button, status); studio.prepend(panel);
    window.addEventListener('pagehide', () => { departed = true; button.disabled = true; }, { once: true });
    button.addEventListener('click', async () => {
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.textContent = 'Checking access to your saved artwork…';
      try {
        if (!current() || await window.DIMSceneIdentity.fingerprint(record.blob) !== record.preparedScene.pngFingerprint || !current()) throw new Error('Saved artwork changed.');
        const permission = await runtime.authorizeCompleted(record, { check: current });
        if (!current() || permission.expiresAt <= Date.now()) throw new Error('Saved artwork access expired.');
        const url = URL.createObjectURL(record.blob);
        const link = document.createElement('a'); link.href = url;
        link.download = 'dream-it-made-image.png';
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 30000);
        status.textContent = 'Image download requested. If it opens, use your browser’s Save Image option.';
      } catch (_) { if (!departed) status.textContent = 'The saved artwork could not be downloaded. Your design has been kept.'; }
      finally { button.disabled = departed; button.removeAttribute('aria-busy'); }
    });
  };
  const initialise = () => {
    document.querySelectorAll('[data-dream-studio]').forEach(async (studio) => {
      if (studio.dataset.initialised === 'true' || studio.__dimScopePending) return;
      const provisionalScope = window.DIMRuntime?.continuation?.provisionalContext || window.DIMRuntime?.context;
      const staff = provisionalScope?.purpose === 'staff-preview' || provisionalScope?.mode === 'alias' && provisionalScope.purpose === 'customer';
      let staffDisplay = null;
      if (staff) {
        studio.__dimScopePending = true;
        try {
          const verified = await window.DIMRuntime.verify();
          if (!window.DIMStorefrontDisplay?.focusedContext(window.DIMRuntime, verified) || !window.DIMRuntime.display) throw new Error('PREVIEW_UNAVAILABLE');
          staffDisplay = window.DIMRuntime.display;
          const options = window.DIMStorefrontDisplay.scopeProductOptions(studio.querySelector('[data-product-select]'), staffDisplay);
          if (!options.length) throw new Error('PREVIEW_UNAVAILABLE');
          studio.querySelector('[data-dim-studio-preview-status]')?.remove();
        } catch (_) {
          let status = studio.querySelector('[data-dim-studio-preview-status]');
          if (!status) { status = document.createElement('p'); status.dataset.dimStudioPreviewStatus = ''; status.setAttribute('role', 'status'); studio.prepend(status); }
          status.textContent = window.DIMRuntime?.runtime?.environment === 'development'
            ? 'This saved storefront is unavailable. Sign in or reopen its saved preview from Storefront Manager.'
            : 'This storefront is unavailable. Return to its home page and try again.';
          // This separate read-only owner never starts Studio or admits a
          // paused storefront. Unsupported/expired originals remain denied.
          void offerRetainedCompletedRecovery(studio, window.DIMRuntime).catch(() => {});
          studio.__dimScopePending = false;
          return;
        }
        studio.__dimScopePending = false;
      }
      studio.dataset.initialised = 'true';

      const profileClient = window.DreamItArtworkProfiles;
      const editorControls = window.DreamItEditorControls;
      let endpoint = '';
      let endpointConfigurationError = null;
      try {
        endpoint = profileClient.selectEndpoint(studio.dataset.apiEndpoint || '', studio.dataset.apiEnvironment || 'disabled');
      } catch (error) {
        endpointConfigurationError = error;
      }
      const unifiedRuntime = window.DIMRuntime;
      const sceneStorage = unifiedRuntime?.scene || null;
      const sourceIntake = sceneStorage && window.DIMSourceIntake?.create?.(sceneStorage);
      const sourceContinuation = () => sourceIntake?.read?.().continuation || null;
      const entryUsesPhoto = entry => Boolean(entry?.source
        && !(entry.sourceIntentExplicit && ['create', 'words'].includes(entry.sourceIntent)));
      if (unifiedRuntime?.enabled && (!sceneStorage || unifiedRuntime.error)) {
        endpointConfigurationError = unifiedRuntime.error || new Error('The development artwork service is unconfigured.');
        endpoint = '';
      }
      const draftKey = sceneStorage?.keys.draft || studio.dataset.draftKey || 'dim:dream-it:draft:v4';
      const draftTtl = Number(studio.dataset.draftTtl) || 7200000;
      const routeInputs = Array.from(studio.querySelectorAll('[data-route-input]'));
      const routeWorkspaces = Array.from(studio.querySelectorAll('[data-route-workspace]'));
      const routeLegal = studio.querySelector('[data-route-legal]');
      const routeLegalRows = Array.from(studio.querySelectorAll('[data-route-legal-links]'));
      const guidelinesRegion = studio.querySelector('[data-product-guidelines-region]');
      const productGuidelines = Array.from(studio.querySelectorAll('[data-product-guidelines]'));
      const variantPlacementGuides = Array.from(studio.querySelectorAll('[data-variant-placement-guide]'));
      const inspirationRegion = studio.querySelector('[data-product-inspiration-region]');
      const productInspiration = Array.from(studio.querySelectorAll('[data-product-inspiration]'));
      const productSelect = studio.querySelector('[data-product-select]');
      const selectedWorkspace = studio.querySelector('[data-selected-workspace]');
      const routePanel = studio.querySelector('[data-route-panel]');
      const routePicker = studio.querySelector('[data-route-picker]');
      let routeChoiceExpanded = !new URLSearchParams(window.location.search).has('route');
      const selectionGuidance = studio.querySelector('[data-selection-guidance]');
      const selectionIntro = studio.querySelector('[data-selection-intro]');
      const selectedSummaryPanel = studio.querySelector('[data-selected-summary]');
      const selectedName = studio.querySelector('[data-selected-name]');
      const selectedVariantName = studio.querySelector('[data-selected-variant]');
      const selectedImage = studio.querySelector('[data-selected-image]');
      const sourceIntro = studio.querySelector('[data-source-intro]');
      const sourceUploadSummary = studio.querySelector('[data-source-upload-summary]');
      const sourceSummary = studio.querySelector('[data-source-summary]');
      const sourceImage = studio.querySelector('[data-source-upload-image]');
      const sourceName = studio.querySelector('[data-source-upload-name]');
      const sourceDescription = studio.querySelector('[data-source-description]');
      const sourceDescriptionDisclosure = studio.querySelector('[data-source-description-disclosure]');
      const sourceSummaryHeading = studio.querySelector('[data-source-summary-heading]');
      const sourceMessage = studio.querySelector('[data-source-message]');
      const sourceContinue = studio.querySelector('[data-source-continue]');
      const generationControls = studio.querySelector('[data-generation-controls]');
      const productField = studio.querySelector('[data-product-field]');
      const variantField = studio.querySelector('[data-variant-field]');
      const variantLabel = studio.querySelector('[data-variant-label]');
      const variantSelect = studio.querySelector('[data-variant-select]');
      const profileStatus = studio.querySelector('[data-profile-status]');
      const noProducts = studio.querySelector('[data-no-products]');
      const ideaInput = studio.querySelector('[data-idea-input]');
      const promptOutput = studio.querySelector('[data-prompt-output]');
      const refinedField = studio.querySelector('[data-refined-field]');
      const descriptionEditor = studio.querySelector('[data-description-editor]');
      const descriptionSummary = studio.querySelector('[data-description-summary]');
      const reuseOriginal = studio.querySelector('[data-reuse-original]');
      const guidedChosen = studio.querySelector('[data-guided-chosen]');
      const guidedIdeaSummary = studio.querySelector('[data-guided-idea-summary]');
      const guidedPrice = studio.querySelector('[data-guided-price]');
      const guidedOwnImage = studio.querySelector('[data-guided-own-image]');
      const guidedBack = studio.querySelector('[data-guided-back]');
      const studioIntro = studio.querySelector('.dream-studio__intro');
      const studioEyebrow = studio.querySelector('[data-studio-eyebrow]');
      const defaultEyebrow = studioEyebrow?.textContent || '';
      const homeOf = node => ({ parent: node?.parentNode, next: node?.nextSibling });
      const introHome = homeOf(studioIntro), legalHome = homeOf(routeLegal), backHome = homeOf(guidedBack);
      const placeBefore = (node, parent, next = null) => {
        if (node && parent?.insertBefore && (node.parentNode !== parent || node.nextSibling !== next)) parent.insertBefore(node, next);
      };
      const restorePlace = (node, home) => placeBefore(node, home.parent, home.next);
      const improveButton = studio.querySelector('[data-improve-description]');
      const generateButton = studio.querySelector('[data-generate-image]');
      const tryAgainButton = studio.querySelector('[data-try-again]');
      const paidStatusButton = studio.querySelector('[data-paid-request-status]');
      const guidedIllustration = studio.querySelector('[data-guided-illustration]');
      const guidedStyle = studio.querySelector('[data-guided-style]');
      const guidedComposition = studio.querySelector('[data-guided-composition]');
      const editButton = studio.querySelector('[data-edit-description]');
      const resetButton = studio.querySelector('[data-reset-draft]');
      const descriptionResetButton = studio.querySelector('[data-reset-descriptions]');
      const descriptionResetDialog = studio.querySelector('[data-reset-descriptions-dialog]');
      const descriptionResetConfirm = studio.querySelector('[data-reset-descriptions-confirm]');
      const startAgainDialog = studio.querySelector('[data-start-again-dialog]');
      const startAgainMessage = studio.querySelector('[data-start-again-message]');
      const startAgainConfirm = studio.querySelector('[data-start-again-confirm]');
      const blockedRestoreResetButton = studio.querySelector('[data-blocked-restore-reset]');
      const replaceImageDialog = studio.querySelector('[data-replace-image-dialog]');
      const context = studio.querySelector('[data-dream-context]');
      const contextRoute = studio.querySelector('[data-context-route]');
      const contextProduct = studio.querySelector('[data-context-product]');
      const changeRouteButton = studio.querySelector('[data-change-route]');
      const changeProductButton = studio.querySelector('[data-change-product]');
      const result = studio.querySelector('[data-image-result]');
      const resultHeading = studio.querySelector('[data-result-heading]');
      const resultStatus = studio.querySelector('[data-result-status]');
      const controlStatus = studio.querySelector('[data-control-status]');
      const controlStatusText = studio.querySelector('[data-control-status-text]');
      const controlStatusSuffix = studio.querySelector('[data-control-status-suffix]');
      const controlStatusSpinner = studio.querySelector('[data-control-status-spinner]');
      const streamingPreview = studio.querySelector('[data-streaming-preview]');
      const streamingPreviewImage = studio.querySelector('[data-streaming-preview-image]');
      const generationPanel = studio.querySelector('[data-generation-panel]');
      const generationElapsed = studio.querySelector('[data-generation-elapsed]');
      const generatedImage = studio.querySelector('[data-generated-image]');
      const imagePlaceholder = studio.querySelector('[data-image-placeholder]');
      const imageActions = studio.querySelector('[data-image-actions]');
      const recoveryActions = studio.querySelector('[data-recovery-actions]');
      const securityPanel = studio.querySelector('[data-dream-security]');
      const uploadInput = studio.querySelector('[data-upload-input]');
      const uploadHeading = studio.querySelector('[data-upload-heading]');
      const uploadChooseButton = studio.querySelector('[data-upload-choose]');
      const wordsOnlyButton = studio.querySelector('[data-words-only]');
      const wordsOnlyReturn = studio.querySelector('[data-words-only-return]');
      const wordsOnlyActions = studio.querySelector('[data-words-only-actions]');
      const uploadDropzone = studio.querySelector('[data-upload-dropzone]');
      const uploadStatus = studio.querySelector('[data-upload-status]');
      const uploadStatusText = studio.querySelector('[data-upload-status-text]');
      const uploadStatusSpinner = studio.querySelector('[data-upload-status-spinner]');
      const uploadProgress = studio.querySelector('[data-upload-progress]');
      const uploadProgressBar = studio.querySelector('[data-upload-progress-bar]');
      const uploadProgressDetail = studio.querySelector('[data-upload-progress-detail]');
      const uploadProductSummary = studio.querySelector('[data-upload-product-summary]');
      const uploadProductName = studio.querySelector('[data-upload-product-name]');
      const uploadProductImage = studio.querySelector('[data-upload-product-image]');
      const createProductSummary = studio.querySelector('[data-create-product-summary]');
      const createProductName = studio.querySelector('[data-create-product-name]');
      const createProductImage = studio.querySelector('[data-create-product-image]');
      const editorShell = studio.querySelector('[data-editor-shell]');
      const editorRoot = studio.querySelector('[data-editor-root]');
      window.DIMExportDiagnostics?.attach(studio, uploadStatus);
      const editorExportButton = studio.querySelector('[data-editor-export]');
      const editorDownloadButton = studio.querySelector('[data-editor-download]');
      const editorChooseButton = studio.querySelector('[data-editor-choose]');
      const replacementChoices = studio.querySelector('[data-replacement-choices]');
      const replacementUpload = studio.querySelector('[data-replacement-upload]');
      const replacementGenerate = studio.querySelector('[data-replacement-generate]');
      const replacementCancel = studio.querySelector('[data-replacement-cancel]');
      const replacementKeep = studio.querySelector('[data-replacement-keep]');
      const descriptionInputError = studio.querySelector('[data-description-input-error]');
      const descriptionProductFirst = studio.querySelector('[data-description-product-first]');
      const generationAlternatives = studio.querySelector('[data-generation-alternatives]');
      const generationFailureMessage = studio.querySelector('[data-generation-failure-message]');
      const generationUseUpload = studio.querySelector('[data-generation-use-upload]');
      const generationUseWords = studio.querySelector('[data-generation-use-words]');
      const editorStartAgainButton = studio.querySelector('[data-editor-start-again]');
      const editorPreviewExplanation = studio.querySelector('[data-editor-preview-explanation]');
      const messages = {
        loading: studio.dataset.messageLoading,
        improving: studio.dataset.messageImproving,
        success: studio.dataset.messageSuccess,
        error: studio.dataset.messageError,
        generationReplacementError: studio.dataset.messageGenerationReplacementError,
        security: studio.dataset.messageSecurity,
        emptyHeading: studio.dataset.messageEmptyHeading,
        emptyText: studio.dataset.messageEmptyText,
        change: studio.dataset.messageChange,
        product: studio.dataset.messageProduct,
        idea: studio.dataset.messageIdea,
        promptSuccess: studio.dataset.messagePromptSuccess,
        controlProduct: studio.dataset.messageControlProduct,
        controlImproving: studio.dataset.messageControlImproving,
        controlPromptSuccess: studio.dataset.messageControlPromptSuccess,
        controlLoading: studio.dataset.messageControlLoading,
        controlLoadingSuffix: studio.dataset.messageControlLoadingSuffix,
        controlSuccess: studio.dataset.messageControlSuccess,
        controlSuccessSuffix: studio.dataset.messageControlSuccessSuffix,
        routeUnavailable: studio.dataset.messageRouteUnavailable,
        startAgain: studio.dataset.messageStartAgain,
        startAgainUpload: studio.dataset.messageStartAgainUpload,
        descriptionReset: studio.dataset.messageDescriptionReset,
        replaceImage: studio.dataset.messageReplaceImage,
        uploadChecking: studio.dataset.messageUploadChecking,
        uploadCheckingFile: studio.dataset.messageUploadCheckingFile,
        uploadCheckingData: studio.dataset.messageUploadCheckingData,
        uploadSaving: studio.dataset.messageUploadSaving,
        uploadOpening: studio.dataset.messageUploadOpening,
        uploadReady: studio.dataset.messageUploadReady,
        uploadEmpty: studio.dataset.messageUploadEmpty,
        uploadProductRequired: studio.dataset.messageUploadProductRequired,
        uploadFormatError: studio.dataset.messageUploadFormatError,
        uploadSizeError: studio.dataset.messageUploadSizeError,
        uploadDimensionsError: studio.dataset.messageUploadDimensionsError,
        uploadDecodeError: studio.dataset.messageUploadDecodeError,
        uploadStorageError: studio.dataset.messageUploadStorageError,
        uploadRemoved: studio.dataset.messageUploadRemoved,
        uploadEditorReady: studio.dataset.messageUploadEditorReady,
        uploadPreparing: studio.dataset.messageUploadPreparing,
        uploadPrepared: studio.dataset.messageUploadPrepared,
        uploadDirty: studio.dataset.messageUploadDirty,
        uploadProductChanged: studio.dataset.messageUploadProductChanged,
        uploadPrepareError: studio.dataset.messageUploadPrepareError,
        profileResolving: studio.dataset.messageProfileResolving,
        variantRequired: studio.dataset.messageVariantRequired,
        profileStale: studio.dataset.messageProfileStale,
        profileIncompatible: studio.dataset.messageProfileIncompatible,
        sourceUpload: studio.dataset.messageSourceUpload,
        sourceIdea: studio.dataset.messageSourceIdea,
        sourceStorageError: studio.dataset.messageSourceStorageError,
        sourceExpired: studio.dataset.messageSourceExpired,
        sourceReprepare: studio.dataset.messageSourceReprepare,
        sourceRegenerate: studio.dataset.messageSourceRegenerate,
        sourceNavigationError: studio.dataset.messageSourceNavigationError,
        continuePreview: studio.dataset.messageContinuePreview,
        transferPending: studio.dataset.messageTransferPending,
        previewNext: studio.dataset.messagePreviewNext
      };
      let busy = false;
      let streamingController = null;
      let resolutionController = null;
      let helperController = null;
      let ordinaryImageController = null;
      let ordinaryGenerationRetry = false;
      let activePaidRequest = null;
      let generatedRecovery = null;
      let busyOwner = null;
      let imageGenerationActive = false;
      let generationTimer = 0;
      let generationStartedAt = 0;
      const setGenerationProgress = (active) => {
        const starting = active && !imageGenerationActive;
        imageGenerationActive = active;
        window.clearTimeout(generationTimer);
        generationTimer = 0;
        if (generationPanel) generationPanel.hidden = !active;
        if (result) result.hidden = active || canIntakeSource();
        if (!active) return;
        if (starting) generationStartedAt = Date.now();
        const tick = () => {
          if (!imageGenerationActive || studio.isConnected === false) return;
          const elapsed = Math.max(0, Math.floor((Date.now() - generationStartedAt) / 1000));
          if (generationElapsed) generationElapsed.textContent = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
          generationTimer = window.setTimeout(tick, 1000);
        };
        tick();
      };
      const clearStreamingPreview = () => {
        if (streamingPreview) streamingPreview.hidden = true;
        if (streamingPreviewImage) streamingPreviewImage.removeAttribute('src');
        if (generationPanel) generationPanel.dataset.hasPreview = 'false';
      };
      const cancelStreamingPreview = () => {
        streamingController?.abort();
        streamingController = null;
        clearStreamingPreview();
        setGenerationProgress(false);
      };
      const cancelInFlightRequests = () => {
        generatedRecovery?.clear();
        const paidOwner = activePaidRequest;
        resolutionController?.abort();
        helperController?.abort();
        ordinaryImageController?.abort();
        resolutionController = null;
        helperController = null;
        ordinaryImageController = null;
        cancelStreamingPreview();
        if (paidOwner) {
          setBusy(false, paidOwner);
          finishReplacement(paidOwner);
          resetTurnstile();
        }
      };
      let currentRoute = '';
      let requestedRouteUnavailable = false;
      let previousProduct = '';
      let saveTimer = 0;
      let expiryTimer = 0;
      let draftExpiresAt = 0;
      let currentImageKey = '';
      let currentImageObjectUrl = '';
      let currentImageSource = '';
      let currentImageName = '';
      let currentImageValidForProduct = false;
      let uploadBusy = false;
      let originalImageKey = '';
      let originalRevision = 0;
      let draftRevision = 0;
      let guidedImportPending = false;
      const guidedPendingMessage = 'Preparing your selected product and design…';
      let guidedHandoffInFlight = false;
      let guidedGeneration = null;
      let guidedActionRequired = false;
      let successorRestoreBlocked = false;
      let blockedRestoreOwner = null;
      let designOrigin = 'direct';
      let guidedDetails = null;
      let guidedIdeaCompletion = null;
      // Presentation evidence from a completed Personalise idea, independent
      // of illustration provenance, artwork admission and preview approval.
      const retainedGuidedIdeaCompletion = (value, draft) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)
          || Object.keys(value).sort().join(',') !== 'brief,candidateRef,createdAt,expiresAt,idea,productId,variantId'
          || !['productId', 'variantId'].every(key => typeof value[key] === 'string' && /^[1-9][0-9]*$/.test(value[key]) && value[key] === draft[key])
          || typeof value.idea !== 'string' || !value.idea.trim() || value.idea.length > 1800 || value.idea !== draft.idea
          || typeof value.brief !== 'string' || !value.brief.trim() || value.brief.length > 10000
          || typeof value.candidateRef !== 'string' || !value.candidateRef.trim() || value.candidateRef.length > 100
          || !Number.isSafeInteger(value.createdAt) || value.createdAt < 0 || value.createdAt > Date.now()
          || !Number.isSafeInteger(value.expiresAt) || value.expiresAt <= Date.now()
          || value.expiresAt <= value.createdAt || value.expiresAt - value.createdAt > draftTtl) return null;
        return structuredClone(value);
      };
      const validatedGuidedDetails = (value) => {
        if (window.DIMSourceIntake?.validateGuidedDetails) return window.DIMSourceIntake.validateGuidedDetails(value);
        // A saved editing draft may have an unfinished composition. The handoff
        // and generation boundaries still require complete illustration details.
        const failure = !value || typeof value !== 'object' || Array.isArray(value) ? 'shape'
          : Object.keys(value).sort().join(',') !== 'compositionIntent,style' ? 'keys'
          : typeof value.style !== 'string' || !value.style.trim() || value.style.length > 80 ? 'style'
          : typeof value.compositionIntent !== 'string' ? 'composition_type'
          : value.compositionIntent.length > 1000 ? 'composition_length' : '';
        if (failure) {
          const error = new Error('The saved illustration details need to be recovered before creating another image.');
          error.code = `GUIDED_DETAILS_${failure.toUpperCase()}`;
          throw error;
        }
        return { style: value.style, compositionIntent: value.compositionIntent };
      };
      const guidedIntentFor = () => designOrigin === 'guided' && guidedDetails ? {
        subject: ideaInput?.value.trim() || '', style: guidedDetails.style,
        compositionIntent: guidedComposition ? guidedComposition.value.trim() : guidedDetails.compositionIntent
      } : null;
      const syncGuidedControls = () => {
        const guided = designOrigin === 'guided' && guidedDetails;
        if (guidedIllustration) guidedIllustration.hidden = !guided;
        if (guidedStyle) guidedStyle.textContent = guided ? guidedDetails.style : '';
        if (guidedComposition) guidedComposition.value = guided ? guidedDetails.compositionIntent : '';
        ideaInput?.setAttribute('maxlength', guided ? '1000' : '1800');
      };
      let editorState = null;
      let composition;
      let wordsCommitOwner = 0;
      let editorDirty = false;
      let imageEditor = null;
      let suggestionOwner = '';
      let suggestionControls = null;
      const refreshWordingSuggestion = () => {
        const controls = imageEditor?.textControls;
        if (!controls?.setSuggestion) return;
        const suggestionBrief = () => JSON.stringify({ ...guidedIntentFor(), letteringInstruction: window.DIMDiscovery?.ILLUSTRATION_INSTRUCTION });
        const design = designOrigin === 'guided' && guidedDetails && currentRoute === 'create' ? {
          selection: { productId: String(productSelect?.value || ''), variantId: String(variantSelect?.value || '') },
          creativeBrief: suggestionBrief()
        } : null;
        let raw = null;
        try { raw = design && sceneStorage?.keys.pending ? window.sessionStorage.getItem(`${sceneStorage.keys.pending}:recommendation:completed`) : null; } catch (_) {}
        const owner = JSON.stringify([design, originalImageKey, raw, unifiedRuntime?.context]);
        if (suggestionControls === controls && owner === suggestionOwner) return;
        suggestionOwner = owner;
        suggestionControls = controls;
        const read = () => window.DIMDiscovery?.readSuggestedWording?.(raw, {
          runtime: unifiedRuntime?.runtime, context: unifiedRuntime?.context,
          recordScope: sceneStorage?.keys.recordScope, nowMs: Date.now()
        }, design, window.DIMTextModel) || '';
        const text = read();
        const alreadyChosen = (imageEditor.getEditorState?.()?.textLayers || []).some(layer => layer.text === text);
        controls.setSuggestion(alreadyChosen ? '' : text, { isCurrent: () => !departed && !busy && !replacementPending && !forwardBusy
          && imageEditor?.ready && suggestionOwner === owner && designOrigin === 'guided'
          && suggestionBrief() === design?.creativeBrief
          && String(productSelect?.value || '') === design?.selection.productId
          && String(variantSelect?.value || '') === design?.selection.variantId && read() === text });
      };
      let editorLoading = false;
      let uploadStatusTimer = 0;
      let uploadProgressOwner = null;
      let currentResolution = null;
      let variantSelectionRequired = false;
      let currentVariantPicker = Object.freeze({ required: false, label: 'Choose Type', variants: Object.freeze([]) });
      let resolutionRevision = 0;
      let resolutionBusy = false;
      let pendingPreviousResolution = null;
      let selectionRevision = 0;
      let editorLoadQueue = Promise.resolve();
      let exportSnapshot = null;
      let forwardBusy = false;
      let forwardNavigating = false;
      let sourceNavigating = false;
      let descriptionUsed = '';
      let sourceSummaryWasVisible = false;
      let helperPromptContext = '';
      let descriptionPresentation = '';
      let descriptionReplacement = null;
      let descriptionReplacementReturn = null;
      let replacementChoicesOpen = false;
      let emptyDescriptionOwner = null;
      let generationFailure = null;
      const syncGuidedReturnLock = () => guidedBack?.setAttribute('aria-disabled', String(
        busy || uploadBusy || replacementPending || forwardBusy || Boolean(activePaidRequest)
      ));
      // Match Worker cleanText: trimmed text, excluded controls, UTF-16 length.
      const descriptionText = input => (input?.value || '').trim().replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
      const clearEmptyDescriptionError = input => {
        if (!emptyDescriptionOwner || emptyDescriptionOwner.input !== input || !descriptionText(input)) return;
        if (!emptyDescriptionOwner.previousInvalid) input.removeAttribute('aria-invalid');
        const id = descriptionInputError?.id;
        if (id) {
          const remaining = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(value => value && value !== id);
          if (remaining.length) input.setAttribute('aria-describedby', remaining.join(' '));
          else input.removeAttribute('aria-describedby');
        }
        emptyDescriptionOwner = null;
        if (descriptionInputError) { descriptionInputError.hidden = true; descriptionInputError.textContent = ''; }
      };
      const explainEmptyDescription = input => {
        if (!input) return false;
        if (emptyDescriptionOwner?.input !== input) {
          emptyDescriptionOwner = { input, previousInvalid: input.getAttribute('aria-invalid') === 'true' };
        }
        const message = "Describe the picture you'd like to create. A few words is enough to get started.";
        if (descriptionInputError) {
          descriptionInputError.textContent = message; descriptionInputError.hidden = false;
          if (descriptionInputError.id) {
            const ids = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
            ids.add(descriptionInputError.id); input.setAttribute('aria-describedby', [...ids].join(' '));
          }
        }
        input.setAttribute('aria-invalid', 'true');
        if (descriptionEditor) descriptionEditor.open = true;
        setResult('error', messages.error, message);
        queueFocus(() => input);
        return false;
      };
      const syncDescriptionCounts = () => {
        const guided = designOrigin === 'guided';
        const counters = [['[data-description-count]', ideaInput, guided ? 1000 : 1800],
          ['[data-refined-count]', promptOutput, 4000], ['[data-composition-count]', guidedComposition, 1000]];
        counters.forEach(([selector, input, limit]) => {
          const counter = studio.querySelector(selector);
          if (counter) counter.textContent = `${String(input?.value || '').length.toLocaleString('en-GB')} / ${limit.toLocaleString('en-GB')} characters`;
        });
        if (ideaInput) ideaInput.maxLength = guided ? 1000 : 1800;
      };
      const syncDescriptionPresentation = () => {
        const guided = designOrigin === 'guided' && currentRoute === 'create';
        const refined = !guided && (Boolean(promptOutput?.value.trim()) || descriptionPresentation === 'refined');
        const mode = guided ? 'guided' : refined ? 'refined' : 'original';
        if (descriptionEditor && mode !== descriptionPresentation) descriptionEditor.open = mode === 'original';
        descriptionPresentation = mode;
        syncDescriptionCounts();
        if (refinedField) refinedField.hidden = !refined;
        if (descriptionSummary) {
          descriptionSummary.hidden = mode === 'original';
          descriptionSummary.textContent = guided ? 'Edit image description' : 'Your original description';
        }
        if (reuseOriginal) { reuseOriginal.hidden = !refined; reuseOriginal.disabled = busy || replacementPending; }
        if (guidedChosen) guidedChosen.hidden = !guided;
        const guidedSource = guided && (!editorShell || editorShell.hidden);
        if (studioEyebrow) studioEyebrow.textContent = guidedSource ? 'Generate an Image with AI'
          : currentRoute === 'create' ? studioEyebrow.dataset.generateCopy || defaultEyebrow
            : currentRoute === 'upload' ? studioEyebrow.dataset.uploadCopy || defaultEyebrow
              : studioEyebrow.dataset.chooseCopy || defaultEyebrow;
        if (changeProductButton) changeProductButton.hidden = guidedSource || staff && Boolean(window.DIMStorefrontDisplay.singleSelection(staffDisplay));
        if (guidedSource) {
          placeBefore(studioIntro, selectedSummaryPanel?.parentNode, selectedSummaryPanel);
          placeBefore(guidedBack, selectedSummaryPanel);
          placeBefore(routeLegal, descriptionEditor?.parentNode, descriptionEditor);
        } else {
          restorePlace(studioIntro, introHome);
          restorePlace(guidedBack, backHome);
          restorePlace(routeLegal, legalHome);
        }
        if (guidedBack) {
          guidedBack.textContent = guidedSource ? 'Choose a Different Idea' : 'Back to ideas';
          guidedBack.classList.toggle('button', guidedSource);
          guidedBack.classList.toggle('button--secondary', guidedSource);
          syncGuidedReturnLock();
        }
        if (guidedIdeaSummary) guidedIdeaSummary.textContent = guided ? ideaInput?.value || '' : '';
        const variant = selectedVariant();
        if (guidedPrice) guidedPrice.textContent = guided && Number.isSafeInteger(variant?.price)
          ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(variant.price / 100) : '';
        if (generateButton) generateButton.textContent = guided ? 'Generate this Idea' : generateButton.dataset.defaultCopy || 'Generate an Image with AI';
        if (guidedOwnImage) {
          guidedOwnImage.hidden = !(selectedOption()?.dataset.routes || '').split(',').includes('upload');
          guidedOwnImage.disabled = busy || replacementPending || uploadBusy || forwardBusy;
        }
      };
      // Creative-text provenance only; product and artwork authority stay with the resolver.
      const promptContextFor = (resolution) => resolution ? JSON.stringify([
        productSelect?.value || '', ideaInput?.value.trim() || '', currentRoute,
        resolution.profile_key, resolution.profile_version, resolution.profile_content_checksum
      ]) : '';
      const clearRefinedPrompt = () => {
        helperPromptContext = '';
        descriptionPresentation = '';
        if (promptOutput) promptOutput.value = '';
        if (refinedField) refinedField.hidden = true;
        syncDescriptionPresentation();
      };
      let retainedUploadKey = '';
      let sourceMode = false;
      let sourceUploadReady = false;
      let restoreDraftError = '';
      let stagedSourceEntry = null;
      const stagedDraftUnchanged = (saved) => {
        try {
          const raw = storage.get();
          return Boolean(raw && JSON.stringify(JSON.parse(raw)) === JSON.stringify(saved));
        } catch (_) { return false; }
      };
      let sourceObjectUrl = '';
      let departed = false;
      let replacementPending = false;
      let replacementOwner = null;

      const beginReplacement = (owner) => {
        replacementOwner = owner;
        replacementPending = true;
        syncGuidedReturnLock();
        if (editorRoot) editorRoot.dataset.replacementPending = 'true';
        imageEditor?.setReplacementPending?.(true);
        updateProductLinks();
      };

      const finishReplacement = (owner) => {
        if (owner && replacementOwner !== owner) return;
        replacementOwner = null;
        replacementPending = false;
        syncGuidedReturnLock();
        if (editorRoot) delete editorRoot.dataset.replacementPending;
        imageEditor?.setReplacementPending?.(false);
        updateProductLinks();
      };

      const storage = {
        get() {
          try {
            if (unifiedRuntime?.enabled && !sceneStorage) return null;
            if (sceneStorage) {
              const restored = sceneStorage.readHeader();
              if (!['empty', 'ready', 'expired'].includes(restored.status)) successorRestoreBlocked = true;
              if (sourceIntake && restored.payload?.entryContinuation && restored.payload.entryContinuation.version !== 1) {
                successorRestoreBlocked = true; return null;
              }
              return restored.status === 'ready' ? JSON.stringify(restored.payload) : null;
            }
            return window.sessionStorage.getItem(draftKey);
          } catch (_) {
            return null;
          }
        },
        set(value) {
          try {
            if (successorRestoreBlocked) return false;
            if (unifiedRuntime?.enabled && !sceneStorage) return false;
            if (sceneStorage) {
              const payload = JSON.parse(value);
              sceneStorage.writeHeader(payload, payload.expiresAt);
              return true;
            }
            window.sessionStorage.setItem(draftKey, value);
            return window.sessionStorage.getItem(draftKey) === value;
          } catch (_) {
            return false;
          }
        },
        remove() {
          try {
            if (successorRestoreBlocked) return;
            if (unifiedRuntime?.enabled && !sceneStorage) return;
            if (sceneStorage) { void sceneStorage.remove(); return; }
            window.sessionStorage.removeItem(draftKey);
          } catch (_) {
            return;
          }
        }
      };

      // Only a parsed current-version failure can offer reset. The scoped owner
      // still validates runtime/schema; revision and content must remain exact
      // across the confirmation, including writes made outside this editor.
      const blockedRestoreUnchanged = () => {
        if (!successorRestoreBlocked || !blockedRestoreOwner || !sceneStorage) return false;
        try {
          const current = sceneStorage.readHeader();
          return ['ready', 'expired'].includes(current.status) && current.payload?.version === 7
            && current.revision === blockedRestoreOwner.revision
            && JSON.stringify(current.record) === blockedRestoreOwner.record
            && JSON.stringify(current.payload) === blockedRestoreOwner.payload;
        } catch (_) { return false; }
      };
      const allowBlockedRestoreReset = () => {
        if (!blockedRestoreUnchanged()) {
          setUploadStatus('error', blockedRestoreOwner
            ? 'Your saved design changed. It has been kept unchanged; reload before starting a new one.'
            : 'This saved design needs a compatible editor. It has been kept unchanged; return to the version that created it.');
          return false;
        }
        try {
          const pendingKey = sceneStorage.keys.pending;
          if (busy || uploadBusy || replacementPending || forwardBusy || activePaidRequest || guidedHandoffInFlight || unifiedRuntime?.paid?.pending?.()
            || pendingKey && (window.sessionStorage.getItem(pendingKey) || window.sessionStorage.getItem(`${pendingKey}:recommendation`))) {
            setUploadStatus('error', 'A previous request still needs a status check. Your saved design has been kept.');
            return false;
          }
        } catch (_) {
          setUploadStatus('error', 'The previous request status could not be read. Your saved design has been kept.');
          return false;
        }
        return true;
      };

      const imageDatabaseName = 'dim-dream-it';
      const imageStoreName = 'generated-images';
      const imageSessionStorageKey = `${draftKey}:image-session`;

      const createIdentifier = () => {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      };

      const getImageSessionId = () => {
        try {
          const existing = window.sessionStorage.getItem(imageSessionStorageKey);
          if (existing) return existing;
          const created = createIdentifier();
          window.sessionStorage.setItem(imageSessionStorageKey, created);
          return created;
        } catch (_) {
          return `ephemeral-${createIdentifier()}`;
        }
      };

      const imageSessionId = getImageSessionId();

      const legacyStorage = !sceneStorage && window.DIMSceneStorage?.createLegacy?.({
        indexedDB: window.indexedDB, name: imageDatabaseName, store: imageStoreName
      });
      const imageStorage = () => {
        const value = sceneStorage || legacyStorage;
        if (!value) throw new Error(messages.uploadStorageError || 'Image storage is unavailable.');
        return value;
      };
      const storageOwner = () => { const revision = selectionRevision; return { isCurrent: () => !departed && selectionRevision === revision }; };
      const putImageRecord = record => imageStorage().putImageRecord(record, storageOwner());
      const getImageRecord = id => imageStorage().getImageRecord(id, storageOwner());
      // Exact owned record cleanup remains valid after navigation; it never changes the current header.
      const deleteImageRecord = id => {
        const continuation = sourceContinuation();
        const prior = continuation?.prior?.payload;
        if ([prior?.originalImageKey, prior?.imageKey, prior?.retainedUploadKey, continuation?.source?.recordId].includes(id)) return Promise.resolve(false);
        return id ? imageStorage().deleteImageRecord(id) : Promise.resolve();
      };
      const pruneExpiredImages = () => imageStorage().pruneExpired({ isCurrent: () => !departed });

      const inspectedBlobs = new WeakMap();
      const inspectImageBlob = async (blob, { expectedMime = '', maximumBytes = 0, onProgress } = {}) => {
        const inspector = window.DreamItImageInspector;
        if (!inspector || typeof inspector.inspect !== 'function' || typeof inspector.admit !== 'function') {
          throw new Error('This image could not be decoded.');
        }
        const cached = inspectedBlobs.get(blob);
        if (cached && maximumBytes && cached.size > maximumBytes) throw new Error('This image could not be decoded.');
        const inspection = cached && (!expectedMime || cached.type === expectedMime)
          ? cached
          : await inspector.inspect(blob, { expectedMime, maximumBytes, onProgress });
        inspector.admit(inspection);
        inspectedBlobs.set(blob, inspection);
        return inspection;
      };

      const probeImageBlob = async (blob) => {
        let decoded;
        if (window.createImageBitmap) {
          try {
            decoded = await window.createImageBitmap(blob, {
              imageOrientation: 'from-image',
              premultiplyAlpha: 'default',
              colorSpaceConversion: 'default'
            });
            return { width: Number(decoded.width), height: Number(decoded.height) };
          } finally {
            decoded?.close?.();
          }
        }
        const objectUrl = URL.createObjectURL(blob);
        const image = new Image();
        try {
          return await new Promise((resolve, reject) => {
            image.onload = () => resolve({ width: Number(image.naturalWidth), height: Number(image.naturalHeight) });
            image.onerror = () => reject(new Error('This image could not be decoded.'));
            image.src = objectUrl;
          });
        } finally {
          image.onload = null;
          image.onerror = null;
          image.src = '';
          URL.revokeObjectURL(objectUrl);
        }
      };

      const probeMatchesInspection = (dimensions, inspection) => {
        return dimensions.width === inspection.width && dimensions.height === inspection.height;
      };

      const formatMessage = (template, values = {}) => Object.entries(values).reduce(
        (message, [key, value]) => message.replaceAll(`[${key}]`, String(value)),
        String(template || '')
      );

      const productName = (option = selectedOption()) => option?.textContent?.trim() || 'this product';

      const productVariants = (option = selectedOption()) => {
        try {
          const parsed = JSON.parse(option?.dataset.variants || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          return [];
        }
      };

      const sourceRoutes = () => Array.from(new Set(Array.from(productSelect?.options || [])
        .filter((option) => /^[1-9][0-9]*$/.test(option.value || '') && !option.disabled
          && option.dataset.productUrl
          && productVariants(option).some((variant) => /^[1-9][0-9]*$/.test(String(variant.id)) && variant.available === true))
        .flatMap((option) => (option.dataset.routes || '').split(',').filter((route) => ['upload', 'create'].includes(route)))));

      const selectedVariant = () => productVariants().find((variant) => (
        String(variant.id) === String(variantSelect?.value || '') && variant.available === true
      )) || null;

      const selectedSummary = () => {
        const name = hasProduct() ? productName() : '';
        const title = String(selectedVariant()?.title || '').trim();
        return { text: title && !/^default(?:\s+title)?$/i.test(title) ? `${name} – ${title}` : name, variantTitle: title };
      };

      const selectedThumbnail = (option = selectedOption()) => ({
        url: selectedVariant()?.featured_image?.src || selectedVariant()?.featured_media?.preview_image?.src || option?.dataset.productImage || ''
      });

      const uploadRule = () => ({
        maximumBytes: Math.max(1, Number(studio.dataset.uploadMaxBytes) || 104857600),
        minimumWidth: 0,
        minimumHeight: 0,
        requirement: ''
      });

      const outputSpecification = () => {
        const width = Number.parseInt(currentResolution?.editor_output_size_px?.width || '', 10);
        const height = Number.parseInt(currentResolution?.editor_output_size_px?.height || '', 10);
        if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0 || width > 10500 || height > 10500) {
          return null;
        }
        return { width, height };
      };

      const preparedFileName = (originalName) => {
        const base = String(originalName || 'uploaded-image')
          .replace(/\.[^.]+$/, '')
          .replace(/[^a-zA-Z0-9_-]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 90) || 'uploaded-image';
        return `${base}-prepared.png`;
      };

      const readableFileSize = (bytes) => {
        const megabytes = bytes / 1048576;
        return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB`;
      };

      const uploadError = (message, rule) => {
        const requirement = rule?.requirement ? ` ${rule.requirement}` : '';
        return new Error(`${message}${requirement}`.trim());
      };

      const validateStoredUploadRecord = (record, option = selectedOption()) => {
        const rule = uploadRule(option);
        const name = productName(option);
        if (!record?.blob || !(record.blob instanceof Blob)) {
          return uploadError(formatMessage(messages.uploadDecodeError, { product: name }), rule);
        }
        if (record.mimeType !== 'image/png' && record.mimeType !== 'image/jpeg') {
          return uploadError(formatMessage(messages.uploadFormatError, { product: name }), rule);
        }
        if (record.blob.size > rule.maximumBytes) {
          return uploadError(formatMessage(messages.uploadSizeError, {
            product: name,
            maximum: readableFileSize(rule.maximumBytes)
          }), rule);
        }
        if (!Number(record.width) || !Number(record.height)) {
          return uploadError(formatMessage(messages.uploadDecodeError, { product: name }), rule);
        }
        if (Number(record.width) < rule.minimumWidth || Number(record.height) < rule.minimumHeight) {
          return uploadError(formatMessage(messages.uploadDimensionsError, {
            product: name,
            dimensions: `${rule.minimumWidth} × ${rule.minimumHeight}`
          }), rule);
        }
        return null;
      };

      const validateUploadFile = async (file, option = selectedOption(), { onProgress, onDecode } = {}) => {
        const rule = uploadRule(option);
        const name = productName(option);
        if (window.DIMSourceIntake?.validateFile) {
          try {
            return await window.DIMSourceIntake.validateFile(file, { rule, requireDecode: canIntakeSource(), onProgress, onDecode,
              inspectImageBlob, probeImageBlob, probeMatchesInspection });
          } catch (error) {
            const message = error.code === 'SOURCE_FORMAT_INVALID' ? messages.uploadFormatError
              : error.code === 'SOURCE_SIZE_INVALID' ? messages.uploadSizeError
                : error.code === 'SOURCE_DIMENSIONS_INVALID' ? messages.uploadDimensionsError : messages.uploadDecodeError;
            throw uploadError(formatMessage(message, { product: name, maximum: readableFileSize(rule.maximumBytes), dimensions: `${rule.minimumWidth} × ${rule.minimumHeight}` }), rule);
          }
        }
        const extension = String(file?.name || '').split('.').pop()?.toLowerCase() || '';
        const validExtension = extension === 'png' || extension === 'jpg' || extension === 'jpeg';
        const validMimeType = file?.type === 'image/png' || file?.type === 'image/jpeg';
        if (!validExtension || !validMimeType) {
          throw uploadError(formatMessage(messages.uploadFormatError, { product: name }), rule);
        }
        if (file.size > rule.maximumBytes) {
          throw uploadError(formatMessage(messages.uploadSizeError, {
            product: name,
            maximum: readableFileSize(rule.maximumBytes)
          }), rule);
        }

        const signature = new Uint8Array(await file.slice(0, 12).arrayBuffer());
        const pngSignature = [0x89,0x50,0x4e,0x47,0x0d,0x0a,0x1a,0x0a];
        const isPng = pngSignature.every((value, index) => signature[index] === value);
        const isJpeg = signature[0] === 0xff && signature[1] === 0xd8 && signature[2] === 0xff;
        const signatureMatches = (file.type === 'image/png' && isPng && extension === 'png')
          || (file.type === 'image/jpeg' && isJpeg && (extension === 'jpg' || extension === 'jpeg'));
        if (!signatureMatches) {
          throw uploadError(formatMessage(messages.uploadFormatError, { product: name }), rule);
        }

        let inspection;
        try {
          inspection = await inspectImageBlob(file, { expectedMime: file.type, maximumBytes: rule.maximumBytes, onProgress });
          if (canIntakeSource() && (!inspection.semantic || inspection.orientation !== 1)) {
            onDecode?.();
            const decoded = await probeImageBlob(file);
            if (!probeMatchesInspection(decoded, inspection)) throw new Error('This image could not be decoded.');
          }
        } catch (_) {
          throw uploadError(formatMessage(messages.uploadDecodeError, { product: name }), rule);
        }
        if (inspection.width < rule.minimumWidth || inspection.height < rule.minimumHeight) {
          throw uploadError(formatMessage(messages.uploadDimensionsError, {
            product: name,
            dimensions: `${rule.minimumWidth} × ${rule.minimumHeight}`
          }), rule);
        }
        return { dimensions: { width: inspection.width, height: inspection.height }, inspection, rule };
      };

      const getTurnstileToken = () => studio.querySelector('[name="cf-turnstile-response"]')?.value || '';
      const resetTurnstile = () => {
        if (window.turnstile) window.turnstile.reset();
      };
      const selectedOption = () => productSelect?.options[productSelect.selectedIndex];
      const hasProduct = () => Boolean(productSelect?.value && selectedOption()?.dataset.productUrl && !selectedOption()?.disabled);
      const canIntakeSource = () => !successorRestoreBlocked && sourceMode && !hasProduct() && !requestedRouteUnavailable;
      const hasResolvedProduct = () => Boolean(
        !successorRestoreBlocked && hasProduct() && selectedVariant() && !resolutionBusy && !endpointConfigurationError && endpoint && currentResolution && outputSpecification()
        && ['upload', 'create'].includes(currentRoute)
        && String(currentResolution.shopify_variant_id) === String(variantSelect.value)
        && currentResolution.route === profileClient.normaliseRoute(currentRoute)
      );
      const validateWordsComposition = (value = composition, resolution = currentResolution) => window.DIMSceneIdentity.validateComposition(value, {
        resolution, originalImageKey: '', originalRevision: 0, now: Date.now()
      });
      const wordsRuntimeAvailable = () => unifiedRuntime?.capabilities?.includes('words-only@1') === true;
      const wordsCreationAllowed = () => {
        if (!sceneStorage || !sourceIntake || !hasResolvedProduct() || currentRoute !== 'upload'
          || unifiedRuntime?.features?.text === false || !wordsRuntimeAvailable() || !currentResolution.words_only) return false;
        try {
          const now = Date.now();
          validateWordsComposition({ kind: 'words', revision: 1, createdAt: now, expiresAt: now + draftTtl,
            capability: currentResolution.words_only });
          return true;
        } catch (_) { return false; }
      };
      const completeWords = (state = imageEditor?.getEditorState?.() || editorState) => {
        try {
          const validated = validateWordsComposition();
          const layers = window.DIMTextModel.validateLayers(state?.textLayers, { forExport: true });
          return window.DIMSceneIdentity.validateCompositionText(validated, layers, { forExport: true }) === true;
        } catch (_) { return false; }
      };
      const hasPrintableSource = () => Boolean(originalImageKey || composition);
      const previewTextIssue = () => {
        try { imageEditor?.textControls?.assertPrintable?.(); return ''; }
        catch (error) { return error.message || 'Check your text before Preview.'; }
      };
      const sourceReadyForPreview = () => !previewTextIssue()
        && (composition ? completeWords() && sourceContinuation()?.sourceIntent !== 'words' : Boolean(originalImageKey));
      const preserveWordsTarget = () => {
        if (composition && sourceIntake && !sourceContinuation()) sourceIntake.continue({ intent: 'words', sourceIntent: 'words',
          eligibility: { composition, resolution: currentResolution } });
      };
      const captureSelection = () => ({
        revision: selectionRevision,
        productId: productSelect?.value || '',
        variantId: variantSelect?.value || '',
        route: currentRoute
      });
      const capturePrintableOperation = () => window.DIMPrintableScene.captureOperation(
        window.DIMSceneIdentity.capture(unifiedRuntime, buildDraft({ editorState: imageEditor.getEditorState() }), {
          originalRevision, draftRevision: selectionRevision, editorRevision: imageEditor.revision
        })
      );
      const printableOperationMatches = (snapshot) => {
        if (!snapshot?.printable) return true;
        try { return window.DIMPrintableScene.canCommitOperation(snapshot.printable, capturePrintableOperation()); }
        catch (_) { return false; }
      };
      const selectionMatches = (snapshot, { authority = true } = {}) => Boolean(snapshot
        && !departed
        && snapshot.revision === selectionRevision
        && snapshot.productId === (productSelect?.value || '')
        && snapshot.variantId === (variantSelect?.value || '')
        && snapshot.route === currentRoute
        && (!authority || printableOperationMatches(snapshot))
        && (!snapshot.editorState || window.DIMTextModel?.editorStatesEqual(snapshot.editorState, imageEditor?.getEditorState?.() || editorState) === true)
      );
      const requireCurrentSelection = (snapshot) => {
        if (!selectionMatches(snapshot)) {
          const error = new Error(messages.uploadProductChanged);
          error.code = 'STALE_SELECTION';
          throw error;
        }
      };
      // The retained IndexedDB record is the journey authority. The rendered
      // image is deliberately hidden while either route is using the editor.
      const hasDraftImage = () => hasPrintableSource();
      const hasPreparedCurrentImage = () => Boolean(
        hasResolvedProduct() && !editorLoading
        && ['create', 'upload'].includes(currentRoute)
        && sourceReadyForPreview()
        && currentImageKey
        && currentImageValidForProduct
        && !editorDirty
      );
      const hasJourneyWork = () => Boolean(ideaInput?.value.trim() || promptOutput?.value.trim() || hasDraftImage());
      const hasProductSpecificWork = () => Boolean(promptOutput?.value.trim() || hasDraftImage());

      // Resolve the visible destination after presentation updates and native
      // dialog focus restoration. This never changes the retained journey state.
      const visibleFocusTarget = (element) => Boolean(element && element.isConnected !== false
        && !element.hidden && !element.disabled && !element.closest?.('[hidden], [inert], [aria-hidden="true"]')
        && (!element.getClientRects || element.getClientRects().length));
      let focusRevision = 0;
      const queueFocus = (resolveTarget, block = 'start') => {
        const attempt = ++focusRevision;
        const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
        schedule(() => {
          if (departed || attempt !== focusRevision || studio.isConnected === false) return;
          const target = resolveTarget();
          if (!visibleFocusTarget(target)) return;
          if (!target.matches?.('a[href], button, input, select, textarea, [tabindex]')) target.setAttribute('tabindex', '-1');
          target.focus?.({ preventScroll: true });
          const context = routeInputs.includes(target) ? studio.querySelector('[data-route-picker]')
            : selectionGuidance?.contains(target) ? selectionGuidance : target;
          (visibleFocusTarget(context) ? context : target).scrollIntoView?.({ block, behavior: 'auto' });
        });
      };
      const choicesFocusTarget = () => routeInputs.find(visibleFocusTarget)
        || [selectionGuidance?.querySelector('a[href]'), selectionGuidance, sourceIntro].find(visibleFocusTarget);
      const afterDialogFocus = (dialog, resolveTarget) => {
        if (dialog?.open) dialog.addEventListener('close', () => queueFocus(resolveTarget), { once: true });
        else queueFocus(resolveTarget);
      };
      const dialogInvokers = new WeakMap();
      const openJourneyDialog = (dialog, fallback) => {
        dialogInvokers.set(dialog, visibleFocusTarget(document.activeElement) ? document.activeElement : fallback);
        dialog.returnValue = '';
        dialog.showModal();
      };
      const confirmImageReplacement = (fallback = generateButton) => {
        if (!replaceImageDialog?.showModal) return Promise.resolve(window.confirm(messages.replaceImage));
        if (replaceImageDialog.open) return Promise.resolve(false);
        return new Promise((resolve) => {
          const close = () => {
            replaceImageDialog.removeEventListener('close', close);
            resolve(replaceImageDialog.returnValue === 'confirm');
          };
          replaceImageDialog.addEventListener('close', close);
          try { openJourneyDialog(replaceImageDialog, fallback); }
          catch (_) {
            replaceImageDialog.removeEventListener('close', close);
            resolve(window.confirm(messages.replaceImage));
          }
        });
      };
      [startAgainDialog, descriptionResetDialog, replaceImageDialog].forEach((dialog) => {
        dialog?.addEventListener('close', () => {
          const invoker = dialogInvokers.get(dialog);
          dialogInvokers.delete(dialog);
          if (!dialog.returnValue || dialog.returnValue === 'cancel') {
            queueFocus(() => visibleFocusTarget(invoker) ? invoker : choicesFocusTarget(), 'nearest');
          }
        });
      });
      const focusResult = () => queueFocus(() => {
        if (['error', 'security'].includes(result?.dataset.resultState) && visibleFocusTarget(controlStatus)) return controlStatus;
        return [studio.querySelector('[data-editor-heading]'), resultHeading, result].find(visibleFocusTarget);
      });

      const setControlStatus = (state, content) => {
        if (!controlStatus || !controlStatusText) return;
        if (!content?.message) {
          controlStatus.hidden = true;
          controlStatusText.textContent = '';
          if (controlStatusSuffix) {
            controlStatusSuffix.textContent = '';
            controlStatusSuffix.hidden = true;
          }
          if (controlStatusSpinner) controlStatusSpinner.hidden = true;
          return;
        }

        const visualState = state === 'security' ? 'error' : state;
        const isError = visualState === 'error';
        controlStatus.dataset.state = visualState;
        controlStatus.setAttribute('role', isError ? 'alert' : 'status');
        controlStatus.setAttribute('aria-live', isError ? 'assertive' : 'polite');
        controlStatusText.textContent = content.message;
        if (controlStatusSuffix) {
          controlStatusSuffix.textContent = content.suffix || '';
          controlStatusSuffix.hidden = !content.suffix;
        }
        if (controlStatusSpinner) controlStatusSpinner.hidden = visualState !== 'loading';
        controlStatus.hidden = false;
      };

      const controlMessageFor = (state, status) => {
        if (state === 'pending') return { message: status };
        if (state === 'loading') {
          if (status === messages.improving) return { message: messages.controlImproving };
          return {
            message: messages.controlLoading,
            suffix: messages.controlLoadingSuffix
          };
        }
        if (state === 'success') {
          return {
            message: messages.controlSuccess,
            suffix: messages.controlSuccessSuffix
          };
        }
        if (state === 'error' || state === 'security') {
          return { message: status === messages.product ? messages.controlProduct : status };
        }
        if (status === messages.promptSuccess) return { message: messages.controlPromptSuccess };
        return null;
      };

      const syncResultCopyVisibility = () => {
        const editorVisible = Boolean(editorShell && !editorShell.hidden && ['upload', 'create'].includes(currentRoute));
        const visibility = editorControls?.resultCopyVisibility({
          editorVisible,
          heading: resultHeading?.textContent || '',
          status: resultStatus?.textContent || ''
        }) || { headingHidden: editorVisible, statusHidden: editorVisible };
        if (resultHeading) resultHeading.hidden = visibility.headingHidden;
        if (resultStatus) resultStatus.hidden = visibility.statusHidden;
      };

      const setResult = (state, heading, status, focus = false) => {
        const imageLoading = state === 'loading' && heading === messages.loading && status === '';
        setGenerationProgress(imageLoading);
        if (!imageLoading) clearStreamingPreview();
        if (result) result.dataset.resultState = state;
        if (resultHeading) resultHeading.textContent = heading;
        if (resultStatus) resultStatus.textContent = status;
        syncResultCopyVisibility();
        const controlState = status === messages.promptSuccess ? 'success' : state;
        setControlStatus(controlState, imageLoading ? null : controlMessageFor(state, status));
        if (focus) focusResult();
      };

      const clearRetry = () => {
        if (recoveryActions) recoveryActions.hidden = true;
        if (tryAgainButton) {
          tryAgainButton.hidden = true;
          delete tryAgainButton.dataset.retryAction;
        }
      };

      const showRetry = (action) => {
        if (recoveryActions) recoveryActions.hidden = false;
        if (tryAgainButton) {
          tryAgainButton.dataset.retryAction = action;
          tryAgainButton.hidden = false;
        }
      };

      const customerErrorMessage = (error, fallback) => {
        if (!error?.paidRequestPending && ['PAID_OPERATION_ISSUE_UNCERTAIN', 'PAID_OPERATION_ISSUE_FAILED', 'PAID_OPERATION_ISSUE_INVALID'].includes(error?.code)) {
          // Issue only prepares an identity; execution starts after its acknowledgement.
          // Preserve status recovery if another attempt owns a lock, or storage is unreadable.
          let noPendingOperation = false;
          try { noPendingOperation = unifiedRuntime?.paid?.pending?.() === null; } catch (_) { /* retain conservative recovery */ }
          if (noPendingOperation) {
            if (paidStatusButton) paidStatusButton.hidden = true;
            return 'The request could not be started. Your description and saved artwork are still here.';
          }
        }
        if (error?.paidRequestPending || String(error?.code || '').startsWith('PAID_OPERATION_')) {
          if (paidStatusButton) paidStatusButton.hidden = false;
          return 'The previous request may still be running or may have completed. Check its status before starting another request. Your saved artwork is still available.';
        }
        const message = error?.message || fallback;
        const retryAfterSeconds = Number(error?.retryAfterSeconds);
        if (!Number.isInteger(retryAfterSeconds) || retryAfterSeconds <= 0) return message;
        return `${message} Try again after ${retryAfterSeconds} second${retryAfterSeconds === 1 ? '' : 's'}.`;
      };
      paidStatusButton?.addEventListener('click', async () => {
        if (!unifiedRuntime?.paid || paidStatusButton.disabled) return;
        paidStatusButton.disabled = true;
        try {
          const result = await unifiedRuntime.paid.status();
          const finished = ['not_dispatched', 'rejected', 'completed', 'completed_unavailable'].includes(result.status);
          paidStatusButton.hidden = finished;
          setResult(finished ? 'empty' : 'error', messages.error, finished
            ? 'That request has ended. Its image or description cannot be recovered here. You can deliberately start a new request; it may use another allowance.'
            : 'That request is still running or its outcome is uncertain. Your saved artwork is available. Check again before starting another request.', true);
        } catch (_) {
          setResult('error', messages.error, 'The previous request could not be confirmed. Your saved artwork is still available. Please check again later.', true);
        } finally { paidStatusButton.disabled = false; }
      });

      const clearUploadProgress = () => {
        uploadProgressOwner = null;
        if (uploadProgress) uploadProgress.hidden = true;
        uploadProgressBar?.removeAttribute('value');
        uploadProgressBar?.removeAttribute('aria-valuetext');
        if (uploadProgressDetail) uploadProgressDetail.textContent = '';
      };

      const setUploadStatus = (state, message, { clearAfter = 0, keepProgress = false } = {}) => {
        if (blockedRestoreResetButton) blockedRestoreResetButton.hidden = !blockedRestoreUnchanged();
        if (!keepProgress) clearUploadProgress();
        if (!uploadStatus || !uploadStatusText) return;
        window.clearTimeout(uploadStatusTimer);
        if (!message) {
          uploadStatus.hidden = true;
          uploadStatusText.textContent = '';
          if (uploadStatusSpinner) uploadStatusSpinner.hidden = true;
          return;
        }
        const isError = state === 'error';
        const changed = uploadStatus.hidden || uploadStatus.dataset.state !== state || uploadStatusText.textContent !== message;
        uploadStatus.dataset.state = state;
        uploadStatus.setAttribute('role', isError ? 'alert' : 'status');
        uploadStatus.setAttribute('aria-live', isError ? 'assertive' : 'polite');
        if (changed) uploadStatusText.textContent = message;
        if (uploadStatusSpinner) uploadStatusSpinner.hidden = keepProgress || state !== 'checking';
        uploadStatus.hidden = false;
        if (clearAfter > 0 && !isError) {
          uploadStatusTimer = window.setTimeout(() => setUploadStatus('', ''), clearAfter);
        }
      };

      const editorStatesMatch = (first, second) => {
        if (window.DIMTextModel) return window.DIMTextModel.editorStatesEqual(first, second);
        if (first?.textLayers?.length || second?.textLayers?.length) return false;
        if (!first || !second) return false;
        const close = (a, b) => Math.abs(Number(a) - Number(b)) < 0.000001;
        return Number(first.outputWidth) === Number(second.outputWidth)
          && Number(first.outputHeight) === Number(second.outputHeight)
          && close(first.x, second.x)
          && close(first.y, second.y)
          && close(first.scale, second.scale)
          && Number(first.rotation) === Number(second.rotation);
      };

      const preparedRecordMatches = (record, specification = outputSpecification()) => Boolean(
        specification &&
        record &&
        record.blob instanceof Blob &&
        record.source === 'edited' &&
        record.sessionId === imageSessionId &&
        String(record.productId) === String(productSelect?.value || '') &&
        profileClient?.exactIdentity(record.resolution, currentResolution) === true &&
        record.parentImageKey === originalImageKey &&
        record.mimeType === 'image/png' &&
        Number(record.width) === specification.width &&
        Number(record.height) === specification.height &&
        Number(record.editorState?.outputWidth) === specification.width &&
        Number(record.editorState?.outputHeight) === specification.height &&
        editorStatesMatch(record.editorState, editorState) &&
        (Number(record.editorState?.scale) <= 1 || record.enlargementConfirmed === true) &&
        profileClient?.isUnexpired(record) === true &&
        (!sceneStorage || window.DIMSceneIdentity?.matches(unifiedRuntime, buildDraft(), record, window.DIMPrintableScene) === true)
      );

      const setEditorVisible = (visible) => {
        studio.dataset.editorActive = visible ? 'true' : 'false';
        if (editorShell) editorShell.hidden = !visible;
        syncResultCopyVisibility();
        if (visible) {
          if (imagePlaceholder) imagePlaceholder.hidden = true;
          if (generatedImage) generatedImage.hidden = true;
          refreshWordingSuggestion();
        }
      };

      const returnToCurrentDesign = () => {
        if (!descriptionReplacement && sourceContinuation()?.prior) { void restorePreviousDesign(); return; }
        if (!descriptionReplacement || busy || replacementPending || uploadBusy || forwardBusy) return;
        const saved = descriptionReplacement;
        descriptionReplacement = null;
        if (descriptionReplacementReturn) descriptionReplacementReturn.hidden = true;
        if (ideaInput) ideaInput.value = saved.idea;
        if (promptOutput) promptOutput.value = saved.prompt;
        if (guidedComposition) guidedComposition.value = saved.composition;
        helperPromptContext = saved.helperPromptContext;
        descriptionPresentation = saved.presentation;
        setEditorVisible(true);
        updateContext();
        if (descriptionEditor) descriptionEditor.open = saved.open;
        queueFocus(() => editorChooseButton || editorRoot, 'nearest');
      };
      const editReplacementDescription = () => {
        if (!descriptionReplacement) descriptionReplacement = {
          idea: ideaInput?.value || '', prompt: promptOutput?.value || '', composition: guidedComposition?.value || '',
          helperPromptContext, presentation: descriptionPresentation, open: descriptionEditor?.open
        };
        if (!descriptionReplacementReturn && descriptionEditor?.parentNode) {
          descriptionReplacementReturn = document.createElement('button');
          descriptionReplacementReturn.type = 'button';
          descriptionReplacementReturn.className = 'button button--secondary';
          descriptionReplacementReturn.dataset.descriptionReplacementReturn = '';
          descriptionReplacementReturn.textContent = 'Keep current design';
          descriptionReplacementReturn.addEventListener('click', returnToCurrentDesign);
          descriptionEditor.parentNode.insertBefore(descriptionReplacementReturn, descriptionEditor);
          studio.addEventListener('keydown', event => {
            if (event.key === 'Escape') { event.preventDefault(); returnToCurrentDesign(); }
          });
        }
        if (descriptionReplacementReturn) descriptionReplacementReturn.hidden = false;
        setEditorVisible(false);
        updateContext();
        if (descriptionEditor) descriptionEditor.open = true;
        // Focus the description that actually owns the next explicit Create.
        queueFocus(() => activeDescriptionInput());
      };

      const handleEditorDirty = (event) => {
        if (editorLoading || !['upload', 'create'].includes(currentRoute) || !hasPrintableSource()) return;
        if (event.detail?.dirty !== true) return;
        const nextState = event.detail?.state || imageEditor?.getEditorState?.() || editorState;
        invalidatePreparation({ resetEditor: false });
        editorState = nextState;
        editorDirty = true;
        currentImageValidForProduct = false;
        setUploadStatus('success', messages.uploadDirty, { clearAfter: 3000 });
        updateProductLinks();
        if (composition && sourceContinuation()?.sourceIntent === 'words') {
          persistDraft();
          void commitWordsCandidate();
        } else saveDraft(true);
      };

      const handleEditorError = (event) => {
        const message = event.detail?.message || messages.uploadPrepareError;
        setUploadStatus('error', message);
        if (forwardBusy && !forwardNavigating) {
          setForwardBusy(false);
          updateProductLinks();
        }
        queueFocus(() => uploadStatus, 'nearest');
      };

      const showUploadProgress = (owner, phase, measurement = null) => {
        if (owner !== uploadProgressOwner || !uploadBusy || departed || !selectionMatches(owner.snapshot)) return;
        const checking = phase === 'structure' || phase === 'pixels';
        if (checking && (!owner.checking || (owner.phase === 'pixels' && phase === 'structure'))) return;
        const message = phase === 'saving' ? messages.uploadSaving
          : phase === 'opening' ? messages.uploadOpening : messages.uploadChecking;
        let detail = '';
        let percent = null;
        if (checking && measurement) {
          const { loaded, total } = measurement;
          if (!Number.isFinite(loaded) || !Number.isFinite(total) || total <= 0 || loaded < 0 || loaded > total) return;
          percent = Math.floor(loaded / total * 100);
          if (phase === owner.phase && percent < owner.percent) return;
          detail = formatMessage(phase === 'pixels' ? messages.uploadCheckingData : messages.uploadCheckingFile, { percent });
        }
        owner.phase = phase;
        owner.percent = percent;
        setUploadStatus('checking', message, { keepProgress: true });
        if (uploadProgress) uploadProgress.hidden = false;
        if (uploadProgressDetail) {
          uploadProgressDetail.textContent = detail;
          uploadProgressDetail.hidden = !detail;
        }
        if (uploadProgressBar) {
          if (percent === null) uploadProgressBar.removeAttribute('value');
          else uploadProgressBar.setAttribute('value', String(percent));
          uploadProgressBar.setAttribute('aria-label', message);
          uploadProgressBar.setAttribute('aria-valuetext', detail || message);
        }
      };

      const handleEditorExportStart = () => {
        exportSnapshot = hasResolvedProduct() && !editorLoading ? captureSelection() : null;
        if (exportSnapshot && window.DIMTextModel) exportSnapshot.editorState = window.DIMTextModel.freezeEditorState(imageEditor.getEditorState());
        if (!exportSnapshot || !['upload', 'create'].includes(currentRoute) || !sourceReadyForPreview()) return;
        setUploadStatus('checking', messages.uploadPreparing);
      };

      const handleEditorExported = async (event) => {
        const diagnosticAttempt = event.detail?.diagnosticAttempt;
        const diagnostics = window.DIMExportDiagnostics;
        diagnostics?.stage(diagnosticAttempt, 'prepared-storage');
        const snapshot = exportSnapshot;
        if (!selectionMatches(snapshot) || (sceneStorage && !snapshot?.printable) || !hasResolvedProduct() || editorLoading || !['upload', 'create'].includes(currentRoute) || !sourceReadyForPreview()) {
          diagnostics?.finish(diagnosticAttempt, new Error());
          if (sceneStorage && selectionMatches(snapshot, { authority: false })) {
            editorDirty = true;
            currentImageValidForProduct = false;
            imageEditor?.markDirty?.();
            setUploadStatus('error', messages.uploadProductChanged || messages.uploadPrepareError);
          }
          if (!forwardNavigating) {
            setForwardBusy(false);
            updateProductLinks();
          }
          return false;
        }
        const specification = outputSpecification();
        const blob = event.detail?.blob;
        const nextState = event.detail?.editorState;
        if (!specification || !(blob instanceof Blob) || Number(event.detail?.width) !== specification.width || Number(event.detail?.height) !== specification.height) {
          diagnostics?.finish(diagnosticAttempt, new Error());
          handleEditorError({ detail: { message: messages.uploadPrepareError } });
          return false;
        }
        setUploadBusy(true);
        setUploadStatus('checking', messages.uploadPreparing);
        const previousPreparedKey = currentImageKey;
        const preserveCompletedOnFailure = event.detail?.download === true && editorDirty === false && currentImageValidForProduct && Boolean(currentImageKey);
        try {
          diagnostics?.stage(diagnosticAttempt, 'prepared-storage');
          const originalRecord = composition ? null : await getImageRecord(originalImageKey);
          requireCurrentSelection(snapshot);
          if (composition) {
            validateWordsComposition();
            if (!completeWords(nextState) || JSON.stringify(event.detail.composition) !== JSON.stringify(composition)) throw new Error(messages.uploadPrepareError);
          } else if (!originalRecord || !['upload', 'generated'].includes(originalRecord.source) || !(originalRecord.blob instanceof Blob)) {
            throw new Error(messages.uploadPrepareError);
          }
          const now = Date.now();
          const continuationExpiry = unifiedRuntime?.continuation?.currentGrant?.()?.expiresAt;
          const completedExpiresAt = Math.min(now + draftTtl, draftExpiresAt || Infinity, originalRecord?.expiresAt || Infinity,
            composition?.expiresAt || Infinity, unifiedRuntime?.journeyAuthority?.expiresAt || Infinity, continuationExpiry || Infinity);
          if (!Number.isSafeInteger(completedExpiresAt) || completedExpiresAt <= now) throw new Error(messages.sourceExpired);
          const key = `${imageSessionId}:${productSelect?.value || 'unselected'}:${createIdentifier()}`;
          const preparedRecord = {
            id: key,
            sessionId: imageSessionId,
            productId: productSelect?.value || '',
            source: 'edited',
            originalName: preparedFileName(originalRecord?.originalName || 'dream-it-made-words.png'),
            parentImageKey: originalImageKey,
            ...(composition ? { composition: structuredClone(composition) } : {}),
            blob,
            mimeType: 'image/png',
            width: specification.width,
            height: specification.height,
            editorState: nextState,
            resolution: structuredClone(currentResolution),
            enlargementConfirmed: Number(nextState?.scale) > 1 ? event.detail?.enlargementConfirmed === true : false,
            createdAt: now,
            expiresAt: completedExpiresAt
          };
          if (sceneStorage) {
            if (!editorStatesMatch(snapshot.editorState, nextState)) throw new Error(messages.uploadPrepareError);
            if (!composition && snapshot.printable.originalRevision !== Number(originalRecord.createdAt)) throw new Error(messages.uploadPrepareError);
            diagnostics?.stage(diagnosticAttempt, 'fingerprint');
            const pngFingerprint = await window.DIMSceneIdentity.fingerprint(blob);
            requireCurrentSelection(snapshot);
            preparedRecord.preparedScene = window.DIMPrintableScene.makePreparedReference(
              snapshot.printable, { blobRecordId: key, pngFingerprint });
            preparedRecord.completedRecovery = unifiedRuntime?.captureCompletedRecovery?.(preparedRecord) || null;
          }
          diagnostics?.stage(diagnosticAttempt, 'prepared-storage');
          await putImageRecord(preparedRecord);
          if (!selectionMatches(snapshot)) {
            void deleteImageRecord(key).catch(() => {});
            requireCurrentSelection(snapshot);
          }
          diagnostics?.stage(diagnosticAttempt, 'draft-storage');
          if (!persistDraft({ imageKey: key, imageSource: 'edited', imageName: preparedRecord.originalName,
            editorState: nextState, editorDirty: false, expiresAt: completedExpiresAt })) {
            const retained = sceneStorage?.readHeader();
            if (retained?.status === 'ready' && retained.payload.imageKey !== key) void deleteImageRecord(key).catch(() => {});
            throw new Error(messages.uploadStorageError || messages.uploadPrepareError);
          }
          currentImageKey = key;
          currentImageSource = 'edited';
          currentImageName = preparedRecord.originalName;
          currentImageValidForProduct = true;
          editorState = nextState;
          editorDirty = false;
          if (imageEditor) imageEditor.dirty = false;
          draftExpiresAt = completedExpiresAt;
          scheduleExpiry();
          updateProductLinks();
          setUploadStatus('success', messages.uploadPrepared, { clearAfter: 3500 });
          setResult('success', messages.uploadPrepared, '');
          if (forwardBusy && event.detail?.download !== true) {
            const productUrl = selectedOption()?.dataset.productUrl || '';
            const destination = window.DreamItJourneyRoutes?.reviewUrl?.({
              productUrl,
              variantId: currentResolution.shopify_variant_id,
              route: currentRoute,
              origin: window.location.origin,
              family: studio.dataset.journeyFamily
            }) || '';
            if (!destination || !persistDraft()) throw new Error(messages.uploadStorageError || messages.uploadPrepareError);
            diagnostics?.stage(diagnosticAttempt, 'transition');
            if (unifiedRuntime) await unifiedRuntime.prepareTransition(new URL(destination, window.location.origin).pathname);
            requireCurrentSelection(snapshot);
            diagnostics?.stage(diagnosticAttempt, 'complete');
            diagnostics?.finish(diagnosticAttempt);
            forwardNavigating = true;
            window.location.assign(destination);
          }
          diagnostics?.stage(diagnosticAttempt, 'complete');
          diagnostics?.finish(diagnosticAttempt);
          if (previousPreparedKey && previousPreparedKey !== key && previousPreparedKey !== originalImageKey) {
            void deleteImageRecord(previousPreparedKey).catch(() => {});
          }
          return true;
        } catch (error) {
          diagnostics?.finish(diagnosticAttempt, error);
          if (!selectionMatches(snapshot, { authority: false })) return;
          if (!preserveCompletedOnFailure) {
            editorDirty = true;
            currentImageValidForProduct = false;
            imageEditor?.markDirty?.();
          }
          setUploadStatus('error', error?.message || messages.uploadPrepareError);
          queueFocus(() => selectionMatches(snapshot) ? uploadStatus : null, 'nearest');
          if (!forwardNavigating) setForwardBusy(false);
          updateProductLinks();
          if (!preserveCompletedOnFailure) persistDraft();
          return false;
        } finally {
          setUploadBusy(false);
          if (!forwardNavigating && forwardBusy) {
            setForwardBusy(false);
            updateProductLinks();
          }
        }
      };

      const admissionMatchesResolution = (admission, resolution, productId, variantId) => {
        const identity = admission?.textPlacement?.identity;
        return Boolean(identity && admission.selection?.productId === productId && admission.selection.variantId === variantId
          && profileClient.exactIdentity(admission.resolution, resolution)
          && identity.productId === productId && identity.variantId === variantId
          && identity.route === resolution.route && identity.profileKey === resolution.profile_key
          && identity.profileVersion === resolution.profile_version && identity.profileContentChecksum === resolution.profile_content_checksum
          && identity.profileChecksum === resolution.profile_checksum
          && identity.outputWidth === resolution.editor_output_size_px.width && identity.outputHeight === resolution.editor_output_size_px.height);
      };

      const ensureImageEditor = () => {
        if (imageEditor) return imageEditor;
        if (!editorRoot || typeof window.DreamItImageEditor !== 'function') {
          throw new Error(messages.uploadPrepareError);
        }
        imageEditor = new window.DreamItImageEditor(editorRoot);
        if (sceneStorage) imageEditor.setExportAdmission?.(async () => {
          const captured = captureSelection();
          if (!hasResolvedProduct()) return false;
          const admission = await unifiedRuntime.requireAdmission({ productId: captured.productId, variantId: captured.variantId, route: captured.route,
            ...(composition ? { treatment: 'words' } : {}) }, { currentPermission: true });
          if (!selectionMatches(captured) || !hasResolvedProduct()) return false;
          const placement = admission.textPlacement;
          const identity = placement?.identity;
          const resolution = currentResolution;
          if (!admissionMatchesResolution(admission, resolution, captured.productId, captured.variantId)) return false;
          if (composition) {
            if (!wordsRuntimeAvailable()) return false;
            try { validateWordsComposition(composition, admission.resolution); } catch (_) { return false; }
          }
          imageEditor.setTextPlacementConstraints({ schemaVersion: placement.schemaVersion, productId: imageEditor.productId,
            outputWidth: identity.outputWidth, outputHeight: identity.outputHeight, status: placement.status, rectPx: placement.rectPx });
          if (imageEditor.exportInFlight || imageEditor.downloadInFlight) {
            if (!selectionMatches(exportSnapshot)) return false;
            exportSnapshot.printable = capturePrintableOperation();
          }
          return true;
        });
        imageEditor.setCompletedOutputHandler?.(detail => handleEditorExported({ detail }));
        imageEditor.setCompletedOutputRecovery?.(async ({ check }) => {
          const snapshot = captureSelection();
          const saved = await retainedCompletedArtwork(unifiedRuntime);
          const current = () => {
            try {
              return saved && !departed && selectionMatches(snapshot) && saved.current() && check()
                && !editorDirty && imageEditor?.dirty !== true && currentImageKey === saved.record.id
                && !busy && !uploadBusy && !forwardBusy && !replacementPending && !activePaidRequest && !unifiedRuntime.paid?.pending?.()
                && window.DIMSceneIdentity.matches(saved.savedRuntime, buildDraft({ editorState: imageEditor.getEditorState() }), saved.record, window.DIMPrintableScene);
            } catch (_) { return false; }
          };
          if (!current() || await window.DIMSceneIdentity.fingerprint(saved.record.blob) !== saved.record.preparedScene.pngFingerprint || !current()) return null;
          const permission = await unifiedRuntime.authorizeCompleted(saved.record, { check: current });
          if (!current() || permission.expiresAt <= Date.now()) return null;
          return saved.record.blob;
        });
        if (replacementPending) imageEditor.setReplacementPending?.(true);
        editorRoot.addEventListener('dreamit:editor-dirty', handleEditorDirty);
        editorRoot.addEventListener('dreamit:editor-error', handleEditorError);
        editorRoot.addEventListener('dreamit:editor-export-start', handleEditorExportStart);
        editorRoot.addEventListener('dreamit:editor-download-start', handleEditorExportStart);
        editorRoot.addEventListener('dreamit:editor-exported', handleEditorExported);
        editorRoot.addEventListener('dreamit:editor-choose', openReplacementChoices);
        editorRoot.addEventListener('dreamit:editor-change-picture', () => {
          if (busy || replacementPending || uploadBusy || forwardBusy) return;
          if (currentRoute === 'upload') chooseUploadImage();
          else editReplacementDescription();
        });
        editorRoot.addEventListener('dreamit:editor-change-product', () => {
          if (!busy && !replacementPending && !uploadBusy && !forwardBusy) changeProductButton?.click?.();
        });
        return imageEditor;
      };

      const createBaselineFor = (record, specification, resolution) => {
        const sourceSize = record?.source === 'generated' ? record.generationSizePx : null;
        if (!sourceSize || !Number.isInteger(Number(sourceSize.width)) || Number(sourceSize.width) <= 0
          || !Number.isInteger(Number(sourceSize.height)) || Number(sourceSize.height) <= 0) return null;
        return {
          sourceSizePx: { width: Number(sourceSize.width), height: Number(sourceSize.height) },
          outputSizePx: { width: specification.width, height: specification.height },
          initialPlacement: resolution.editor_initial_placement || 'fit'
        };
      };

      const prepareReplacementEditor = async (record, snapshot, restoredState = null) => {
        if (!hasResolvedProduct()) return null;
        const specification = outputSpecification();
        const resolution = structuredClone(currentResolution);
        const editor = ensureImageEditor();
        if (typeof editor.prepareLoad !== 'function' || typeof editor.commitPreparedLoad !== 'function'
          || typeof editor.discardPreparedLoad !== 'function') throw new Error(messages.uploadPrepareError);
        editorLoading = true;
        let candidate = null;
        try {
          candidate = await editor.prepareLoad({
            blob: record.blob,
            ...(record.composition ? { composition: record.composition, resolution } : {}),
            productId: snapshot.productId,
            outputWidth: specification.width,
            outputHeight: specification.height,
            restoredState,
            productName: productName(),
            sourceRoute: snapshot.route,
            outputPhysicalSizeMm: resolution.production_artboard_mm || null,
             ppi: resolution.ppi || null,
             initialPlacement: resolution.editor_initial_placement || 'fit',
             createBaseline: createBaselineFor(record, specification, resolution),
             inspection: inspectedBlobs.get(record.blob) || null
          });
          requireCurrentSelection(snapshot);
          return { editor, candidate, specification };
        } catch (error) {
          if (candidate) {
            try { editor.discardPreparedLoad(candidate); } catch (_) { /* The retained editor remains authoritative. */ }
          }
          if (error?.code === 'STALE_SELECTION') throw error;
          throw new Error(messages.uploadPrepareError);
        } finally {
          editorLoading = false;
        }
      };

      const discardReplacementEditor = (prepared) => {
        if (!prepared) return;
        try { prepared.editor.discardPreparedLoad(prepared.candidate); } catch (_) { /* The retained editor remains authoritative. */ }
      };

      const restoreStoredDraft = (value) => {
        if (value === null) storage.remove();
        else storage.set(value);
      };

      const commitSourceReplacement = async ({ record, snapshot, preparedEditor = null, description = '', retainUpload = false, expectedSourceRevision }) => {
        requireCurrentSelection(snapshot);
        const staged = stagedSourceEntry;
        if (staged && (!staged.confirmed || !stagedDraftUnchanged(staged.draft))) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
        const previousDraft = storage.get();
        const previousKeys = staged
          ? [staged.draft.originalImageKey, staged.draft.imageKey, staged.draft.retainedUploadKey]
          : [originalImageKey, currentImageKey, retainedUploadKey];
        const continuation = sourceContinuation();
        const expiresAt = continuation ? record.expiresAt : profileClient.createExpiry(Date.now(), draftTtl);
        const nextEditorState = preparedEditor?.candidate?.editorState || null;
        const draftOverrides = {
          expiresAt,
          descriptionUsed: description,
          originalImageKey: record.id,
          originalRevision: record.composition ? 0 : Number(record.createdAt),
          composition: record.composition ? structuredClone(record.composition) : undefined,
          retainedUploadKey: record.composition ? '' : retainUpload ? record.id : retainedUploadKey,
          imageKey: '',
          imageSource: record.source,
          imageName: record.originalName,
          editorState: nextEditorState,
          editorDirty: Boolean(preparedEditor)
        };
        let adoptedSelection = null;
        const adoptState = () => {
          descriptionReplacement = null;
          if (descriptionReplacementReturn) descriptionReplacementReturn.hidden = true;
          try { cancelStreamingPreview(); } catch (_) { /* Adoption is already committed. */ }
          selectionRevision += 1;
          exportSnapshot = null;
          originalImageKey = record.id;
          originalRevision = record.composition ? 0 : Number(record.createdAt);
          composition = record.composition ? structuredClone(record.composition) : undefined;
          if (composition) retainedUploadKey = '';
          if (retainUpload) retainedUploadKey = record.id;
          currentImageKey = '';
          currentImageSource = record.source;
          currentImageName = record.originalName;
          currentImageValidForProduct = false;
          descriptionUsed = description;
          editorState = nextEditorState;
          editorDirty = Boolean(preparedEditor);
          draftExpiresAt = expiresAt;
          try { scheduleExpiry(); } catch (_) { /* The persisted expiry remains authoritative. */ }
          if (preparedEditor) {
            preparedEditor.editor.dirty = true;
            try {
              if (editorRoot) {
                editorRoot.style.setProperty('--dream-editor-aspect', `${preparedEditor.specification.width} / ${preparedEditor.specification.height}`);
                editorRoot.dataset.editorState = 'dirty';
              }
              setEditorVisible(true);
            } catch (_) { /* Editor adoption cannot be rolled back by a presentation hook. */ }
          }
          if (currentImageObjectUrl) {
            try { URL.revokeObjectURL(currentImageObjectUrl); } catch (_) { /* Browser cleanup is best effort. */ }
            currentImageObjectUrl = '';
          }
            adoptedSelection = captureSelection();
        };
        if (staged) stagedSourceEntry = null;
        if (continuation) {
          if (!preparedEditor) throw new Error(messages.uploadPrepareError);
          const revision = expectedSourceRevision ?? sourceIntake.read().revision;
          await sourceIntake.commit({ payload: buildDraft({ ...draftOverrides, draftRevision: draftRevision + 1 }), expiresAt, expectedRevision: revision,
            validated: true, loaded: true, persisted: true, isCurrent: () => selectionMatches(snapshot),
            adoptPrepared: () => { preparedEditor.editor.commitPreparedLoad(preparedEditor.candidate); draftRevision += 1; adoptState(); } });
        } else if (!persistDraft(draftOverrides)) {
          stagedSourceEntry = staged;
          throw Object.assign(new Error(messages.sourceStorageError || messages.uploadStorageError), { code: 'DRAFT_HEADER_WRITE_FAILED' });
        }
        try {
          // Persisted candidate state is still provisional until the same active
          // selection adopts the already-decoded editor candidate.
          if (!continuation) requireCurrentSelection(snapshot);
          if (preparedEditor && !continuation) preparedEditor.editor.commitPreparedLoad(preparedEditor.candidate);
        } catch (error) {
          if (selectionMatches(snapshot)) restoreStoredDraft(previousDraft);
          stagedSourceEntry = staged;
          if (error?.code === 'STALE_SELECTION') throw error;
          throw new Error(messages.uploadPrepareError);
        }

        if (!continuation) adoptState();

        const retainedKeys = new Set([record.id, retainedUploadKey].filter(Boolean));
        Array.from(new Set((continuation ? [] : previousKeys).filter((key) => key && !retainedKeys.has(key))))
          .forEach((key) => void deleteImageRecord(key).catch(() => {}));
        return adoptedSelection;
      };

      const loadUploadEditor = async (record, restoredState = null, dirty = true) => {
        if (!hasResolvedProduct()) throw new Error(messages.uploadPrepareError);
        const snapshot = captureSelection();
        const specification = outputSpecification();
        const resolution = structuredClone(currentResolution);
        const pending = editorLoadQueue.catch(() => {}).then(async () => {
          requireCurrentSelection(snapshot);
          const editor = ensureImageEditor();
          editorLoading = true;
          setEditorVisible(true);
          if (editorRoot) editorRoot.style.setProperty('--dream-editor-aspect', `${specification.width} / ${specification.height}`);
          try {
            await editor.load({
              blob: record.blob,
              ...(record.composition ? { composition: record.composition, resolution } : {}),
              productId: snapshot.productId,
              outputWidth: specification.width,
              outputHeight: specification.height,
              restoredState,
              productName: productName(),
              sourceRoute: snapshot.route,
              outputPhysicalSizeMm: resolution.production_artboard_mm || null,
               ppi: resolution.ppi || null,
               initialPlacement: resolution.editor_initial_placement || 'fit',
               createBaseline: createBaselineFor(record, specification, resolution)
            });

            if (!selectionMatches(snapshot)) {
              editor.unload?.();
              setEditorVisible(false);
              requireCurrentSelection(snapshot);
            }
            editorState = editor.getEditorState();
            editorDirty = dirty;
            editor.dirty = dirty;
            if (editorRoot) editorRoot.dataset.editorState = dirty ? 'dirty' : 'ready';
            editor.updateControlsFromState?.();
            refreshWordingSuggestion();
          } finally {
            editorLoading = false;
          }
        });
        editorLoadQueue = pending;
        return pending;
      };
      const setUploadBusy = (value) => {
        uploadBusy = value;
        [uploadChooseButton, editorChooseButton].forEach((button) => {
          if (!button) return;
          button.disabled = value;
          if (value) button.setAttribute('aria-busy', 'true');
          else button.removeAttribute('aria-busy');
        });
        if (uploadDropzone) uploadDropzone.setAttribute('aria-busy', value ? 'true' : 'false');
        updateContext();
      };

      const setBusy = (value, owner = null) => {
        if (!value && owner && busyOwner !== owner) return;
        if (descriptionReplacementReturn) descriptionReplacementReturn.disabled = value;
        busy = value;
        busyOwner = value ? owner : null;
        activePaidRequest = value ? owner : null;
        [ideaInput, promptOutput].forEach((input) => {
          if (input) input.readOnly = value;
        });
        [improveButton, generateButton, tryAgainButton, descriptionResetButton].forEach((button) => {
          if (!button) return;
          button.disabled = value;
          if (value) button.setAttribute('aria-busy', 'true');
          else button.removeAttribute('aria-busy');
        });
        syncDescriptionPresentation();
        if (generateButton && generatedRecovery?.hasPending()) generateButton.disabled = true;
      };

      const setForwardBusy = (value) => {
        forwardBusy = value;
        syncGuidedReturnLock();
        if (editorRoot) {
          if (value) editorRoot.dataset.forwardPending = 'true';
          else delete editorRoot.dataset.forwardPending;
        }
        if (editorExportButton) {
          editorExportButton.textContent = value
            ? (messages.transferPending || 'Transferring your creation, please wait…')
            : (messages.continuePreview || 'Continue to product preview');
          if (value) {
            // Let the editor's existing click listener receive this event before
            // the native button becomes disabled, then hold it disabled through
            // IndexedDB persistence and navigation.
            editorExportButton.setAttribute('aria-busy', 'true');
            editorExportButton.setAttribute('aria-disabled', 'true');
            Promise.resolve().then(() => {
              if (forwardBusy && editorExportButton) editorExportButton.disabled = true;
            });
          } else {
            editorExportButton.disabled = false;
            editorExportButton.removeAttribute('aria-busy');
            editorExportButton.removeAttribute('aria-disabled');
          }
        }
      };

      const setSourceContinueBusy = (value) => {
        sourceNavigating = value;
        if (!sourceContinue) return;
        sourceContinue.textContent = value
          ? (sourceContinue.dataset.busyCopy || 'Opening Choose it…')
          : (sourceContinue.dataset.defaultCopy || 'Choose a product');
        if (value) {
          sourceContinue.disabled = true;
          sourceContinue.setAttribute('aria-busy', 'true');
          if (sourceMessage) sourceMessage.textContent = 'Checking the journey before opening product choices… Your description is kept.';
          sourceContinue.setAttribute('aria-disabled', 'true');
        } else {
          sourceContinue.removeAttribute('aria-busy');
          sourceContinue.removeAttribute('aria-disabled');
        }
      };

      const updateImageActions = () => {
        const controls = editorControls?.routeControls(currentRoute) || {};
        if (editorExportButton) editorExportButton.hidden = controls.useVisible !== true;
        if (editorDownloadButton) editorDownloadButton.hidden = controls.downloadVisible !== true;
        if (editorChooseButton) {
          editorChooseButton.hidden = sourceIntake
            ? !(['upload', 'create'].includes(currentRoute) && hasPrintableSource())
            : controls.replaceVisible !== true;
          editorChooseButton.textContent = composition ? 'Add an Image' : 'Replace Image';
          editorChooseButton.setAttribute('aria-expanded', String(replacementChoicesOpen));
        }
        if (replacementChoices) replacementChoices.hidden = !replacementChoicesOpen;
        if (editorStartAgainButton) editorStartAgainButton.hidden = controls.startAgainVisible !== true;
        if (tryAgainButton && currentRoute === 'upload' && tryAgainButton.dataset.retryAction !== 'resolve') tryAgainButton.hidden = true;
      };

      const updateProductLinks = () => {
        if (editorRoot) {
          const context = hasResolvedProduct() ? JSON.stringify([
            String(productSelect.value), currentResolution.shopify_variant_id, currentResolution.profile_key,
            currentResolution.profile_version, currentResolution.editor_output_size_px.width, currentResolution.editor_output_size_px.height
          ]) : '';
          if (editorRoot.dataset.imageOverlayContext !== context) editorRoot.dataset.imageOverlayContext = context;
          const overlayText = context ? selectedOption()?.dataset.imageOverlayText || '' : '';
          if (editorRoot.dataset.imageOverlayText !== overlayText) editorRoot.dataset.imageOverlayText = overlayText;
        }
        const option = selectedOption();
        const baseUrl = option?.dataset.productUrl || '';
        const selectedProductName = productName(option);
        const available = Boolean(baseUrl && hasResolvedProduct() && sourceReadyForPreview() && imageEditor?.ready && !editorLoading && !replacementPending
          && ['upload', 'create'].includes(currentRoute));
        if (editorExportButton) {
          editorExportButton.textContent = forwardBusy
            ? (messages.transferPending || 'Transferring your creation, please wait…')
            : (messages.continuePreview || 'Continue to product preview');
          editorExportButton.disabled = !available || forwardBusy;
          if (!available || forwardBusy) editorExportButton.setAttribute('aria-disabled', 'true');
          else editorExportButton.removeAttribute('aria-disabled');
        }
        if (editorPreviewExplanation) {
          editorPreviewExplanation.textContent = previewTextIssue() || editorControls?.productCopy?.(messages.previewNext, selectedProductName)
            || String(messages.previewNext || '').replaceAll('[product]', selectedProductName || 'selected product');
        }
        if (imageActions) imageActions.hidden = !['upload', 'create'].includes(currentRoute);
        updateImageActions();
      };

      const invalidatePreparation = ({ clearResolution = false, dropGenerated = false, resetEditor = true, retainEditorState = false } = {}) => {
        if (clearResolution || dropGenerated) {
          descriptionReplacement = null;
          if (descriptionReplacementReturn) descriptionReplacementReturn.hidden = true;
        }
        cancelInFlightRequests();
        if (forwardBusy && !forwardNavigating) setForwardBusy(false);
        selectionRevision += 1;
        exportSnapshot = null;
        const stalePreparedKey = currentImageKey && currentImageKey !== originalImageKey ? currentImageKey : '';
        currentImageKey = '';
        currentImageValidForProduct = false;
        // Keep editable lettering while a new product/profile is being resolved.
        // The editor validates/rebases image geometry at load; dirty state bars reuse.
        editorState = (sceneStorage || retainEditorState) && editorState ? structuredClone(editorState) : null;
        editorDirty = hasPrintableSource();
        if (resetEditor) {
          imageEditor?.unload?.();
          setEditorVisible(false);
        }
        if (dropGenerated) {
          if (currentRoute === 'create' && originalImageKey) {
            const prior = sourceContinuation()?.prior?.payload;
            if (![prior?.originalImageKey, prior?.imageKey, prior?.retainedUploadKey].includes(originalImageKey)) {
              void deleteImageRecord(originalImageKey).catch(() => {});
            }
            originalImageKey = '';
            editorDirty = false;
          }
          descriptionUsed = '';
          clearRefinedPrompt();
          if (refinedField) refinedField.hidden = true;
        }
        currentImageSource = originalImageKey ? (currentRoute === 'create' ? 'generated' : 'upload') : '';
        if (!originalImageKey) currentImageName = '';
        if (clearResolution) {
          resolutionRevision += 1;
          resolutionBusy = false;
          currentResolution = null;
          pendingPreviousResolution = null;
        }
        const prior = sourceContinuation()?.prior?.payload;
        if (stalePreparedKey && ![prior?.originalImageKey, prior?.imageKey, prior?.retainedUploadKey].includes(stalePreparedKey)) {
          void deleteImageRecord(stalePreparedKey).catch(() => {});
        }
        updateProductLinks();
        updateContext();
        persistDraft();
      };

      const discardStoredImage = ({ preserveUpload = false } = {}) => {
        cancelInFlightRequests();
        selectionRevision += 1;
        exportSnapshot = null;
        const staleKeys = Array.from(new Set([currentImageKey, originalImageKey, retainedUploadKey]
          .filter((key) => key && !(preserveUpload && key === retainedUploadKey))));
        currentImageKey = '';
        originalImageKey = '';
        composition = undefined;
        wordsCommitOwner += 1;
        currentImageSource = '';
        currentImageName = '';
        descriptionUsed = '';
        currentImageValidForProduct = false;
        editorState = null;
        editorDirty = false;
        imageEditor?.configureText?.([]);
        if (!preserveUpload) retainedUploadKey = '';
        sourceUploadReady = false;
        if (sourceObjectUrl) URL.revokeObjectURL(sourceObjectUrl);
        sourceObjectUrl = '';
        if (sourceImage) { sourceImage.removeAttribute('src'); sourceImage.hidden = true; }
        imageEditor?.unload?.();
        setEditorVisible(false);
        staleKeys.forEach((key) => void deleteImageRecord(key).catch(() => {}));
      };

      const clearDraftImage = ({ forgetStored = true, preserveUpload = false } = {}) => {
        if (currentImageObjectUrl) {
          URL.revokeObjectURL(currentImageObjectUrl);
          currentImageObjectUrl = '';
        }
        if (forgetStored) discardStoredImage({ preserveUpload });
        else currentImageValidForProduct = false;
        if (generatedImage) {
          generatedImage.removeAttribute('src');
          generatedImage.hidden = true;
        }
        if (uploadInput) uploadInput.value = '';
        if (imagePlaceholder) imagePlaceholder.hidden = false;
        if (tryAgainButton) tryAgainButton.hidden = true;
        updateProductLinks();
        updateContext();
      };

      const clearExpiredDraft = () => {
        if (!draftExpiresAt || draftExpiresAt > Date.now()) return;
        window.clearTimeout(saveTimer);
        const continuation = sourceContinuation();
        if (continuation?.prior?.expiresAt > Date.now()) {
          cancelInFlightRequests(); selectionRevision += 1; exportSnapshot = null;
          void sourceIntake.rollback().then(async ({ payload }) => {
            if (departed) return;
            restoreDraft(); populateVariantChoices(); if (variantSelect) variantSelect.value = payload.variantId || '';
            const resolved = payload.productId ? await resolveSelectedProduct({ restoreVariantId: payload.variantId }) : false;
            if (resolved) await restoreDraftImage();
            setUploadStatus('error', 'The new source expired. Your previous design has been restored; Preview approval must be checked again.');
          }).catch(() => setUploadStatus('error', 'The new source expired. Your previous design has been kept. Reload to recover it.'));
          return;
        }
        storage.remove();
        draftExpiresAt = 0;
        resolutionRevision += 1;
        resolutionBusy = false;
        if (ideaInput) ideaInput.value = '';
        clearRefinedPrompt();
        descriptionUsed = '';
        if (refinedField) refinedField.hidden = true;
        clearDraftImage();
        setUploadStatus('error', messages.sourceExpired);
        if (sourceMessage) sourceMessage.textContent = messages.sourceExpired || '';
        updateContext();
      };

      const scheduleExpiry = () => {
        window.clearTimeout(expiryTimer);
        if (!draftExpiresAt) return;
        expiryTimer = window.setTimeout(clearExpiredDraft, Math.max(0, draftExpiresAt - Date.now()));
      };

      const buildDraft = (overrides = {}) => {
        const expiresAt = Number(Object.hasOwn(overrides, 'expiresAt') ? overrides.expiresAt : draftExpiresAt);
        const draft = {
          version: sceneStorage ? 7 : 6,
          ...(sceneStorage ? { draftRevision, designOrigin,
            ...(designOrigin === 'guided' && guidedActionRequired ? { guidedActionRequired: true } : {}),
            ...(designOrigin === 'guided' && guidedDetails ? { guidedDetails: { style: guidedDetails.style,
              compositionIntent: guidedComposition ? guidedComposition.value : guidedDetails.compositionIntent } } : {}) } : {}),
          createdAt: expiresAt - draftTtl,
          productId: productSelect?.value || '',
          variantId: variantSelect?.value || '',
          resolution: currentResolution ? structuredClone(currentResolution) : null,
          expiresAt,
          route: currentRoute,
          idea: ideaInput?.value || '',
          prompt: promptOutput?.value || '',
          helperPromptContext: promptOutput?.value.trim() ? helperPromptContext : '',
          activeDescription: descriptionPresentation === 'refined' ? 'refined' : 'original',
          descriptionUsed,
          originalImageKey,
          ...(sceneStorage ? { originalRevision } : {}),
          retainedUploadKey,
          imageKey: currentImageKey,
          imageSource: currentImageSource,
          imageName: currentImageName,
          editorState,
          editorDirty,
          ...(['words', 'layout'].includes(editorRoot?.dataset.eEditorTask) ? { editorTask: editorRoot.dataset.eEditorTask } : {}),
          ...(composition ? { composition: structuredClone(composition) } : {}),
          ...overrides
        };
        guidedIdeaCompletion = retainedGuidedIdeaCompletion(guidedIdeaCompletion, draft);
        if (sceneStorage && guidedIdeaCompletion) draft.guidedIdeaCompletion = structuredClone(guidedIdeaCompletion);
        return draft;
      };

      const persistDraft = (overrides = {}) => {
        if (stagedSourceEntry) return false;
        if (guidedImportPending) return false;
        // A replacement description is provisional until new pixels commit.
        // Refresh/cancel/failure must still recover the complete current design.
        if (descriptionReplacement && !Object.keys(overrides).length) return true;
        const draft = buildDraft(overrides);
        if (!draft.expiresAt || draft.expiresAt <= Date.now() || departed) return false;
        if (sourceContinuation()) {
          try { sourceIntake.stage({ candidateDraft: draft }); return true; }
          catch (_) { return false; }
        }
        if (sceneStorage) {
          const previous = sceneStorage.readHeader();
          const comparable = value => JSON.stringify({ ...value, draftRevision: 0 });
          if (previous.status === 'ready' && comparable(previous.payload) === comparable(draft)) return true;
          draft.draftRevision = Math.max(draftRevision, previous.payload?.draftRevision || 0) + 1;
        }
        const saved = storage.set(JSON.stringify(draft));
        if (saved && sceneStorage) draftRevision = draft.draftRevision;
        return saved;
      };

      const saveDraft = () => {
        if (departed || stagedSourceEntry) return;
        if (!draftExpiresAt) draftExpiresAt = profileClient.createExpiry(Date.now(), draftTtl);
        scheduleExpiry();
        window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(() => {
          const saved = persistDraft();
          if (canIntakeSource() && sourceMessage) {
            const hasSource = currentRoute === 'upload' ? sourceUploadReady : Boolean(ideaInput?.value.trim());
            sourceMessage.textContent = !hasSource ? '' : !saved ? messages.sourceStorageError
              : currentRoute === 'upload' ? messages.sourceUpload : messages.sourceIdea;
            updateContext();
          }
        }, 180);
      };

      const displayDraftImage = (source, { name = currentImageName } = {}) => {
        if (generatedImage) {
          generatedImage.src = source;
          generatedImage.alt = name ? `Preview of ${name}` : 'Your selected artwork preview';
          generatedImage.hidden = false;
        }
        if (imagePlaceholder) imagePlaceholder.hidden = true;
        if (tryAgainButton) tryAgainButton.hidden = true;
        updateProductLinks();
      };

      const replaceCurrentStoredImage = async (record) => {
        const previousKey = currentImageKey;
        await putImageRecord(record);
        currentImageKey = record.id;
        currentImageSource = record.source || '';
        currentImageName = record.originalName || '';
        currentImageValidForProduct = true;
        if (previousKey && previousKey !== record.id) void deleteImageRecord(previousKey).catch(() => {});
      };

      const prepareGeneratedImage = async (imageUrl, exactDescription, generationSizePx, guidedIntent = null) => {
        const snapshot = captureSelection();
        const resolution = structuredClone(currentResolution);
        const response = await fetch(imageUrl);
        requireCurrentSelection(snapshot);
        if (!response.ok) throw new Error('Unable to prepare the generated image for local storage.');
        const blob = await response.blob();
        requireCurrentSelection(snapshot);
        if (!blob.type.startsWith('image/')) throw new Error('The generated result is not an image.');
        const inspection = await inspectImageBlob(blob, {
          expectedMime: blob.type,
          maximumBytes: uploadRule().maximumBytes
        });
        requireCurrentSelection(snapshot);
        const originalName = 'dream-it-made-image.png';
        const key = `${imageSessionId}:${productSelect?.value || 'unselected'}:${createIdentifier()}`;
        const createdAt = Date.now();
        const record = {
          id: key,
          sessionId: imageSessionId,
          productId: productSelect?.value || '',
          source: 'generated',
          originalName,
          blob,
          mimeType: blob.type,
          width: inspection.width,
          height: inspection.height,
          resolution,
          generationSizePx: {
            width: Number(generationSizePx?.width),
            height: Number(generationSizePx?.height)
          },
          descriptionUsed: String(exactDescription || '').slice(0, 4000),
          ...(guidedIntent ? { designOrigin: 'guided', guidedIntent: structuredClone(guidedIntent) } : {}),
          createdAt,
          expiresAt: profileClient.createExpiry(createdAt, draftTtl)
        };
        requireCurrentSelection(snapshot);
        return record;
      };

      const storeUploadedImage = async (file, dimensions) => {
        const snapshot = captureSelection();
        const key = `${imageSessionId}:${productSelect?.value || 'unselected'}:${createIdentifier()}`;
        const createdAt = Date.now();
        const record = {
          id: key,
          sessionId: imageSessionId,
          productId: productSelect?.value || '',
          source: 'upload',
          originalName: file.name,
          blob: file,
          mimeType: file.type,
          width: dimensions.width,
          height: dimensions.height,
          resolution: structuredClone(currentResolution),
          createdAt,
          expiresAt: profileClient.createExpiry(createdAt, draftTtl)
        };
        await putImageRecord(record);
        if (!selectionMatches(snapshot)) {
          void deleteImageRecord(record.id).catch(() => {});
          requireCurrentSelection(snapshot);
        }
        return record;
      };

      const showStoredImage = (record) => {
        if (currentImageObjectUrl) URL.revokeObjectURL(currentImageObjectUrl);
        currentImageObjectUrl = URL.createObjectURL(record.blob);
        currentImageSource = record.source || (currentRoute === 'upload' ? 'upload' : 'generated');
        currentImageName = record.originalName || (currentImageSource === 'upload' ? 'uploaded-image' : 'dream-it-made-image.png');
        displayDraftImage(currentImageObjectUrl, { name: currentImageName });
      };

      // An original upload is independent of a product. Bind a fresh record to
      // the confirmed selection without changing its bytes or extending its life.
      const bindOriginalUpload = async (record, snapshot) => {
        if (/^[1-9][0-9]*$/.test(String(record.productId))) return record;
        if (record.productId !== '' || record.resolution !== null) throw new Error(messages.uploadStorageError);
        const previousKey = originalImageKey;
        const previousRetainedKey = retainedUploadKey;
        const previousState = { currentImageKey, currentImageValidForProduct, editorState, editorDirty };
        const bound = { ...record, id: `${imageSessionId}:${productSelect.value}:${createIdentifier()}`,
          productId: productSelect.value, resolution: structuredClone(currentResolution) };
        await putImageRecord(bound);
        if (!selectionMatches(snapshot) || originalImageKey !== previousKey || !profileClient.isUnexpired(bound)) {
          void deleteImageRecord(bound.id).catch(() => {});
          requireCurrentSelection(snapshot);
          throw new Error(messages.sourceExpired);
        }
        const stalePreparedKey = currentImageKey;
        originalImageKey = bound.id;
        retainedUploadKey = bound.id;
        currentImageKey = '';
        currentImageValidForProduct = false;
        // A neutral source has no placement authority for the new product.
        // Lettering is independent of that image placement and remains editable.
        editorState = sceneStorage && editorState?.textLayers ? {
          textSchemaVersion: editorState.textSchemaVersion,
          textRendererVersion: editorState.textRendererVersion,
          textLayers: structuredClone(editorState.textLayers)
        } : null;
        editorDirty = true;
        if (!persistDraft()) {
          originalImageKey = previousKey;
          retainedUploadKey = previousRetainedKey;
          ({ currentImageKey, currentImageValidForProduct, editorState, editorDirty } = previousState);
          void deleteImageRecord(bound.id).catch(() => {});
          throw new Error(messages.sourceStorageError || messages.uploadStorageError);
        }
        if (stalePreparedKey && stalePreparedKey !== previousKey) void deleteImageRecord(stalePreparedKey).catch(() => {});
        void deleteImageRecord(previousKey).catch(() => {});
        return bound;
      };

      const generatedSourceCompatible = (record) => {
        try {
          const before = profileClient.validateResolution(record.resolution);
          const after = profileClient.validateResolution(currentResolution);
          // A generated original is reusable source material within this product.
          // Its provenance stays intact; preparation always uses the fresh profile.
          return String(record.productId) === String(productSelect.value)
            && before.route === 'create_with_ai' && after.route === 'create_with_ai';
        } catch (_) { return false; }
      };

      const showSourceRecord = (record) => {
        if (sourceObjectUrl) URL.revokeObjectURL(sourceObjectUrl);
        sourceObjectUrl = URL.createObjectURL(record.blob);
        sourceUploadReady = true;
        currentImageName = record.originalName;
        if (sourceImage) { sourceImage.src = sourceObjectUrl; sourceImage.alt = record.originalName || ''; sourceImage.hidden = false; }
        if (sourceName) sourceName.textContent = record.originalName || '';
        if (sourceMessage) sourceMessage.textContent = messages.sourceUpload || '';
        updateContext();
      };

      const retainNeutralUpload = async (record, snapshot) => {
        if (record.productId === '' && record.resolution === null) return record;
        if (!sceneStorage) {
          const neutral = { ...record, productId: '', resolution: null };
          await putImageRecord(neutral);
          requireCurrentSelection(snapshot);
          return neutral;
        }
        const oldKey = record.id;
        const neutral = { ...record, id: `${imageSessionId}:unselected:${createIdentifier()}`, productId: '', resolution: null };
        await putImageRecord(neutral);
        if (!selectionMatches(snapshot) || retainedUploadKey !== oldKey) {
          void deleteImageRecord(neutral.id).catch(() => {});
          throw new Error(messages.uploadProductChanged);
        }
        const previous = { originalImageKey, retainedUploadKey, currentImageKey, currentImageValidForProduct, editorDirty };
        retainedUploadKey = neutral.id;
        if (originalImageKey === oldKey) {
          originalImageKey = neutral.id;
          currentImageKey = ''; currentImageValidForProduct = false; editorDirty = true;
        }
        if (!persistDraft()) {
          ({ originalImageKey, retainedUploadKey, currentImageKey, currentImageValidForProduct, editorDirty } = previous);
          void deleteImageRecord(neutral.id).catch(() => {});
          throw new Error(messages.sourceStorageError || messages.uploadStorageError);
        }
        if (previous.currentImageKey && previous.currentImageKey !== oldKey && previous.originalImageKey === oldKey) {
          void deleteImageRecord(previous.currentImageKey).catch(() => {});
        }
        void deleteImageRecord(oldKey).catch(() => {});
        return neutral;
      };

      const restoreUnboundSource = async () => {
        if (!canIntakeSource() || currentRoute !== 'upload' || !retainedUploadKey) { updateContext(); return false; }
        const snapshot = captureSelection();
        let key = retainedUploadKey;
        try {
          let record = await getImageRecord(key);
          requireCurrentSelection(snapshot);
          if (key !== retainedUploadKey || !record || record.source !== 'upload'
            || record.sessionId !== imageSessionId || !profileClient.isUnexpired(record)
            || validateStoredUploadRecord(record)) throw new Error(messages.sourceExpired || messages.uploadStorageError);
          if (record.productId !== '' || record.resolution !== null) {
            record = await retainNeutralUpload(record, snapshot);
            key = record.id;
            if (key !== retainedUploadKey) throw new Error(messages.uploadProductChanged);
          }
          originalImageKey = key;
          showSourceRecord(record);
          return true;
        } catch (error) {
          if (!selectionMatches(snapshot)) return false;
          const message = error.message || messages.uploadStorageError;
          if (sceneStorage) {
            sourceUploadReady = false;
            setUploadStatus('error', `${message} Your saved upload has been kept. Reload to try restoring it again.`);
            updateContext();
            return false;
          }
          discardStoredImage();
          showRoute('', false);
          if (sourceMessage) sourceMessage.textContent = '';
          setProfileStatus(message, true);
          persistDraft();
          updateContext();
          return false;
        }
      };

      const restoreDraftImage = async () => {
        if (!hasResolvedProduct()) return false;
        if (composition) {
          try {
            if (!wordsRuntimeAvailable()) throw new Error('This saved lettering needs a compatible editor. Your design has been kept.');
            const snapshot = captureSelection();
            const admission = await unifiedRuntime.requireAdmission({ productId: snapshot.productId, variantId: snapshot.variantId, route: snapshot.route });
            requireCurrentSelection(snapshot);
            validateWordsComposition(composition, admission.resolution);
            const prepared = currentImageKey ? await getImageRecord(currentImageKey) : null;
            requireCurrentSelection(snapshot);
            const valid = preparedRecordMatches(prepared) && !editorDirty;
            await loadUploadEditor(wordsRecord(), editorState, !valid);
            requireCurrentSelection(snapshot);
            currentImageValidForProduct = valid;
            editorDirty = !valid;
            updateContext(); updateProductLinks();
            if (!completeWords()) {
              setUploadStatus('success', 'Add your words to continue to Preview.');
              openWordsEntry();
            } else if (sourceContinuation()?.sourceIntent === 'words') return await commitWordsCandidate();
            return true;
          } catch (error) {
            currentImageValidForProduct = false;
            setUploadStatus('error', error.message || messages.uploadPrepareError); updateProductLinks(); return false;
          }
        }
        const snapshot = captureSelection();
        try {
          await pruneExpiredImages();
          requireCurrentSelection(snapshot);
          if (requestedRouteUnavailable || (currentRoute !== 'create' && currentRoute !== 'upload')) return false;
          if (!originalImageKey) return false;
          let originalRecord = await getImageRecord(originalImageKey);
          requireCurrentSelection(snapshot);
          const expectedOriginalSource = currentRoute === 'create' ? 'generated' : 'upload';
          const originalValid = Boolean(
            originalRecord &&
            originalRecord.blob instanceof Blob &&
            originalRecord.source === expectedOriginalSource &&
            originalRecord.sessionId === imageSessionId &&
            profileClient.isUnexpired(originalRecord)
          );
          if (!originalValid) {
            clearDraftImage({ preserveUpload: currentRoute === 'create' });
            persistDraft();
            setUploadStatus('error', messages.sourceExpired || messages.uploadStorageError);
            return false;
          }
          originalRevision = Number(originalRecord.createdAt);
          if (currentRoute === 'create' && !descriptionUsed) {
            descriptionUsed = String(originalRecord.descriptionUsed || promptOutput?.value || ideaInput?.value || '').slice(0, 4000);
          }
          if (currentRoute === 'create' && !generatedSourceCompatible(originalRecord)) {
            let retainedForAnotherProduct = false;
            try {
              retainedForAnotherProduct = /^[1-9]\d*$/.test(String(originalRecord.productId))
                && String(originalRecord.productId) !== String(productSelect.value)
                && profileClient.validateResolution(originalRecord.resolution).route === 'create_with_ai';
            } catch (_) { /* Invalid provenance is not a recoverable Create source. */ }
            if (retainedForAnotherProduct) {
              // Generated pixels stay tied to their original product. Keep
              // them for a return journey without loading this product's editor.
              currentImageValidForProduct = false;
            } else invalidatePreparation({ dropGenerated: true });
            setResult('empty', messages.emptyHeading, messages.sourceRegenerate);
            setControlStatus('empty', { message: messages.sourceRegenerate });
            updateProductLinks();
            return false;
          }
          const validationError = validateStoredUploadRecord(originalRecord);
          if (validationError) {
            currentImageValidForProduct = false;
            setUploadStatus('error', validationError.message);
            updateProductLinks();
            return false;
          }
          const specification = outputSpecification();
          if (!specification) throw new Error(messages.uploadPrepareError);
          if (currentRoute === 'upload' && !/^[1-9][0-9]*$/.test(String(originalRecord.productId))) {
            originalRecord = await bindOriginalUpload(originalRecord, snapshot);
          }
          requireCurrentSelection(snapshot);
          const preparedRecord = currentImageKey ? await getImageRecord(currentImageKey) : null;
          requireCurrentSelection(snapshot);
          const preparedValid = preparedRecordMatches(preparedRecord, specification) && editorDirty === false;
          await loadUploadEditor(originalRecord, editorState, !preparedValid);
          requireCurrentSelection(snapshot);
          currentImageValidForProduct = preparedValid;
          currentImageSource = preparedValid ? 'edited' : originalRecord.source;
          currentImageName = preparedValid ? preparedRecord.originalName : originalRecord.originalName;
          editorDirty = !preparedValid;
          setUploadStatus('success', preparedValid ? messages.uploadPrepared : (messages.sourceReprepare || messages.uploadEditorReady), { clearAfter: 3500 });
          setResult('success', preparedValid ? messages.uploadPrepared : messages.uploadEditorReady, '');
          updateProductLinks();
          updateContext();
          return true;
        } catch (error) {
          if (!selectionMatches(snapshot)) return false;
          currentImageValidForProduct = false;
          if (originalImageKey) setUploadStatus('error', error?.message || messages.uploadPrepareError);
          updateProductLinks();
          return false;
        }
      };
      const clearProductSpecificWork = () => {
        clearRefinedPrompt();
        if (refinedField) refinedField.hidden = true;
        clearDraftImage();
        setResult('empty', messages.emptyHeading, messages.emptyText);
      };

      const routeLabel = () => routeInputs.find((input) => input.value === currentRoute)?.nextElementSibling?.querySelector('span')?.textContent?.trim() || '';

      const updateContext = () => {
        studio.dataset.sourceMode = canIntakeSource() ? 'true' : 'false';
        studio.dataset.wordsOnly = composition ? 'true' : 'false';
        let wordsMappingCurrent = false;
        try { wordsMappingCurrent = Boolean(composition && validateWordsComposition()); } catch (_) { /* Retain mismatched words for deliberate rebinding/recovery. */ }
        if (wordsOnlyButton) wordsOnlyButton.hidden = wordsMappingCurrent || !wordsCreationAllowed();
        const priorWords = sourceContinuation()?.prior?.payload?.composition;
        if (wordsOnlyReturn) wordsOnlyReturn.hidden = !sourceContinuation()?.prior || !(composition || priorWords);
        if (wordsOnlyActions) wordsOnlyActions.hidden = wordsOnlyButton?.hidden !== false && wordsOnlyReturn?.hidden !== false;
        const family = window.DreamItJourneyRoutes?.routeFamily?.({ search: window.location.search, designOrigin, sourceMode: canIntakeSource() });
        if (family && studio.dataset.journeyFamily !== family) studio.dataset.journeyFamily = family;
        const selectedProductName = hasProduct() ? productName() : '';
        const selectedProductId = hasProduct() ? String(productSelect.value) : '';
        const summary = selectedSummary();
        const thumbnail = selectedThumbnail();
        const thumbnailAlt = summary.text ? `${summary.text} product thumbnail` : '';
        if (selectedName) selectedName.textContent = selectedProductName;
        if (selectedVariantName) selectedVariantName.textContent = /^default(?:\s+title)?$/i.test(summary.variantTitle) ? '' : summary.variantTitle;
        if (selectedImage) {
          selectedImage.hidden = !thumbnail.url;
          if (thumbnail.url) { selectedImage.src = thumbnail.url; selectedImage.alt = thumbnailAlt; }
          else { selectedImage.removeAttribute('src'); selectedImage.alt = ''; }
        }
        const selected = Boolean(hasProduct() && selectedVariant());
        if (selectedSummaryPanel) selectedSummaryPanel.hidden = guidedImportPending || !selected;
        if (selectionGuidance) selectionGuidance.hidden = selected || canIntakeSource() || guidedImportPending;
        if (guidedImportPending) setProfileStatus(guidedPendingMessage);
        else if (profileStatus?.textContent === guidedPendingMessage) setProfileStatus('');
        if (selectionIntro) selectionIntro.hidden = !selected;
        const guidedCreate = designOrigin === 'guided' && currentRoute === 'create';
        if (routePanel) {
          routePanel.hidden = guidedImportPending || guidedCreate || (!selected && !canIntakeSource());
          routePanel.inert = guidedImportPending;
        }
        if (routePicker) routePicker.hidden = guidedImportPending || guidedCreate || (!routeChoiceExpanded && Boolean(currentRoute));
        if (changeRouteButton) changeRouteButton.hidden = guidedImportPending || guidedCreate || routeChoiceExpanded || !currentRoute;
        syncDescriptionPresentation();
        refreshWordingSuggestion();
        if (improveButton) improveButton.hidden = designOrigin === 'guided';
        if (selectedWorkspace) {
          selectedWorkspace.hidden = guidedImportPending || !hasResolvedProduct() && !(canIntakeSource() && currentRoute)
            && !(sourceContinuation()?.prior && (composition || priorWords));
          selectedWorkspace.inert = guidedImportPending;
        }
        if (sourceIntro) sourceIntro.hidden = guidedImportPending || !canIntakeSource();
        if (generationControls) generationControls.hidden = guidedImportPending || !hasResolvedProduct();
        if (securityPanel) securityPanel.hidden = guidedImportPending || !(currentRoute === 'create' && hasResolvedProduct() && !busy
          && (guidedGeneration || (!hasDraftImage() || descriptionReplacement) && descriptionText(activeDescriptionInput()))
          && (studio.dataset.imageEnabled === 'true' || studio.dataset.promptHelperEnabled === 'true'));
        if (descriptionProductFirst) descriptionProductFirst.hidden = !canIntakeSource() || currentRoute !== 'create';
        const priorDesign = sourceContinuation()?.prior?.payload;
        if (replacementKeep) replacementKeep.hidden = !priorDesign?.originalImageKey || Boolean(composition);
        if (generationAlternatives) generationAlternatives.hidden = !generationFailure;
        if (generationUseWords) generationUseWords.hidden = !generationFailure || !wordsCreationAllowed();
        if (result) result.hidden = guidedImportPending || imageGenerationActive || canIntakeSource();
        const hasSource = currentRoute === 'upload' ? sourceUploadReady : Boolean(currentRoute === 'create' && ideaInput?.value.trim());
        const hasGeneratedDescription = Boolean(currentRoute === 'create' && !canIntakeSource() && originalImageKey && descriptionUsed);
        const showSourceUploadSummary = Boolean(canIntakeSource() && currentRoute === 'upload' && sourceUploadReady);
        if (sourceUploadSummary) sourceUploadSummary.hidden = !showSourceUploadSummary;
        if (sourceSummary) sourceSummary.hidden = !hasGeneratedDescription;
        if (sourceDescriptionDisclosure && hasGeneratedDescription && !sourceSummaryWasVisible) {
          sourceDescriptionDisclosure.open = false;
        }
        sourceSummaryWasVisible = hasGeneratedDescription;
        if (sourceSummaryHeading) {
          sourceSummaryHeading.textContent = sourceSummary.dataset.descriptionHeadingCopy || 'Your description';
        }
        if (sourceDescription) {
          sourceDescription.hidden = currentRoute !== 'create';
          sourceDescription.textContent = currentRoute === 'create'
            ? (hasGeneratedDescription ? descriptionUsed : ideaInput?.value.trim() || '')
            : '';
        }
        if (descriptionResetButton) descriptionResetButton.hidden = !hasGeneratedDescription;
        if (sourceImage) sourceImage.hidden = currentRoute !== 'upload' || !sourceUploadReady;
        if (sourceName) sourceName.hidden = currentRoute !== 'upload';
        if (uploadHeading) {
          uploadHeading.textContent = canIntakeSource() && currentRoute === 'upload' && sourceUploadReady
            ? (uploadHeading.dataset.sourceReplaceCopy || 'Find something different')
            : (uploadHeading.dataset.defaultCopy || 'Use something you have');
        }
        const replaceSourceUpload = canIntakeSource() && currentRoute === 'upload' && sourceUploadReady;
        if (uploadChooseButton) {
          uploadChooseButton.textContent = replaceSourceUpload
            ? (uploadChooseButton.dataset.sourceReplaceCopy || 'Choose another image')
            : (uploadChooseButton.dataset.defaultCopy || 'Find your image');
        }
        if (uploadInput) {
          uploadInput.setAttribute('aria-label', replaceSourceUpload
            ? (uploadInput.dataset.sourceReplaceLabel || 'Choose another image')
            : (uploadInput.dataset.defaultLabel || 'Find your image'));
        }
        if (sourceContinue) {
          sourceContinue.hidden = !canIntakeSource() || !currentRoute;
          sourceContinue.disabled = !canIntakeSource() || !hasSource || uploadBusy || busy || sourceNavigating;
        }
        if (sourceMessage) sourceMessage.hidden = !canIntakeSource() || !sourceMessage.textContent;
        const guideVariantId = selected ? String(selectedVariant().id) : '';
        variantPlacementGuides.forEach((guide) => {
          guide.hidden = !currentRoute || !guideVariantId || guide.dataset.guideProductId !== selectedProductId
            || guide.dataset.variantPlacementGuide !== guideVariantId;
        });
        productGuidelines.forEach((details) => {
          const hasGuide = variantPlacementGuides.some((guide) => !guide.hidden && guide.dataset.guideProductId === details.dataset.productGuidelines);
          details.hidden = !currentRoute || details.dataset.productGuidelines !== selectedProductId
            || (details.dataset.guidelinesProse === 'false' && !hasGuide);
        });
        if (guidelinesRegion) guidelinesRegion.hidden = !productGuidelines.some((details) => !details.hidden);
        productInspiration.forEach((details) => {
          details.hidden = currentRoute !== 'create' || details.dataset.productInspiration !== selectedProductId;
        });
        if (inspirationRegion) inspirationRegion.hidden = designOrigin === 'guided' || !productInspiration.some((details) => !details.hidden);
        if (productField) productField.hidden = true;
        if (variantField) variantField.hidden = true;
        if (contextRoute) contextRoute.textContent = routeLabel();
        if (contextProduct) contextProduct.textContent = selectedProductName;
        if (context) context.hidden = !(currentRoute || selectedProductName);
        if (uploadProductName) uploadProductName.textContent = summary.text;
        if (uploadProductImage) {
          uploadProductImage.hidden = !thumbnail.url;
          if (thumbnail.url) {
            uploadProductImage.src = thumbnail.url;
            uploadProductImage.alt = thumbnailAlt;
          } else {
            uploadProductImage.removeAttribute('src');
            uploadProductImage.alt = '';
          }
        }
        if (uploadProductSummary) uploadProductSummary.hidden = currentRoute !== 'upload' || !selectedProductName;
        if (createProductName) createProductName.textContent = summary.text;
        if (createProductImage) {
          createProductImage.hidden = !thumbnail.url;
          if (thumbnail.url) {
            createProductImage.src = thumbnail.url;
            createProductImage.alt = thumbnailAlt;
          } else {
            createProductImage.removeAttribute('src');
            createProductImage.alt = '';
          }
        }
        if (createProductSummary) createProductSummary.hidden = currentRoute !== 'create' || !selectedProductName;
      };

      const setEmptyResultForRoute = () => {
        if (hasDraftImage()) return;
        if (currentRoute === 'upload') {
          setResult('empty', messages.emptyHeading, messages.uploadEmpty);
          setUploadStatus('', '');
          return;
        }
        setResult('empty', messages.emptyHeading, messages.emptyText);
      };

      const filterProductsForRoute = () => {
        const allowed = (selectedOption()?.dataset.routes || '').split(',');
        routeInputs.forEach((input) => {
          input.disabled = canIntakeSource() ? !sourceRoutes().includes(input.value)
            : !hasProduct() || !selectedVariant() || !allowed.includes(input.value);
          const card = input.closest('.route-choice');
          if (card) card.hidden = input.disabled;
        });
        if (productField) productField.hidden = true;
        if (noProducts) noProducts.hidden = hasProduct() || canIntakeSource();
      };

      const showRoute = (route, save = true) => {
        if (save) routeChoiceExpanded = false;
        currentRoute = route;
        routeInputs.forEach((input) => {
          input.checked = input.value === route;
        });
        routeWorkspaces.forEach((workspace) => {
          workspace.hidden = workspace.dataset.routeWorkspace !== route;
        });
        routeLegalRows.forEach((row) => {
          row.hidden = row.dataset.routeLegalLinks !== route;
        });
        if (routeLegal) routeLegal.hidden = !routeLegalRows.some((row) => !row.hidden);
        if (securityPanel) securityPanel.hidden = route !== 'create';
        filterProductsForRoute();
        updateProductLinks();
        updateContext();
        setEmptyResultForRoute();
        if (save) {
          if (window.history?.replaceState && ['upload', 'create'].includes(route)) {
            const destination = new URL(window.location.href);
            destination.searchParams.set('route', route);
            window.history.replaceState(window.history.state, '', `${destination.pathname}${destination.search}${destination.hash}`);
            window.DreamItJourneyRoutes?.enhanceSourceLinks?.(document, window.location);
          }
          saveDraft(true);
          if (hasProduct()) void resolveSelectedProduct({ restoreVariantId: variantSelect?.value || '' }).then((resolved) => {
            if (resolved) return restoreDraftImage();
          });
          else if (canIntakeSource()) void restoreUnboundSource();
        }
      };

      const requireUploadProduct = () => {
        if (canIntakeSource() && currentRoute === 'upload') return true;
        if (hasResolvedProduct()) return true;
        const message = variantSelectionRequired
          ? formatMessage(messages.variantRequired, { label: currentVariantPicker.label })
          : (messages.uploadProductRequired || messages.product);
        setUploadStatus('error', message);
        setResult('error', messages.error, message);
        const focusTarget = variantSelectionRequired ? variantSelect : productSelect;
        focusTarget?.setAttribute('aria-invalid', 'true');
        queueFocus(() => visibleFocusTarget(focusTarget) ? focusTarget : choicesFocusTarget());
        return false;
      };

      const chooseUploadImage = () => {
        if (uploadBusy || !requireUploadProduct()) return;
        uploadInput?.click();
      };
      const closeReplacementChoices = () => {
        replacementChoicesOpen = false;
        updateImageActions();
      };
      const openReplacementChoices = () => {
        if (busy || replacementPending || uploadBusy || forwardBusy) return;
        if (!sourceIntake) { chooseUploadImage(); return; }
        if (generatedRecovery?.hasPending()) { generatedRecovery.focus(); return; }
        if (!replacementChoices) { chooseUploadImage(); return; }
        replacementChoicesOpen = true;
        updateImageActions();
        queueFocus(() => replacementUpload);
      };
      const chooseReplacementSource = async route => {
        if (busy || replacementPending || uploadBusy || forwardBusy) return false;
        if (generatedRecovery?.hasPending()) { generatedRecovery.focus(); return false; }
        const allowed = (selectedOption()?.dataset.routes || '').split(',');
        if (!allowed.includes(route) || route === 'create' && studio.dataset.imageEnabled !== 'true') {
          setUploadStatus('error', route === 'create' ? 'Image generation is not available here right now. Your design has been kept.' : messages.routeUnavailable);
          return false;
        }
        closeReplacementChoices(); generationFailure = null;
        if (route === currentRoute) {
          if (route === 'upload') chooseUploadImage();
          else editReplacementDescription();
          updateContext(); return true;
        }
        const snapshot = captureSelection();
        try {
          if (!sourceIntake) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
          if (hasDraftImage() && !(await confirmImageReplacement(editorChooseButton))) return false;
          requireCurrentSelection(snapshot);
          if (!persistDraft({ editorState: imageEditor?.getEditorState?.() || editorState })) throw new Error(messages.uploadStorageError);
          sourceIntake.continue({ intent: route, rawText: ideaInput?.value || '' });
          if (route === 'create') sourceIntake.stage({ source: null });
          // Keep the complete previous scene until validated replacement commit.
          composition = undefined; wordsCommitOwner += 1;
          originalImageKey = ''; originalRevision = 0; retainedUploadKey = ''; currentImageKey = '';
          invalidatePreparation({ clearResolution: true, retainEditorState: true });
          designOrigin = 'direct'; guidedDetails = null; guidedActionRequired = false;
          if (route === 'create' && ideaInput) ideaInput.value = '';
          clearRefinedPrompt(); syncGuidedControls(); showRoute(route, false);
          if (!persistDraft()) throw new Error(messages.uploadStorageError);
          const resolved = await resolveSelectedVariant();
          if (!resolved || currentRoute !== route) return false;
          if (route === 'upload') chooseUploadImage();
          else { if (descriptionEditor) descriptionEditor.open = true; queueFocus(() => ideaInput); }
          updateContext(); return true;
        } catch (error) { setUploadStatus('error', error.message || messages.uploadStorageError); updateContext(); return false; }
      };
      replacementUpload?.addEventListener('click', () => { void chooseReplacementSource('upload'); });
      replacementGenerate?.addEventListener('click', () => { void chooseReplacementSource('create'); });
      replacementCancel?.addEventListener('click', () => { closeReplacementChoices(); queueFocus(() => editorChooseButton); });
      studio.addEventListener('keydown', event => {
        if (event.key === 'Escape' && replacementChoicesOpen) { event.preventDefault(); closeReplacementChoices(); queueFocus(() => editorChooseButton); }
      });
      generationUseUpload?.addEventListener('click', () => { void chooseReplacementSource('upload'); });
      generationUseWords?.addEventListener('click', () => { if (wordsCreationAllowed()) { generationFailure = null; void chooseWordsOnly(); } });

      const handleUploadFile = async (file) => {
        if (!file || uploadBusy || !requireUploadProduct()) return;
        const snapshot = captureSelection();
        const replacement = canIntakeSource() ? null : { kind: 'upload' };
        if (replacement) beginReplacement(replacement);
        setUploadBusy(true);
        setUploadStatus('checking', formatMessage(messages.uploadChecking, { product: productName() }));
        const progressOwner = { snapshot, checking: true, phase: '', percent: null };
        uploadProgressOwner = progressOwner;
        showUploadProgress(progressOwner, 'structure');
        productSelect?.removeAttribute('aria-invalid');
        let newRecord = null;
        let preparedEditor = null;
        try {
          const { dimensions } = await validateUploadFile(file, selectedOption(), {
            onProgress: progress => showUploadProgress(progressOwner, progress.phase, progress),
            onDecode: () => { progressOwner.checking = false; showUploadProgress(progressOwner, 'opening'); }
          });
          requireCurrentSelection(snapshot);
          if (!stagedSourceEntry && canIntakeSource() && currentRoute === 'upload' && originalImageKey
            && !(await confirmImageReplacement(uploadInput))) return;
          requireCurrentSelection(snapshot);
          if (sourceIntake && canIntakeSource()) {
            if (unifiedRuntime?.paid?.pending?.()) throw new Error('A previous request still needs a status check. Your saved design has been kept.');
            progressOwner.checking = false; showUploadProgress(progressOwner, 'saving');
            const accepted = await sourceIntake.acceptFile(file, { maximumBytes: uploadRule().maximumBytes,
              inspectImageBlob, probeImageBlob, probeMatchesInspection, isCurrent: () => selectionMatches(snapshot) });
            requireCurrentSelection(snapshot);
            newRecord = accepted.record;
            stagedSourceEntry = null;
            originalImageKey = newRecord.id; retainedUploadKey = newRecord.id; originalRevision = Number(newRecord.createdAt);
            currentImageKey = ''; currentImageSource = 'upload'; currentImageName = newRecord.originalName;
            currentImageValidForProduct = false; editorDirty = true; draftExpiresAt = newRecord.expiresAt;
            const priorText = accepted.continuation.prior?.payload?.editorState;
            editorState = priorText?.textLayers ? { textSchemaVersion: priorText.textSchemaVersion, textRendererVersion: priorText.textRendererVersion, textLayers: structuredClone(priorText.textLayers) } : null;
            sourceIntake.continue({ intent: 'upload', rawText: ideaInput?.value || '' });
            persistDraft(); scheduleExpiry(); showSourceRecord(newRecord); setUploadStatus('', ''); updateProductLinks();
            return;
          }
          if (!commitStagedSourceEntry({ forUpload: true })) return;
          progressOwner.checking = false;
          showUploadProgress(progressOwner, 'saving');
          newRecord = await storeUploadedImage(file, dimensions);
          requireCurrentSelection(snapshot);
          if (!canIntakeSource()) {
            showUploadProgress(progressOwner, 'opening');
            preparedEditor = await prepareReplacementEditor(newRecord, snapshot);
          }
          requireCurrentSelection(snapshot);
          const committedSelection = await commitSourceReplacement({
            record: newRecord,
            snapshot,
            preparedEditor,
            description: '',
            retainUpload: true
          });
          if (!selectionMatches(committedSelection)) return;
          updateProductLinks();
          if (canIntakeSource()) {
            showSourceRecord(newRecord);
            setUploadStatus('', '');
            queueFocus(() => selectionMatches(snapshot) ? sourceUploadSummary : null);
          } else {
            setUploadStatus('success', messages.uploadEditorReady, { clearAfter: 3500 });
            setResult('success', messages.uploadEditorReady, '', true);
          }
        } catch (error) {
          discardReplacementEditor(preparedEditor);
          if (!selectionMatches(snapshot)) {
            if (newRecord?.id && newRecord.id !== originalImageKey) void deleteImageRecord(newRecord.id).catch(() => {});
            return;
          }
          if (newRecord?.id && newRecord.id !== originalImageKey) void deleteImageRecord(newRecord.id).catch(() => {});
          setUploadStatus('error', error?.message || messages.uploadStorageError);
          queueFocus(() => selectionMatches(snapshot) ? uploadStatus : null, 'nearest');
        } finally {
          if (stagedSourceEntry) stagedSourceEntry.confirmed = false;
          if (uploadProgressOwner === progressOwner) clearUploadProgress();
          if (uploadInput) uploadInput.value = '';
          if (replacement) finishReplacement(replacement);
          setUploadBusy(false);
          updateProductLinks();
        }
      };

      const revalidateUploadForSelectedProduct = async () => {
        if (!['upload', 'create'].includes(currentRoute) || !hasPrintableSource() || !hasResolvedProduct()) return;
        currentImageValidForProduct = false;
        editorDirty = true;
        updateProductLinks();
        await restoreDraftImage();
      };
      const commitStagedSourceEntry = ({ forUpload = false } = {}) => {
        if (!stagedSourceEntry) return true;
        const saved = stagedSourceEntry.draft;
        if (!stagedDraftUnchanged(saved)) {
          setUploadStatus('error', 'Your saved design changed in another tab. Reload before starting a new one.');
          return false;
        }
        try {
          if (unifiedRuntime?.paid?.pending?.()) {
            setUploadStatus('error', 'A previous request still needs a status check. Your saved design has been kept.');
            return false;
          }
        } catch (_) {
          setUploadStatus('error', 'The previous request status could not be read. Your saved design has been kept.');
          return false;
        }
        if (!window.confirm(messages.startAgain || messages.startAgainUpload)) return false;
        if (!stagedDraftUnchanged(saved)) {
          setUploadStatus('error', 'Your saved design changed in another tab. Reload before starting a new one.');
          return false;
        }
        if (forUpload) {
          stagedSourceEntry.confirmed = true;
          return true;
        }
        if (sourceIntake) {
          try {
            sourceIntake.continue({ intent: currentRoute, rawText: ideaInput?.value || '' });
            stagedSourceEntry = null;
            draftExpiresAt = Math.min(saved.expiresAt, Date.now() + draftTtl);
            persistDraft(); scheduleExpiry(); return true;
          } catch (_) { setUploadStatus('error', messages.sourceStorageError || messages.uploadStorageError); return false; }
        }
        const previousExpiry = draftExpiresAt;
        draftExpiresAt = profileClient.createExpiry(Date.now(), draftTtl);
        stagedSourceEntry = null;
        if (!persistDraft()) {
          stagedSourceEntry = { draft: saved };
          draftExpiresAt = previousExpiry;
          setUploadStatus('error', messages.sourceStorageError || messages.uploadStorageError);
          return false;
        }
        scheduleExpiry();
        Array.from(new Set([saved.originalImageKey, saved.imageKey, saved.retainedUploadKey].filter(Boolean)))
          .forEach((key) => void deleteImageRecord(key).catch(() => {}));
        return true;
      };
      sourceContinue?.addEventListener('click', async () => {
        if (!canIntakeSource() || busy || uploadBusy || sourceNavigating || !['upload', 'create'].includes(currentRoute)) return;
        if (currentRoute === 'create' && !descriptionText(ideaInput)) { explainEmptyDescription(ideaInput); return; }
        const staged = Boolean(stagedSourceEntry);
        setSourceContinueBusy(true);
        try {
          if (currentRoute === 'upload' && sourceContinuation()?.source) {
            const record = await sourceIntake.getCandidate();
            if (!record) throw new Error(messages.sourceExpired || messages.uploadStorageError);
            showSourceRecord(record);
          } else if (currentRoute === 'upload' && !(await restoreUnboundSource())) {
            setSourceContinueBusy(false);
            updateContext();
            return;
          }
          window.clearTimeout(saveTimer);
          if (!draftExpiresAt || draftExpiresAt <= Date.now() || (!staged && !persistDraft())) {
            throw new Error(messages.sourceStorageError || messages.uploadStorageError);
          }
          const destination = new URL(studio.dataset.collectionUrl || '/collections/all', window.location.origin);
          if (destination.origin !== window.location.origin || !/^\/collections\/all\/?$/.test(destination.pathname)) {
            throw new Error(messages.sourceNavigationError || messages.sourceStorageError || messages.uploadStorageError);
          }
          destination.search = '';
          destination.searchParams.set('entry', 'source');
          destination.searchParams.set('route', currentRoute);
          const navigationSelection = captureSelection();
          if (unifiedRuntime) await unifiedRuntime.prepareTransition(destination.pathname);
          requireCurrentSelection(navigationSelection);
          if (staged && !commitStagedSourceEntry()) {
            setSourceContinueBusy(false);
            return;
          }
          if (sourceIntake && sourceContinuation()) sourceIntake.continue({ intent: currentRoute, rawText: ideaInput?.value || '' });
          window.location.assign(destination.href);
        } catch (error) {
          const message = error?.message || messages.sourceNavigationError || messages.sourceStorageError || messages.uploadStorageError;
          setSourceContinueBusy(false);
          if (sourceMessage) sourceMessage.textContent = message;
          setUploadStatus('error', message);
          updateContext();
          queueFocus(() => visibleFocusTarget(sourceMessage) ? sourceMessage : uploadStatus, 'nearest');
        }
      });
      uploadChooseButton?.addEventListener('click', chooseUploadImage);
      uploadInput?.addEventListener('change', () => {
        const [file] = Array.from(uploadInput.files || []);
        if (file) void handleUploadFile(file);
      });
      uploadDropzone?.addEventListener('dragenter', (event) => {
        event.preventDefault();
        if (!uploadBusy) uploadDropzone.classList.add('is-dragging');
      });
      uploadDropzone?.addEventListener('dragover', (event) => {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      });
      uploadDropzone?.addEventListener('dragleave', (event) => {
        if (!uploadDropzone.contains(event.relatedTarget)) uploadDropzone.classList.remove('is-dragging');
      });
      uploadDropzone?.addEventListener('drop', (event) => {
        event.preventDefault();
        uploadDropzone.classList.remove('is-dragging');
        const [file] = Array.from(event.dataTransfer?.files || []);
        if (file) void handleUploadFile(file);
      });

      const setProfileStatus = (message = '', error = false) => {
        if (!profileStatus) return;
        profileStatus.textContent = message;
        profileStatus.setAttribute('role', error ? 'alert' : 'status');
        profileStatus.setAttribute('aria-live', error ? 'assertive' : 'polite');
      };

      const populateVariantChoices = () => {
        const option = selectedOption();
        currentVariantPicker = editorControls?.variantPicker({
          variants: productVariants(option),
          configuredLabel: option?.dataset.variantPickerLabel || ''
        }) || Object.freeze({ required: false, label: 'Choose Type', variants: Object.freeze([]) });
        variantSelectionRequired = currentVariantPicker.required === true;
        if (variantLabel) variantLabel.textContent = currentVariantPicker.label;
        if (variantField) variantField.hidden = true;
        if (!variantSelect) return currentVariantPicker;
        variantSelect.required = variantSelectionRequired;
        variantSelect.setAttribute('aria-required', variantSelectionRequired ? 'true' : 'false');
        variantSelect.replaceChildren(new Option(currentVariantPicker.label, ''));
        productVariants(option).filter((variant) => variant.available === true).forEach((variant) => {
          variantSelect.append(new Option(String(variant.title || variant.name || variant.id), String(variant.id)));
        });
        return currentVariantPicker;
      };

      const applyResolution = async (nextResolution) => {
        const next = profileClient.validateResolution(nextResolution);
        if (!hasProduct() || !selectedVariant() || String(next.shopify_variant_id) !== String(variantSelect.value)
          || next.route !== profileClient.normaliseRoute(currentRoute)) throw new Error(messages.profileStale);
        const previous = currentResolution || pendingPreviousResolution;
        const identical = previous ? profileClient.exactIdentity(previous, next) : false;
        if (previous && !identical) invalidatePreparation();
        // A later governed-target change must clear old guidance even after a
        // same-target refresh has already removed its Helper provenance.
        if (previous && previous.shopify_variant_id !== next.shopify_variant_id
          && promptContextFor(previous) !== promptContextFor(next)) clearRefinedPrompt();
        else if (helperPromptContext && helperPromptContext !== promptContextFor(next)) {
          // Refreshing the same target invalidates Helper provenance, not the
          // accepted wording (which may contain later deliberate edits).
          if (previous?.shopify_variant_id === next.shopify_variant_id) helperPromptContext = '';
          else clearRefinedPrompt();
        }
        currentResolution = next;
        pendingPreviousResolution = null;
        setProfileStatus('');
        window.dispatchEvent(new CustomEvent('dreamitmade:profile-resolution', { detail: { resolution: structuredClone(next), compatible: identical } }));
        persistDraft();
        updateProductLinks();
      };

      const resolveSelectedVariant = async () => {
        resolutionController?.abort();
        resolutionController = null;
        clearRetry();
        const revision = ++resolutionRevision;
        const snapshot = captureSelection();
        if (!hasProduct() || !selectedVariant() || !currentRoute || !profileClient || !endpoint || endpointConfigurationError) {
          currentResolution = null;
          resolutionBusy = false;
          setProfileStatus(endpointConfigurationError?.message || (hasProduct() ? messages.variantRequired : ''));
          updateContext();
          updateProductLinks();
          return false;
        }
        const requestController = new AbortController();
        resolutionController = requestController;
        resolutionBusy = true;
        setProfileStatus(messages.profileResolving);
        updateContext();
        updateProductLinks();
        try {
          // Capability negotiation must precede constructing the first resolve body.
          if (unifiedRuntime?.verify && unifiedRuntime.hasJourneyDataReuse !== true) await unifiedRuntime.verify();
          if (revision !== resolutionRevision || !selectionMatches(snapshot)) return false;
          const payload = await profileClient.resolveProfile(
            endpoint, snapshot.variantId, currentRoute, 'auto', undefined, { signal: requestController.signal }
          );
          if (revision !== resolutionRevision || !selectionMatches(snapshot)) return false;
          await applyResolution(payload.resolution);
          clearRetry();
          return true;
        } catch (error) {
          if (revision === resolutionRevision) {
            currentResolution = null;
            const message = error?.code === 'STALE_PROFILE' ? messages.profileStale : customerErrorMessage(error, messages.error);
            setProfileStatus(message, true);
            showRetry('resolve');
          }
          return false;
        } finally {
          if (revision === resolutionRevision) {
            resolutionBusy = false;
            updateContext();
            updateProductLinks();
          }
          if (resolutionController === requestController) resolutionController = null;
        }
      };

      const resolveSelectedProduct = async ({ restoreVariantId = '' } = {}) => {
        pendingPreviousResolution = currentResolution;
        currentResolution = null;
        populateVariantChoices();
        if (Array.from(variantSelect?.options || []).some((option) => option.value === restoreVariantId)) variantSelect.value = restoreVariantId;
        filterProductsForRoute();
        return resolveSelectedVariant();
      };

      const activeDescriptionInput = () => descriptionPresentation === 'refined' && designOrigin !== 'guided' ? promptOutput : ideaInput;
      const validateProtectedAction = (descriptionInput = activeDescriptionInput(), { humanCheck = true } = {}) => {
        if (endpointConfigurationError || !endpoint) {
          setResult('error', messages.error, endpointConfigurationError?.message || 'The artwork service is disabled or unconfigured.', true);
          return false;
        }
        if (!hasResolvedProduct()) {
          const message = variantSelectionRequired
            ? formatMessage(messages.variantRequired, { label: currentVariantPicker.label })
            : messages.product;
          setResult('error', messages.error, message);
          const focusTarget = variantSelectionRequired ? variantSelect : productSelect;
          focusTarget?.setAttribute('aria-invalid', 'true');
          queueFocus(() => visibleFocusTarget(focusTarget) ? focusTarget : choicesFocusTarget());
          return false;
        }
        if (!descriptionText(descriptionInput)) return explainEmptyDescription(descriptionInput);
        if (humanCheck && studio.querySelector('.cf-turnstile') && !getTurnstileToken()) {
          setResult('security', messages.error, messages.security, true);
          return false;
        }
        return true;
      };

      const improveDescription = async () => {
        if (!promptHelperAllowed()) return;
        if (busy || !validateProtectedAction()) return;
        const descriptionInput = activeDescriptionInput();
        const helperDescription = descriptionText(descriptionInput);
        if (helperDescription.length > 1800) {
          setResult('error', messages.error, 'Keep the description to 1,800 characters or fewer before using the Helper.');
          descriptionInput.setAttribute?.('aria-invalid', 'true'); queueFocus(() => descriptionInput); return;
        }
        const snapshot = captureSelection();
        helperController?.abort();
        const requestController = new AbortController();
        const requestOwner = { kind: 'helper' };
        helperController = requestController;
        clearRetry();
        setBusy(true, requestOwner);
        productSelect?.removeAttribute('aria-invalid');
        ideaInput?.removeAttribute('aria-invalid');
        setResult('loading', messages.improving, messages.improving);
        try {
          const payload = await profileClient.coachPrompt(
            endpoint, currentResolution.shopify_variant_id, helperDescription, getTurnstileToken(), undefined,
            { signal: requestController.signal }
          );
          requireCurrentSelection(snapshot);
          if (!profileClient.exactIdentity(currentResolution, payload.resolution)) throw new Error(messages.profileStale);
          promptOutput.value = payload.prompt || '';
          helperPromptContext = promptOutput.value.trim() ? promptContextFor(currentResolution) : '';
          refinedField.hidden = !promptOutput.value;
          syncDescriptionPresentation();
          saveDraft();
          // Retain delivered wording before presenting success or waiting for
          // another Human Check. The normal save still handles later edits;
          // provisional image replacements retain their existing prior owner.
          persistDraft();
          setResult('empty', messages.emptyHeading, messages.promptSuccess);
          queueFocus(() => promptOutput);
        } catch (error) {
          if (!selectionMatches(snapshot)) return;
          setResult('error', messages.error, customerErrorMessage(error, messages.error), true);
        } finally {
          if (helperController === requestController) helperController = null;
          if (activePaidRequest === requestOwner) {
            setBusy(false, requestOwner);
            resetTurnstile();
          }
        }
      };

      const generationAllowed = () => Boolean(
        studio.dataset.imageEnabled === 'true' && !guidedImportPending && !canIntakeSource() && currentRoute === 'create'
        && hasResolvedProduct() && currentResolution.route === 'create_with_ai'
      );
      const promptHelperAllowed = () => Boolean(
        designOrigin !== 'guided' && studio.dataset.promptHelperEnabled === 'true' && generationAllowed()
      );

      const saveCompletedGeneratedImage = async (record, snapshot, isCurrent = () => true) => {
        let preparedEditor = null;
        let committedSelection = null;
        const check = () => {
          requireCurrentSelection(snapshot);
          if (!isCurrent() || record.expiresAt <= Date.now() || !profileClient.exactIdentity(currentResolution, record.resolution)) {
            throw Object.assign(new Error(messages.uploadProductChanged), { code: 'STALE_SELECTION' });
          }
          if (snapshot.sourceContinuationRevision !== undefined && sourceIntake?.read().revision !== snapshot.sourceContinuationRevision) {
            throw Object.assign(new Error(messages.uploadProductChanged), { code: 'STALE_SELECTION' });
          }
        };
        try {
          check();
          await putImageRecord(record);
          check();
          // First-image Guided text exists before the editor is ready. Carry
          // its live layers, including a clear, without old image placement.
          const currentTextState = sceneStorage && imageEditor?.getEditorState();
          const restoredTextState = currentTextState && Array.isArray(currentTextState.textLayers) ? {
            textSchemaVersion: currentTextState.textSchemaVersion,
            textRendererVersion: currentTextState.textRendererVersion,
            textLayers: structuredClone(currentTextState.textLayers)
          } : null;
          preparedEditor = await prepareReplacementEditor(record, snapshot, restoredTextState);
          check();
          committedSelection = await commitSourceReplacement({ record, snapshot, preparedEditor, description: record.descriptionUsed, retainUpload: false,
            expectedSourceRevision: snapshot.sourceContinuationRevision });
        } catch (error) {
          discardReplacementEditor(preparedEditor);
          if (record.id !== originalImageKey) void deleteImageRecord(record.id).catch(() => {});
          throw error;
        }
        if (!selectionMatches(committedSelection)) return;
        updateContext(); updateProductLinks();
        setUploadStatus('success', messages.uploadEditorReady, { clearAfter: 3500 });
        setResult('success', messages.uploadEditorReady, '', true);
        clearRetry();
      };
      generatedRecovery = window.DIMGeneratedRecovery?.create({
        root: studio,
        isCurrent: ({ record, snapshot }) => selectionMatches(snapshot)
          && snapshot.original === originalImageKey && (!snapshot.original || snapshot.editorRevision === (imageEditor?.revision ?? null))
          && record.descriptionUsed === descriptionText(activeDescriptionInput())
          && profileClient.exactIdentity(currentResolution, record.resolution),
        save: async (record, snapshot, isCurrent) => {
          const owner = { kind: 'save-generated-image' };
          beginReplacement(owner); setBusy(true, owner);
          try {
            // A failed readback may follow a committed write. Never overwrite that
            // immutable record; each explicit local save owns a fresh disposable ID.
            await saveCompletedGeneratedImage({ ...record, id: `${imageSessionId}:recovery:${createIdentifier()}` }, snapshot, isCurrent);
          } finally { setBusy(false, owner); finishReplacement(owner); }
        },
        onState: active => {
          if (generateButton) generateButton.disabled = busy || active;
          if (recoveryActions) recoveryActions.hidden = !active;
          if (active && tryAgainButton) tryAgainButton.hidden = true;
        },
        onError: error => setResult('error', messages.error, window.DIMGeneratedRecovery.storageFailure(error)
          ? 'This browser still could not save your image. You can download it below without generating again.'
          : customerErrorMessage(error, messages.uploadPrepareError), true),
        onExpired: () => setResult('error', messages.error, 'The unsaved image has expired. Your previously saved design is unchanged.', true)
      });
      const generateImage = async (generationAction) => {
        if (generatedRecovery?.hasPending()) { generatedRecovery.focus(); return; }
        if (!generationAllowed()) return;
        if (busy || !validateProtectedAction()) return;
        const refinedPrompt = promptOutput?.value.trim() || '';
        const descriptionInput = activeDescriptionInput();
        const prompt = descriptionText(descriptionInput);
        const limit = designOrigin === 'guided' ? 1000 : descriptionPresentation === 'refined' ? 4000 : 1800;
        if (prompt.length > limit) {
          setResult('error', messages.error, `Keep the description to ${limit.toLocaleString('en-GB')} characters or fewer.`);
          descriptionInput?.setAttribute('aria-invalid', 'true'); queueFocus(() => descriptionInput); return;
        }
        const promptOrigin = refinedPrompt && helperPromptContext
          && helperPromptContext === promptContextFor(currentResolution) ? 'helper' : 'customer';
        const guidedIntent = guidedIntentFor();
        if (designOrigin === 'guided' && (!guidedIntent?.subject || guidedIntent.subject.length > 1000
          || !guidedIntent.compositionIntent || guidedIntent.compositionIntent.length > 1000)) {
          setResult('error', messages.error, 'Check the illustration description and composition before creating your image.', true);
          return;
        }
        // A deliberate manual Create after a confirmed failure is a new action,
        // but must still require the server acknowledgement introduced with the
        // two-action Guided journey. Restoring a draft never dispatches it.
        if (!generationAction && designOrigin === 'guided' && guidedActionRequired) {
          generationAction = { id: createIdentifier(), createdAt: Date.now() };
        }
        const snapshot = captureSelection();
        snapshot.original = originalImageKey;
        if (sourceContinuation()) snapshot.sourceContinuationRevision = sourceIntake.read().revision;
        snapshot.editorRevision = imageEditor?.revision ?? null;
        const requestOwner = { kind: 'generation' };
        generationFailure = null;
        clearRetry();
        beginReplacement(requestOwner);
        setBusy(true, requestOwner);
        if (imagePlaceholder) imagePlaceholder.hidden = false;
        productSelect?.removeAttribute('aria-invalid');
        ideaInput?.removeAttribute('aria-invalid');
        setResult('loading', messages.loading, '');
        let newRecord = null;
        try {
          const streamExperiment = studio.dataset.imageStreamingExperiment === 'true'
            && typeof profileClient.generateImageStreaming === 'function' && !ordinaryGenerationRetry;
          ordinaryGenerationRetry = false;
          let payload;
          if (streamExperiment) {
            streamingController = new AbortController();
            delete studio.dataset.imageFirstPartialMs;
            delete studio.dataset.imageFinalMs;
            for (const field of ['Stage', 'Status', 'Code', 'Param', 'UpstreamMessage', 'ClientStage', 'ClientFailureReason', 'ClientErrorCode', 'ClientErrorName', 'ClientErrorMessage', 'Accepted', 'MetadataReceived', 'EventType', 'Base64Type', 'Base64Length', 'Base64Whitespace', 'Base64InvalidAlphabet', 'Base64Prefix']) delete studio.dataset[`imageStream${field}`];
            studio.dataset.imagePartialCount = '0';
            studio.dataset.imageStreamState = 'starting';
            payload = await profileClient.generateImageStreaming(endpoint, currentResolution, prompt, 'auto', getTurnstileToken(), {
              promptOrigin, guidedIntent, ...(generationAction ? { generationAction } : {}), signal: streamingController.signal,
              onPartial: (imageUrl, progress) => {
                requireCurrentSelection(snapshot);
                if (!streamingPreview || !streamingPreviewImage) return;
                streamingPreviewImage.src = imageUrl;
                streamingPreview.hidden = false;
                if (generationPanel) generationPanel.dataset.hasPreview = 'true';
                studio.dataset.imagePartialCount = String(progress.partialCount);
                if (!studio.dataset.imageFirstPartialMs) studio.dataset.imageFirstPartialMs = String(progress.elapsedMs);
              },
              onProgress: (progress) => {
                requireCurrentSelection(snapshot);
                studio.dataset.imageStreamState = progress.state;
                if (progress.state === 'completed') {
                  studio.dataset.imageFinalMs = String(progress.elapsedMs);
                  clearStreamingPreview();
                }
              }
            });
          } else {
            ordinaryImageController?.abort();
            const requestController = new AbortController();
            ordinaryImageController = requestController;
            try {
              payload = await profileClient.generateImage(
                endpoint, currentResolution, prompt, 'auto', getTurnstileToken(), undefined, promptOrigin,
                { signal: requestController.signal, guidedIntent, ...(generationAction ? { generationAction } : {}) }
              );
            } finally {
              if (ordinaryImageController === requestController) ordinaryImageController = null;
            }
          }
          requireCurrentSelection(snapshot);
          if (!profileClient.exactIdentity(currentResolution, payload.resolution)) throw new Error(messages.profileStale);
          const imageUrl = payload.image_data_url;
          if (!imageUrl) throw new Error(messages.error);
          newRecord = await prepareGeneratedImage(imageUrl, prompt, payload.generation.size, guidedIntent);
          if (newRecord.width !== payload.generation.size.width || newRecord.height !== payload.generation.size.height) {
            throw new Error(messages.uploadDimensionsError);
          }
          requireCurrentSelection(snapshot);
          await saveCompletedGeneratedImage(newRecord, snapshot);
        } catch (error) {
          if (!selectionMatches(snapshot)) return;
          if (newRecord && window.DIMGeneratedRecovery?.storageFailure(error) && generatedRecovery?.offer(newRecord, snapshot, error)) {
            setResult('error', 'Your image was created', 'It has not been saved on this device.', true);
            queueFocus(() => studio.querySelector('[data-generated-recovery-save]'));
            return;
          }
          if (error?.streamFallbackAvailable) {
            ordinaryGenerationRetry = true;
            studio.dataset.imageStreamState = 'ordinary-retry';
          }
          if (error?.streamDiagnostics) {
            for (const field of ['stage', 'status', 'code', 'param', 'upstreamMessage', 'clientStage', 'clientFailureReason', 'clientErrorCode', 'clientErrorName', 'clientErrorMessage', 'accepted', 'metadataReceived', 'eventType', 'base64Type', 'base64Length', 'base64Whitespace', 'base64InvalidAlphabet', 'base64Prefix']) {
              const value = error.streamDiagnostics[field];
              if (value) studio.dataset[`imageStream${field[0].toUpperCase()}${field.slice(1)}`] = value;
            }
          }
          if (error?.code === 'STALE_PROFILE') {
            currentImageValidForProduct = false;
            setProfileStatus(messages.profileStale, true);
          }
          generationFailure = profileClient.describeGenerationFailure?.(error) || {
            kind: error?.paidRequestPending ? 'unknown' : 'unavailable',
            message: customerErrorMessage(error, messages.error), retryAllowed: !error?.paidRequestPending
              && !String(error?.code || '').startsWith('PAID_OPERATION_')
          };
          if (generationFailureMessage) generationFailureMessage.textContent = generationFailure.message;
          if (!generationFailure.retryAllowed || ['GENERATION_ACTION_UNAVAILABLE'].includes(error?.code)) clearRetry();
          else showRetry('generate');
          // The previous source and editor remain the current retained draft.
          persistDraft();
          setResult('error', messages.error, customerErrorMessage(error, messages.error), true);
        } finally {
          if (activePaidRequest === requestOwner) {
            cancelStreamingPreview();
            setBusy(false, requestOwner);
            resetTurnstile();
          }
          finishReplacement(requestOwner);
          updateContext();
        }
      };

      const beginForwardAction = (event) => {
        const available = hasResolvedProduct() && sourceReadyForPreview() && imageEditor?.ready && !editorLoading && !replacementPending
          && ['upload', 'create'].includes(currentRoute);
        if (forwardBusy || !available) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!forwardBusy) setUploadStatus('error', previewTextIssue() || messages.uploadPrepareError);
          return;
        }
        forwardNavigating = false;
        setForwardBusy(true);
      };
      editorExportButton?.addEventListener('click', beginForwardAction, true);

      routeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (guidedImportPending) { routeInputs.forEach(owned => { owned.checked = owned.value === currentRoute; }); return; }
          if (successorRestoreBlocked) {
            routeInputs.forEach((routeInput) => { routeInput.checked = routeInput.value === currentRoute; });
            setUploadStatus('error', restoreDraftError);
            return;
          }
          if (!input.checked || input.disabled || (!canIntakeSource() && (!hasProduct() || !selectedVariant()))) return;
          try { preserveWordsTarget(); } catch (error) { setUploadStatus('error', error.message); return; }
          if (stagedSourceEntry) {
            showRoute(input.value, false);
            setUploadStatus('', '');
            return;
          }
          if (sourceContinuation()) {
            sourceIntake.continue({ intent: input.value, rawText: ideaInput?.value || '' });
            if (input.value === 'create') sourceIntake.stage({ source: null });
          }
          if (currentRoute && currentRoute !== input.value) {
            composition = undefined;
            wordsCommitOwner += 1;
            if (currentRoute === 'upload' && originalImageKey) retainedUploadKey = originalImageKey;
            invalidatePreparation({ clearResolution: true, dropGenerated: currentRoute === 'create' });
            originalImageKey = input.value === 'upload' ? retainedUploadKey : '';
            currentImageSource = originalImageKey ? 'upload' : '';
            sourceUploadReady = false;
            editorDirty = false;
            clearRefinedPrompt();
            if (refinedField) refinedField.hidden = true;
          }
          showRoute(input.value);
          setUploadStatus('', '');
          if (sourceMessage) sourceMessage.textContent = '';
          updateContext();
        });
      });

      productSelect?.addEventListener('change', async () => {
        if (productSelect.value === previousProduct) return;
        try { preserveWordsTarget(); } catch (error) { setUploadStatus('error', error.message); return; }
        invalidatePreparation({ clearResolution: true, retainEditorState: true });
        clearRefinedPrompt();
        if (refinedField) refinedField.hidden = true;
        previousProduct = productSelect.value;
        if (variantSelect) variantSelect.value = '';
        updateContext();
        const resolved = await resolveSelectedProduct();
        if (resolved) await restoreDraftImage();
        updateContext();
        persistDraft();
      });

      variantSelect?.addEventListener('change', async () => {
        try { preserveWordsTarget(); } catch (error) { setUploadStatus('error', error.message); return; }
        variantSelect.removeAttribute('aria-invalid');
        const previousResolution = currentResolution || pendingPreviousResolution;
        invalidatePreparation({ clearResolution: true,
          retainEditorState: currentRoute === 'create' && Boolean(originalImageKey) && !imageEditor?.ready });
        pendingPreviousResolution = previousResolution;
        updateContext();
        const resolved = await resolveSelectedVariant();
        if (resolved && hasPrintableSource()) await revalidateUploadForSelectedProduct();
        saveDraft(true);
      });

      [ideaInput, promptOutput, guidedComposition].forEach((input) => input?.addEventListener('input', () => {
        clearEmptyDescriptionError(input);
        generatedRecovery?.clear();
        syncDescriptionCounts();
        if (busy) return;
        if (!hasResolvedProduct() && !(canIntakeSource() && currentRoute === 'create' && input === ideaInput)) return;
        if (input === ideaInput || input === guidedComposition) clearRefinedPrompt();
        else if (!promptOutput?.value.trim()) helperPromptContext = '';
        if (descriptionReplacement) { updateContext(); return; }
        if (resultStatus?.textContent === messages.promptSuccess || controlStatus?.dataset.state === 'success') setResult('empty', messages.emptyHeading, '');
        invalidatePreparation();
        saveDraft(true);
        updateContext();
      }));
      improveButton?.addEventListener('click', improveDescription);
      const requestImageGeneration = async () => {
        if (generatedRecovery?.hasPending()) { generatedRecovery.focus(); return; }
        // A second press while Human Check is pending belongs to the same
        // deliberate action, rather than cancelling its scheduled continuation.
        if (guidedGeneration) { resumeGuidedGeneration(); return; }
        if (!generationAllowed()) return;
        if (!validateProtectedAction(activeDescriptionInput(), { humanCheck: false })) return;
        const snapshot = captureSelection(); const previousKey = originalImageKey;
        if (hasDraftImage() && !(await confirmImageReplacement())) return;
        if (!selectionMatches(snapshot) || originalImageKey !== previousKey || !generationAllowed()) return;
        if (studio.querySelector('.cf-turnstile') && !getTurnstileToken()) {
          // This explicit save owns the pending intention. A previously queued
          // typing save must not revise its otherwise unchanged continuation.
          window.clearTimeout(saveTimer);
          if (!persistDraft()) { setResult('error', messages.error, messages.uploadStorageError, true); return; }
          guidedGeneration = { kind: 'direct', selection: captureSelection(), original: originalImageKey,
            description: descriptionText(activeDescriptionInput()), header: storage.get(), draftRevision,
            expiresAt: draftExpiresAt || Date.now() + draftTtl };
          setResult('pending', messages.emptyHeading, messages.security); updateContext(); return;
        }
        void generateImage();
      };
      generateButton?.addEventListener('click', () => {
        requestImageGeneration();
      });
      tryAgainButton?.addEventListener('click', () => {
        if (tryAgainButton.dataset.retryAction === 'resolve') {
          void resolveSelectedVariant().then((resolved) => {
            if (resolved && originalImageKey) return restoreDraftImage();
            return false;
          });
          return;
        }
        requestImageGeneration({ skipPromptHelper: true });
      });

      const resetDescriptions = () => {
        if (currentRoute !== 'create' || !originalImageKey) return;
        const originalDescription = ideaInput?.value || '';
        descriptionUsed = '';
        invalidatePreparation({ dropGenerated: true });
        if (ideaInput) {
          ideaInput.value = originalDescription;
          ideaInput.removeAttribute('aria-invalid');
        }
        clearRefinedPrompt();
        if (refinedField) refinedField.hidden = true;
        setControlStatus('empty', null);
        setResult('empty', messages.emptyHeading, messages.emptyText);
        updateContext();
        saveDraft(true);
        afterDialogFocus(descriptionResetDialog, () => visibleFocusTarget(ideaInput) ? ideaInput : choicesFocusTarget());
      };

      descriptionResetButton?.addEventListener('click', () => {
        if (currentRoute !== 'create' || !originalImageKey || !descriptionUsed) return;
        if (descriptionResetDialog?.showModal) {
          openJourneyDialog(descriptionResetDialog, descriptionResetButton);
          return;
        }
        if (window.confirm(messages.descriptionReset)) resetDescriptions();
      });
      descriptionResetConfirm?.addEventListener('click', resetDescriptions);

      const resetDraftState = () => {
        const recoveringBlocked = successorRestoreBlocked;
        if (recoveringBlocked) {
          if (!allowBlockedRestoreReset()) return;
          // Confirmed removal is read back before releasing the restore lock or
          // clearing local work. A storage failure retains the blocked owner.
          try {
            sceneStorage.remove();
            if (sceneStorage.readHeader().status !== 'empty') throw new Error('Draft removal failed');
          } catch (_) {
            setUploadStatus('error', 'This saved design could not be cleared. It has been kept unchanged; reload to try again.');
            return;
          }
        }
        if (sceneStorage && !['empty', 'ready', 'expired'].includes(sceneStorage.readHeader().status)) {
          setUploadStatus('error', 'This saved design needs a compatible editor. It has been kept unchanged; return to the version that created it.');
          return;
        }
        successorRestoreBlocked = false;
        blockedRestoreOwner = null;
        restoreDraftError = '';
        delete studio.dataset.draftRestoreFailure;
        delete studio.dataset.draftRestoreCode;
        designOrigin = 'direct';
        guidedDetails = null;
        guidedIdeaCompletion = null;
        syncGuidedControls();
        const retainedProductId = productSelect?.value || '';
        const retainedVariantId = variantSelect?.value || '';
        const retainedResolution = !recoveringBlocked && currentResolution ? structuredClone(currentResolution) : null;
        const retainedRoute = currentRoute;
        window.clearTimeout(saveTimer);
        window.clearTimeout(expiryTimer);
        forwardNavigating = false;
        setForwardBusy(false);
        clearProductSpecificWork();
        if (!recoveringBlocked) storage.remove();
        draftExpiresAt = Date.now() + draftTtl;
        scheduleExpiry();
        if (ideaInput) ideaInput.value = '';
        clearRefinedPrompt();
        descriptionUsed = '';
        if (refinedField) refinedField.hidden = true;
        if (productSelect) {
          productSelect.value = retainedProductId;
          productSelect.removeAttribute('aria-invalid');
        }
        populateVariantChoices();
        if (variantSelect) {
          if (Array.from(variantSelect.options).some((option) => option.value === retainedVariantId)) {
            variantSelect.value = retainedVariantId;
          }
          variantSelect.removeAttribute('aria-invalid');
        }
        resolutionRevision += 1;
        exportSnapshot = null;
        resolutionBusy = false;
        currentResolution = retainedResolution;
        pendingPreviousResolution = null;
        previousProduct = retainedProductId;
        sourceMode = false;
        requestedRouteUnavailable = false;
        showRoute(retainedRoute, false);
        if (window.history?.replaceState) {
          const destination = new URL(window.location.href);
          destination.searchParams.delete('route');
          destination.searchParams.delete('entry');
          destination.searchParams.delete('@dim_source');
          destination.searchParams.delete('guided');
          destination.searchParams.set('journey', retainedProductId ? 'product' : 'source');
          if (retainedProductId) destination.searchParams.set('product', retainedProductId);
          if (retainedVariantId) destination.searchParams.set('variant', retainedVariantId);
          if (['upload', 'create'].includes(retainedRoute)) destination.searchParams.set('route', retainedRoute);
          window.history.replaceState(window.history.state, '', `${destination.pathname}${destination.search}${destination.hash}`);
          updateContext();
        }
        persistDraft();
        updateProductLinks();
        afterDialogFocus(startAgainDialog, choicesFocusTarget);
        if (recoveringBlocked) { setUploadStatus('', ''); void bootstrapResolvedState(); }
      };

      const currentStartAgainMessage = () => (
        currentRoute === 'upload' ? messages.startAgainUpload : messages.startAgain
      );

      const requestStartAgain = (event) => {
        const invoker = event?.currentTarget || event?.target || editorStartAgainButton;
        if (successorRestoreBlocked && !allowBlockedRestoreReset()) return;
        if (!successorRestoreBlocked && !hasJourneyWork() && designOrigin !== 'guided') {
          resetDraftState();
          return;
        }
        const warning = currentStartAgainMessage();
        if (startAgainMessage) startAgainMessage.textContent = warning;
        if (startAgainDialog?.showModal) {
          openJourneyDialog(startAgainDialog, invoker);
          return;
        }
        if (window.confirm(warning)) resetDraftState();
      };
      editorStartAgainButton?.addEventListener('click', requestStartAgain);
      blockedRestoreResetButton?.addEventListener('click', requestStartAgain);
      guidedBack?.addEventListener('click', event => {
        if (busy || uploadBusy || replacementPending || forwardBusy || activePaidRequest) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      });
      startAgainConfirm?.addEventListener('click', resetDraftState);
      editButton?.addEventListener('click', () => {
        if (descriptionEditor) descriptionEditor.open = true;
        queueFocus(() => visibleFocusTarget(ideaInput) ? ideaInput : choicesFocusTarget());
      });
      changeRouteButton?.addEventListener('click', () => {
        if (guidedImportPending) return;
        routeChoiceExpanded = true;
        updateContext();
        queueFocus(choicesFocusTarget);
      });
      guidedOwnImage?.addEventListener('click', () => {
        if (busy || replacementPending || uploadBusy || forwardBusy || successorRestoreBlocked || guidedImportPending) return;
        const upload = routeInputs.find(input => input.value === 'upload' && !input.disabled);
        if (!upload) return;
        upload.checked = true;
        upload.dispatchEvent(new Event('change', { bubbles: true }));
        queueFocus(() => uploadChooseButton);
      });
      reuseOriginal?.addEventListener('click', () => {
        if (busy || replacementPending || designOrigin === 'guided' || descriptionPresentation !== 'refined') return;
        clearRefinedPrompt();
        if (!descriptionReplacement) {
          invalidatePreparation();
          saveDraft(true);
        }
        updateContext();
        queueFocus(() => ideaInput);
      });
      const makeUploadSourceProductNeutral = async () => {
        const snapshot = captureSelection();
        const key = currentRoute === 'upload' ? originalImageKey : retainedUploadKey;
        if (!key) return true;
        const record = await getImageRecord(key);
        if ((currentRoute === 'upload' && key !== originalImageKey) || key !== retainedUploadKey
          || !record || record.source !== 'upload' || record.sessionId !== imageSessionId
          || !profileClient.isUnexpired(record) || validateStoredUploadRecord(record)) {
          throw new Error(messages.sourceExpired || messages.uploadStorageError);
        }
        if (record.productId !== '' || record.resolution !== null) {
          await retainNeutralUpload(record, snapshot);
          if (sourceIntake && currentRoute === 'upload' && sourceContinuation()) {
            const neutral = await getImageRecord(retainedUploadKey);
            requireCurrentSelection(snapshot);
            sourceIntake.stage({ source: { recordId: neutral.id, sessionId: neutral.sessionId, expiresAt: neutral.expiresAt } });
          }
          return true;
        }
        retainedUploadKey = key;
        if (sourceIntake && currentRoute === 'upload' && sourceContinuation()) sourceIntake.stage({ source: { recordId: record.id, sessionId: record.sessionId, expiresAt: record.expiresAt } });
        return true;
      };

      changeProductButton?.addEventListener('click', async () => {
        if (busy || uploadBusy || guidedImportPending || changeProductButton.disabled || !['upload', 'create'].includes(currentRoute)) return;
        const retainedRoute = currentRoute;
        const preflightSelection = captureSelection();
        if (window.DIMTextModel && imageEditor) preflightSelection.editorState = window.DIMTextModel.freezeEditorState(imageEditor.getEditorState());
        let committing = false;
        changeProductButton.disabled = true;
        changeProductButton.setAttribute('aria-busy', 'true');
        try {
          const destination = new URL(studio.dataset.collectionUrl || '/collections/all', window.location.origin);
          if (destination.origin !== window.location.origin || !/^\/collections\/all\/?$/.test(destination.pathname)) {
            throw new Error(messages.sourceStorageError || messages.uploadStorageError);
          }
          destination.search = '';
          destination.hash = '';
          destination.searchParams.set('entry', 'source');
          destination.searchParams.set('route', retainedRoute);
          // Authenticate the transition before neutralising records or clearing
          // the current editor. Failed/replaced attempts leave this design usable.
          if (unifiedRuntime) await unifiedRuntime.prepareTransition(destination.pathname);
          requireCurrentSelection(preflightSelection);
          if (composition?.kind === 'words') {
            if (!sourceIntake) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
            // Words are bound to an admitted target/background. Browse with a
            // separate intent while the complete committed scene stays valid.
            if (!persistDraft(preflightSelection.editorState ? { editorState: preflightSelection.editorState } : {})) {
              throw new Error(messages.sourceStorageError || messages.uploadStorageError);
            }
            window.clearTimeout(saveTimer);
            const layers = preflightSelection.editorState?.textLayers || editorState?.textLayers || [];
            sourceIntake.continue({ intent: 'choose', sourceIntent: 'words', sourceIntentConfirmed: true,
              rawText: ideaInput?.value || '', wording: layers.map(layer => String(layer.text || '')).join('\n') });
            window.location.assign(`${destination.pathname}${destination.search}`);
            return;
          }
          window.clearTimeout(saveTimer);
          if (sourceIntake && !sourceContinuation()) sourceIntake.continue({ intent: retainedRoute, rawText: ideaInput?.value || '' });
          if (retainedRoute === 'upload') await makeUploadSourceProductNeutral();
          else if (retainedUploadKey) {
            try {
              await makeUploadSourceProductNeutral();
            } catch (_) {
              requireCurrentSelection(preflightSelection);
              retainedUploadKey = '';
            }
          }
          requireCurrentSelection(preflightSelection);
          committing = true;
          // Browsing is not a replacement. Keep the generated original and
          // editable scene so returning to its product can restore the draft.
          invalidatePreparation({ clearResolution: true, retainEditorState: true });
          if (productSelect) {
            productSelect.value = '';
            productSelect.removeAttribute('aria-invalid');
          }
          populateVariantChoices();
          if (variantSelect) {
            variantSelect.value = '';
            variantSelect.removeAttribute('aria-invalid');
          }
          previousProduct = '';
          sourceMode = true;
          requestedRouteUnavailable = false;
          sourceUploadReady = false;
          currentImageValidForProduct = false;
          // invalidatePreparation retained successor lettering; the next
          // product load rebases image geometry without discarding text.
          editorDirty = false;
          currentImageKey = '';
          currentImageSource = retainedRoute === 'upload' && retainedUploadKey ? 'upload' : '';
          if (retainedRoute === 'upload') originalImageKey = retainedUploadKey;
          if (!draftExpiresAt || draftExpiresAt <= Date.now()) draftExpiresAt = profileClient.createExpiry(Date.now(), draftTtl);
          scheduleExpiry();
          showRoute(retainedRoute, false);
          if (!persistDraft()) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
          window.location.assign(`${destination.pathname}${destination.search}`);
        } catch (error) {
          changeProductButton.disabled = false;
          changeProductButton.removeAttribute('aria-busy');
          if (!committing && !selectionMatches(preflightSelection)) return;
          setUploadStatus('error', error?.message || messages.sourceStorageError || messages.uploadStorageError);
          queueFocus(() => uploadStatus, 'nearest');
        }
      });
      resetButton?.addEventListener('click', () => {
        if (hasJourneyWork() && !window.confirm(currentStartAgainMessage())) return;
        resetDraftState();
      });

      const restoreDraft = () => {
        let restoreStage = 'header';
        let restoreHeader = null;
        const raw = storage.get();
        if (!raw) {
          if (successorRestoreBlocked) restoreDraftError = 'This saved design needs a compatible editor. It has been kept unchanged; return to the version that created it.';
          return null;
        }
        try {
          restoreHeader = sceneStorage?.readHeader();
          const storedDraft = JSON.parse(raw);
          const continuation = sourceIntake && storedDraft.entryContinuation?.version === 1 ? storedDraft.entryContinuation : null;
          // A short-lived candidate cannot remove the protected outer header.
          // Leave its continuation available for the guarded rollback below.
          const candidate = continuation?.candidateDraft;
          const recoverPrior = continuation?.prior?.expiresAt > Date.now()
            && (continuation.expiresAt <= Date.now() || candidate?.expiresAt <= Date.now());
          const draft = recoverPrior ? structuredClone(continuation.prior.payload)
            : candidate ? structuredClone(candidate) : storedDraft;
          restoreStage = 'expiry';
          if (!(sceneStorage ? [7] : [4, 5, 6]).includes(draft.version) || draft.expiresAt <= Date.now()) {
            storage.remove();
            restoreDraftError = messages.sourceExpired || messages.profileStale || '';
            if (sourceMessage) sourceMessage.textContent = restoreDraftError;
            return null;
          }
          if ([6, 7].includes(draft.version) && !profileClient.isUnexpired(draft)) throw new Error('Invalid draft expiry.');
          draftExpiresAt = Number(draft.expiresAt);
          draftRevision = Number.isSafeInteger(draft.draftRevision) ? draft.draftRevision : 0;
          designOrigin = draft.designOrigin === 'guided' ? 'guided' : 'direct';
          guidedActionRequired = designOrigin === 'guided' && draft.guidedActionRequired === true;
          restoreStage = 'guided-details';
          guidedDetails = designOrigin === 'guided' ? validatedGuidedDetails(draft.guidedDetails) : null;
          guidedIdeaCompletion = retainedGuidedIdeaCompletion(draft.guidedIdeaCompletion, draft);
          restoreStage = 'route-controls';
          scheduleExpiry();
          const route = routeInputs.some((input) => input.value === draft.route) ? draft.route : '';
          showRoute(route, false);
          const productExists = Array.from(productSelect?.options || []).some((option) => option.value && option.value === draft.productId && !option.disabled);
          if (productSelect && productExists) productSelect.value = draft.productId;
          if (ideaInput) ideaInput.value = String(draft.idea || '').slice(0, 1800);
          syncGuidedControls();
          if (promptOutput) promptOutput.value = String(draft.prompt || '').slice(0, 4000);
          descriptionPresentation = draft.activeDescription === 'refined' || promptOutput?.value.trim() ? 'refined' : 'original';
          // Restored active wording chooses the initial disclosure state. Later
          // context updates preserve the customer's own expand/collapse choice.
          if (descriptionEditor) descriptionEditor.open = designOrigin !== 'guided' && descriptionPresentation === 'original';
          helperPromptContext = typeof draft.helperPromptContext === 'string' && promptOutput?.value.trim()
            ? draft.helperPromptContext.slice(0, 4000) : '';
          descriptionUsed = [6, 7].includes(draft.version) && typeof draft.descriptionUsed === 'string'
            ? draft.descriptionUsed.slice(0, 4000)
            : (draft.route === 'create' && draft.originalImageKey ? String(draft.prompt || draft.idea || '').slice(0, 4000) : '');
          if (refinedField) refinedField.hidden = !promptOutput?.value;
          currentImageKey = typeof draft.imageKey === 'string' ? draft.imageKey : '';
          currentImageSource = typeof draft.imageSource === 'string' ? draft.imageSource : '';
          currentImageName = typeof draft.imageName === 'string' ? draft.imageName : '';
          editorState = draft.editorState && typeof draft.editorState === 'object' ? draft.editorState : null;
          if (editorRoot) {
            if (['words', 'layout'].includes(draft.editorTask)) editorRoot.dataset.eEditorRestoreTask = draft.editorTask;
            else delete editorRoot.dataset.eEditorRestoreTask;
          }
          editorDirty = draft.editorDirty === true;
          composition = Object.hasOwn(draft, 'composition') ? window.DIMSceneIdentity.validateComposition(draft.composition, {
            resolution: draft.resolution, originalImageKey: draft.originalImageKey, originalRevision: draft.originalRevision, now: Date.now()
          }) : undefined;
          restoreStage = 'text-editor';
          if (sceneStorage && editorState?.textLayers) ensureImageEditor().configureText(editorState.textLayers);
          restoreStage = 'profile';
          if ([6, 7].includes(draft.version)) {
            currentResolution = draft.resolution ? profileClient.validateResolution(draft.resolution) : null;
            originalImageKey = typeof draft.originalImageKey === 'string' ? draft.originalImageKey : '';
            originalRevision = Number(draft.originalRevision) || 0;
          } else if (draft.version === 5) {
            originalImageKey = typeof draft.originalImageKey === 'string' ? draft.originalImageKey : '';
          } else if (route === 'upload' || route === 'create') {
            originalImageKey = currentImageKey;
            currentImageKey = '';
            currentImageSource = route === 'upload' ? 'upload' : 'generated';
            editorState = null;
            editorDirty = true;
          }
          retainedUploadKey = typeof draft.retainedUploadKey === 'string' ? draft.retainedUploadKey
            : (route === 'upload' ? originalImageKey : '');
          previousProduct = productSelect?.value || '';
          restoreStage = 'context';
          updateProductLinks();
          updateContext();
          if (![6, 7].includes(draft.version)) persistDraft();
          return draft;
        } catch (error) {
          studio.dataset.draftRestoreFailure = restoreStage;
          studio.dataset.draftRestoreCode = /^GUIDED_DETAILS_(SHAPE|KEYS|STYLE|COMPOSITION_(TYPE|LENGTH))$/.test(error?.code) ? error.code : 'RESTORE_FAILED';
          console.warn('Dream it Made draft restore failed', { stage: restoreStage, code: studio.dataset.draftRestoreCode });
          if (sceneStorage) {
            successorRestoreBlocked = true;
            blockedRestoreOwner = restoreHeader?.status === 'ready' && restoreHeader.payload?.version === 7
              && Number.isSafeInteger(restoreHeader.revision) && JSON.stringify(restoreHeader.payload) === raw
              ? { revision: restoreHeader.revision, record: JSON.stringify(restoreHeader.record), payload: raw } : null;
          } else storage.remove();
          restoreDraftError = sceneStorage
            ? 'This saved design could not be opened. It has been kept unchanged. Reload to try again, or choose Start Again to begin a new design.'
            : messages.sourceExpired || messages.profileStale || '';
          if (sourceMessage) sourceMessage.textContent = restoreDraftError;
          return null;
        }
      };
      const query = new URLSearchParams(window.location.search);
      const initialContinuation = sourceContinuation();
      let guidedPendingRaw = '';
      try { if (sceneStorage && query.getAll('guided').length === 1 && query.get('guided') === '1') guidedPendingRaw = window.sessionStorage.getItem(sceneStorage.keys.pending) || ''; } catch (_) {}
      guidedImportPending = Boolean(guidedPendingRaw);
      let guidedEntryRoute = 'create';
      try { if (JSON.parse(guidedPendingRaw)?.action?.kind === 'upload') guidedEntryRoute = 'upload'; } catch (_) {}
      const hasSelectionHints = query.has('product') || query.has('variant');
      const requestedEntries = query.getAll('entry');
      const requestedRoutes = query.getAll('route');
      const requestedRoute = query.get('route');
      const availableSourceRoutes = sourceRoutes();
      const sourceIntent = requestedEntries.length === 1 && requestedEntries[0] === 'source'
        && requestedRoutes.length <= 1 && (!requestedRoute || availableSourceRoutes.includes(requestedRoute))
        && availableSourceRoutes.length > 0;
      sourceMode = sourceIntent && !hasSelectionHints;
      const restoredDraft = restoreDraft();
      // Legacy v4/v5 restore immediately rewrites the session draft as v6.
      // Compare subsequent replacement attempts with that canonical value.
      let restoredAuthority = restoredDraft;
      if (restoredDraft) {
        try { restoredAuthority = JSON.parse(storage.get()) || restoredDraft; } catch (_) { /* A later unchanged check will fail closed. */ }
      }
      const restoredProductId = String(restoredDraft?.productId || '');
      const restoredVariantId = String(restoredDraft?.variantId || '');
      const restoredRoute = String(restoredDraft?.route || '');
      const explicitSourceRoute = sourceIntent && requestedRoutes.length === 1;
      // A new header choice is presentation until the customer deliberately
      // continues with a replacement source. The protected draft owns its old
      // artwork, product and approval throughout this entry view.
      stagedSourceEntry = !initialContinuation && !guidedPendingRaw && explicitSourceRoute && restoredDraft
        && (restoredRoute !== requestedRoute || restoredProductId || restoredVariantId || restoredDraft.designOrigin === 'guided')
        ? { draft: structuredClone(restoredAuthority) } : null;
      const restoredCandidate = Array.from(productSelect?.options || []).find((option) => (
        option.value === restoredProductId && option.dataset.productUrl && !option.disabled
      ));
      const restoredSelectionValid = Boolean(
        /^[1-9]\d*$/.test(restoredProductId) && /^[1-9]\d*$/.test(restoredVariantId)
        && restoredCandidate
        && productVariants(restoredCandidate).some((variant) => String(variant.id) === restoredVariantId && variant.available === true)
        && (restoredCandidate.dataset.routes || '').split(',').includes(restoredRoute)
        && routeInputs.some((input) => input.value === restoredRoute)
      );
      const plainDreamEntry = requestedEntries.length === 0 && requestedRoutes.length === 0;
      const resumeStoredDraft = !hasSelectionHints && requestedEntries.length <= 1 && requestedRoutes.length <= 1
        && (plainDreamEntry || sourceIntent) && restoredSelectionValid && !stagedSourceEntry;
      const resumeSourceDraft = !hasSelectionHints && !restoredProductId && !restoredVariantId
        && requestedEntries.length <= 1 && requestedRoutes.length <= 1
        && (plainDreamEntry || sourceIntent) && !stagedSourceEntry
        && availableSourceRoutes.includes(restoredRoute)
        && (restoredRoute === 'upload' ? Boolean(restoredDraft?.retainedUploadKey || restoredDraft?.originalImageKey)
          : restoredRoute === 'create' && Boolean(String(restoredDraft?.idea || '').trim()));
      const hintedProduct = hasSelectionHints ? query.get('product') : resumeStoredDraft ? restoredProductId : '';
      const hintedVariant = hasSelectionHints ? query.get('variant') : resumeStoredDraft ? restoredVariantId : '';
      const uniqueHints = !hasSelectionHints || (query.getAll('product').length === 1 && query.getAll('variant').length === 1);
      const candidate = Array.from(productSelect?.options || []).find((option) => option.value === hintedProduct && option.dataset.productUrl && !option.disabled);
      const validHints = uniqueHints && /^[1-9]\d*$/.test(hintedProduct || '') && /^[1-9]\d*$/.test(hintedVariant || '')
        && candidate && productVariants(candidate).some((variant) => String(variant.id) === hintedVariant && variant.available === true);
      sourceMode = availableSourceRoutes.length > 0 && !validHints && !hasSelectionHints
        && (resumeSourceDraft || sourceIntent || (plainDreamEntry && !resumeStoredDraft));
      if (!initialContinuation && !guidedPendingRaw && !stagedSourceEntry && (!validHints || (restoredDraft && (String(restoredDraft.productId) !== hintedProduct || String(restoredDraft.variantId) !== hintedVariant)))) {
        // A new selection invalidates product-specific output, not the saved
        // Create source. Its recorded product identity is checked on restore.
        const changedProduct = String(restoredDraft?.productId || '') !== hintedProduct;
        invalidatePreparation({ clearResolution: true, retainEditorState: changedProduct });
        if (changedProduct) {
          clearRefinedPrompt();
          if (refinedField) refinedField.hidden = true;
        }
      }
      if (productSelect) productSelect.value = validHints ? hintedProduct : '';
      populateVariantChoices();
      if (variantSelect) variantSelect.value = validHints ? hintedVariant : '';
      previousProduct = productSelect?.value || '';
      if (stagedSourceEntry) {
        // Keep the saved header and immutable records intact, but do not expose
        // an old product-bound image or guided idea as this new source choice.
        currentResolution = null;
        originalImageKey = '';
        originalRevision = 0;
        currentImageKey = '';
        retainedUploadKey = '';
        currentImageSource = '';
        currentImageName = '';
        editorState = null;
        editorDirty = false;
        sourceUploadReady = false;
        designOrigin = 'direct';
        guidedDetails = null;
        guidedIdeaCompletion = null;
        descriptionUsed = '';
        if (ideaInput) ideaInput.value = '';
        clearRefinedPrompt();
        syncGuidedControls();
        imageEditor?.unload?.();
        setEditorVisible(false);
      }
      const allowedRoutes = sourceMode ? sourceRoutes() : validHints ? (candidate.dataset.routes || '').split(',').filter((route) => routeInputs.some((input) => input.value === route)) : [];
      const effectiveRequestedRoute = resumeStoredDraft || resumeSourceDraft ? '' : requestedRoute;
      requestedRouteUnavailable = Boolean(requestedRoutes.length > 1 || (effectiveRequestedRoute && !allowedRoutes.includes(effectiveRequestedRoute)));
      let nextRoute = guidedPendingRaw ? guidedEntryRoute : requestedRouteUnavailable ? '' : stagedSourceEntry ? requestedRoute : resumeStoredDraft ? restoredRoute
        : resumeSourceDraft ? restoredRoute : (effectiveRequestedRoute || (validHints && allowedRoutes.includes(currentRoute) ? currentRoute
          : sourceMode ? '' : (allowedRoutes.includes('upload') ? 'upload' : allowedRoutes[0] || '')));
      if (!initialContinuation && !guidedPendingRaw && !stagedSourceEntry && currentRoute && currentRoute !== nextRoute) {
        if (currentRoute === 'upload' && originalImageKey) retainedUploadKey = originalImageKey;
        invalidatePreparation({ clearResolution: true, dropGenerated: currentRoute === 'create' });
        originalImageKey = nextRoute === 'upload' ? retainedUploadKey : '';
        currentImageSource = originalImageKey ? 'upload' : '';
        clearRefinedPrompt();
        if (refinedField) refinedField.hidden = true;
      }
      if (sourceMode && !stagedSourceEntry) {
        originalImageKey = nextRoute === 'upload' ? retainedUploadKey : nextRoute === 'create' ? originalImageKey : '';
        editorDirty = false;
      }
      let retainEntryOriginal = false;
      let entryWordingUpdate = false;
      if (initialContinuation) {
        const prior = initialContinuation.prior?.payload;
        retainEntryOriginal = Boolean(!initialContinuation.source && !initialContinuation.sourceIntentExplicit && prior?.originalImageKey
          && String(prior.productId) === String(productSelect?.value) && String(prior.variantId) === String(variantSelect?.value));
        const priorWords = (prior?.editorState?.textLayers || []).map(layer => String(layer.text || '')).join('\n');
        entryWordingUpdate = retainEntryOriginal && (Object.hasOwn(initialContinuation, 'wording')
          ? initialContinuation.wording !== priorWords : !priorWords.trim() && Boolean(initialContinuation.suggestedWording?.trim()));
        nextRoute = entryUsesPhoto(initialContinuation) || initialContinuation.sourceIntent === 'words' ? 'upload' : retainEntryOriginal ? prior.route
          : ['upload', 'create'].includes(initialContinuation.sourceIntent) ? initialContinuation.sourceIntent : '';
        if (nextRoute && !allowedRoutes.includes(nextRoute)) nextRoute = '';
        requestedRouteUnavailable = false;
        if (!retainEntryOriginal) {
          // Keep an older candidate for recovery, but explicit source selection
          // cannot activate another route's preparation or Words background.
          const entryCandidate = initialContinuation.candidateDraft?.route === nextRoute
            && (!initialContinuation.candidateDraft.composition || initialContinuation.sourceIntent === 'words')
            ? initialContinuation.candidateDraft : null;
          // A new entry carries words, not the prior target's background/mapping
          // authority. Only a staged candidate owns a composition to restore.
          if (initialContinuation.intent === 'choose' || !entryCandidate?.composition) composition = undefined;
          const selectedUpload = nextRoute === 'upload' && entryCandidate?.originalImageKey && !entryCandidate.composition;
          originalImageKey = selectedUpload ? entryCandidate.originalImageKey : '';
          originalRevision = selectedUpload ? entryCandidate.originalRevision : 0;
          currentImageKey = ''; retainedUploadKey = selectedUpload ? entryCandidate.retainedUploadKey || entryCandidate.originalImageKey : '';
          currentImageSource = ''; currentImageName = ''; currentImageValidForProduct = false; editorDirty = true;
          descriptionUsed = ''; currentResolution = null;
          const text = initialContinuation.candidateDraft?.editorState || prior?.editorState;
          editorState = selectedUpload ? structuredClone(text) : text?.textLayers
            ? { textSchemaVersion: text.textSchemaVersion, textRendererVersion: text.textRendererVersion, textLayers: structuredClone(text.textLayers) } : null;
          if (editorState?.textLayers) ensureImageEditor().configureText(editorState.textLayers);
          const photoEntry = entryUsesPhoto(initialContinuation);
          // Saved model descriptions are not evidence about an attached photo.
          // Retain the customer's request while composing their actual photo.
          const intent = photoEntry ? null : initialContinuation.idea?.artworkIntent;
          if (ideaInput) ideaInput.value = String(photoEntry ? initialContinuation.rawText || ''
            : entryCandidate?.idea ?? intent?.subject ?? initialContinuation.rawText ?? '').slice(0, 1800);
          clearRefinedPrompt();
          if (!photoEntry && entryCandidate?.designOrigin === 'guided' && entryCandidate?.guidedDetails) {
            guidedDetails = validatedGuidedDetails(entryCandidate.guidedDetails); designOrigin = 'guided';
          } else if (intent?.suggestedStyle && intent?.compositionIntent && intent.letteringMode === 'none') {
            try { guidedDetails = validatedGuidedDetails({ style: intent.suggestedStyle, compositionIntent: intent.compositionIntent }); designOrigin = 'guided'; guidedActionRequired = false; }
            catch (_) { guidedDetails = null; designOrigin = 'direct'; }
          } else { guidedDetails = null; designOrigin = 'direct'; }
          const target = { productId: productSelect?.value || '', variantId: variantSelect?.value || '', idea: ideaInput?.value || '' };
          guidedIdeaCompletion = retainedGuidedIdeaCompletion(entryCandidate?.guidedIdeaCompletion, target);
          if (initialContinuation.intent === 'personalise' && guidedDetails && intent?.subject?.trim()
            && initialContinuation.selection?.productId === target.productId && initialContinuation.selection?.variantId === target.variantId) {
            guidedIdeaCompletion = retainedGuidedIdeaCompletion({ ...target, brief: initialContinuation.rawText,
              candidateRef: initialContinuation.idea.candidateRef, createdAt: initialContinuation.createdAt,
              expiresAt: initialContinuation.expiresAt }, target);
          }
          draftExpiresAt = Math.min(initialContinuation.expiresAt, prior?.expiresAt || initialContinuation.expiresAt);
          syncGuidedControls();
        }
      }
      showRoute(nextRoute, false);
      if (guidedPendingRaw) {
        if (selectedWorkspace) selectedWorkspace.hidden = true;
        if (routePanel) routePanel.hidden = true;
      }
      if (sourceMode && sourceMessage && !restoreDraftError) {
        sourceMessage.textContent = nextRoute === 'upload' && retainedUploadKey ? messages.sourceUpload
          : nextRoute === 'create' && ideaInput?.value.trim() ? messages.sourceIdea : '';
      }
      if (!validHints && !guidedPendingRaw && !stagedSourceEntry) persistDraft();
      const prepareEntryWording = async (continuation, admission, isCurrent) => {
        const existing = editorState?.textLayers || continuation.prior?.payload?.editorState?.textLayers || [];
        let layers = existing;
        const explicit = Object.hasOwn(continuation, 'wording');
        const hasExisting = existing.some(layer => String(layer.text || '').trim());
        const wording = explicit ? continuation.wording : continuation.suggestedWording;
        const explicitChange = explicit && wording !== existing.map(layer => layer.text).join('\n');
        if (explicitChange && hasExisting && !window.confirm('Replace the saved wording with the words you entered?')) {
          throw new Error('Your previous wording and design have been kept.');
        }
        if (explicitChange || !hasExisting) {
          if (wording?.trim() && window.DIMDiscovery?.createWordingLayers) {
            layers = window.DIMDiscovery.createWordingLayers({ wording: [wording], fontCatalogue: unifiedRuntime.fontCatalogue,
              validateLayers: window.DIMTextModel.validateLayers });
          } else if (explicitChange) layers = [];
        }
        if (layers.length || explicitChange) {
          const manifest = unifiedRuntime.fontCatalogue;
          const origins = [...new Set(Object.values(manifest.fonts).map(font => new URL(font.url, window.location.href).origin))];
          const loader = window.DIMTextFonts.createFontLoader(manifest, { allowedOrigins: origins, baseUrl: window.location.href });
          await Promise.all(layers.map(layer => loader.ensure(layer.fontId, layer.fontRevision, { text: layer.text })));
          if (!isCurrent() || !admissionMatchesResolution(admission, currentResolution, productSelect.value, variantSelect.value)) throw new Error(messages.profileStale);
          editorState = { ...(editorState || {}), textSchemaVersion: 1, textRendererVersion: 1, textLayers: structuredClone(layers) };
          ensureImageEditor().configureText(layers);
        }
      };
      const wordsRecord = () => ({ id: '', source: 'words', blob: null, originalName: 'dream-it-made-words.png',
        createdAt: composition.createdAt, expiresAt: composition.expiresAt, composition: structuredClone(composition) });
      const openWordsEntry = () => {
        editorRoot?.querySelector('[data-e-editor-task="words"]')?.click();
        if (!(imageEditor?.getEditorState?.()?.textLayers || []).length) {
          editorRoot?.querySelector('[data-dim-text-empty-add]')?.click();
        }
      };
      const commitWordsCandidate = async () => {
        if (!composition || !completeWords() || sourceContinuation()?.sourceIntent !== 'words') return false;
        const owner = ++wordsCommitOwner;
        const snapshot = captureSelection();
        const entry = sourceIntake.read();
        const state = structuredClone(imageEditor.getEditorState());
        const editorRevision = imageEditor.revision;
        const isCurrent = () => owner === wordsCommitOwner && selectionMatches(snapshot)
          && imageEditor.revision === editorRevision && sourceIntake.read().revision === entry.revision;
        let prepared;
        try {
          const admission = await unifiedRuntime.requireAdmission({ productId: snapshot.productId, variantId: snapshot.variantId, route: snapshot.route });
          if (!isCurrent() || !admissionMatchesResolution(admission, currentResolution, snapshot.productId, snapshot.variantId)) throw new Error(messages.profileStale);
          validateWordsComposition(composition, admission.resolution);
          prepared = await prepareReplacementEditor(wordsRecord(), snapshot, state);
          if (!isCurrent()) { discardReplacementEditor(prepared); return false; }
          if (typeof prepared.editor.ensurePreparedTextReady !== 'function') throw new Error('This lettering needs a compatible editor. Your previous design has been kept.');
          await prepared.editor.ensurePreparedTextReady(prepared.candidate, { placementConstraints: admission.textPlacement });
          if (!isCurrent()) { discardReplacementEditor(prepared); return false; }
          const finalAdmission = await unifiedRuntime.requireAdmission({ productId: snapshot.productId, variantId: snapshot.variantId, route: snapshot.route });
          if (!isCurrent() || !admissionMatchesResolution(finalAdmission, currentResolution, snapshot.productId, snapshot.variantId)
            || JSON.stringify(finalAdmission.textPlacement) !== JSON.stringify(admission.textPlacement)) throw new Error(messages.profileStale);
          validateWordsComposition(composition, finalAdmission.resolution);
          await commitSourceReplacement({ record: wordsRecord(), snapshot, preparedEditor: prepared, expectedSourceRevision: entry.revision });
          setUploadStatus('success', 'Your words are ready for Preview.'); updateContext(); updateProductLinks();
          return true;
        } catch (error) {
          discardReplacementEditor(prepared);
          if (selectionMatches(snapshot)) setUploadStatus('error', error.message || messages.uploadPrepareError);
          return false;
        }
      };
      const chooseWordsOnly = async ({ continuing = false } = {}) => {
        if (busy || replacementPending || uploadBusy || forwardBusy || successorRestoreBlocked) return false;
        const snapshot = captureSelection();
        try {
          if (unifiedRuntime.hasJourneyDataReuse !== true) await unifiedRuntime.verify();
          requireCurrentSelection(snapshot);
          if (!wordsCreationAllowed()) throw new Error('Words only is not available for this product and option. Your design has been kept.');
          if (unifiedRuntime.paid?.pending?.()) throw new Error('A previous request still needs a status check. Your saved design has been kept.');
          const admission = await unifiedRuntime.requireAdmission({ productId: snapshot.productId, variantId: snapshot.variantId, route: snapshot.route });
          if (!selectionMatches(snapshot) || !admissionMatchesResolution(admission, currentResolution, snapshot.productId, snapshot.variantId)) throw new Error(messages.profileStale);
          if (continuing) {
            const entry = sourceIntake.read();
            await prepareEntryWording(entry.continuation, admission, () => selectionMatches(snapshot) && sourceIntake.read().revision === entry.revision);
          }
          const now = Date.now();
          const sourceDeadline = sourceIntake.read().record?.expiresAt || draftExpiresAt || now + draftTtl;
          const expiresAt = Math.min(now + draftTtl, sourceDeadline, composition?.expiresAt || Infinity);
          const nextComposition = validateWordsComposition({ kind: 'words', revision: (composition?.revision || 0) + 1, createdAt: now, expiresAt,
            capability: structuredClone(admission.resolution.words_only) }, admission.resolution);
          if (!continuing && originalImageKey && !window.confirm('Use words only for this design? Your current design is kept until the lettering is ready.')) return false;
          requireCurrentSelection(snapshot);
          sourceIntake.continue({ intent: 'words', sourceIntent: 'words', selection: { productId: snapshot.productId, variantId: snapshot.variantId, options: [] },
            eligibility: { composition: nextComposition, resolution: admission.resolution } });
          composition = nextComposition;
          originalImageKey = ''; originalRevision = 0; currentImageKey = ''; retainedUploadKey = '';
          currentImageSource = 'words'; currentImageName = 'dream-it-made-words.png'; descriptionUsed = '';
          currentImageValidForProduct = false; editorDirty = true; draftExpiresAt = expiresAt;
          editorState = { textSchemaVersion: 1, textRendererVersion: 1, textLayers: structuredClone(editorState?.textLayers || []) };
          designOrigin = 'direct'; guidedDetails = null; syncGuidedControls();
          await loadUploadEditor(wordsRecord(), editorState, true);
          requireCurrentSelection(snapshot);
          if (!persistDraft()) throw new Error(messages.uploadStorageError);
          scheduleExpiry(); updateContext(); updateProductLinks();
          if (completeWords()) return await commitWordsCandidate();
          setUploadStatus('success', 'Add your words to continue to Preview.');
          openWordsEntry();
          return true;
        } catch (error) {
          if (selectionMatches(snapshot)) setUploadStatus('error', error.message || messages.uploadPrepareError);
          return false;
        }
      };
      wordsOnlyButton?.addEventListener('click', () => { void chooseWordsOnly(); });
      const restorePreviousDesign = async () => {
        if (busy || replacementPending || uploadBusy || forwardBusy || !sourceContinuation()?.prior) return;
        try {
          generationFailure = null; closeReplacementChoices();
          wordsCommitOwner += 1; selectionRevision += 1; exportSnapshot = null;
          const restored = await sourceIntake.rollback();
          successorRestoreBlocked = false;
          sourceMode = !restored.payload.productId;
          restoreDraft(); populateVariantChoices(); if (variantSelect) variantSelect.value = restored.payload.variantId || '';
          if (restored.payload.productId && await resolveSelectedProduct({ restoreVariantId: restored.payload.variantId })) await restoreDraftImage();
          updateContext(); updateProductLinks(); queueFocus(() => editorRoot, 'nearest');
        } catch (error) { setUploadStatus('error', error.message || messages.uploadStorageError); }
      };
      wordsOnlyReturn?.addEventListener('click', restorePreviousDesign);
      replacementKeep?.addEventListener('click', restorePreviousDesign);
      const restoreEntryCandidate = async ({ reuseUpload = false } = {}) => {
        const entry = sourceIntake.read();
        const continuation = entry.continuation;
        if (!continuation) return false;
        reuseUpload = reuseUpload || continuation.sourceIntent === 'upload' && continuation.candidateDraft?.route === 'upload'
          && Boolean(originalImageKey) && continuation.candidateDraft.originalImageKey === originalImageKey;
        if (entry.continuationStatus !== 'ready') {
          setUploadStatus('error', 'This new source has expired. Your previous design has been kept; return to it or choose another image.');
          return false;
        }
        if (unifiedRuntime?.paid?.pending?.()) {
          setUploadStatus('error', 'A previous request still needs a status check. Your saved design has been kept.'); return false;
        }
        if (canIntakeSource()) {
          if (entryUsesPhoto(continuation)) {
            const record = await sourceIntake.getCandidate();
            if (!record) throw new Error(messages.sourceExpired || messages.uploadStorageError);
            originalImageKey = record.id; originalRevision = Number(record.createdAt); retainedUploadKey = record.id;
            currentImageName = record.originalName; draftExpiresAt = record.expiresAt;
            showSourceRecord(record); persistDraft();
          }
          return false;
        }
        if (!hasResolvedProduct()) return false;
        if (continuation.sourceIntent === 'words') {
          if (composition) {
            if (!(await restoreDraftImage())) return false;
            return completeWords() ? await commitWordsCandidate() : false;
          }
          return await chooseWordsOnly({ continuing: true });
        }
        const snapshot = captureSelection();
        const isCurrent = () => selectionMatches(snapshot) && sourceIntake.read().revision === entry.revision;
        const selection = continuation.selection && continuation.selection.productId === snapshot.productId && continuation.selection.variantId === snapshot.variantId
          ? continuation.selection : { productId: snapshot.productId, variantId: snapshot.variantId };
        const admission = await unifiedRuntime.requireAdmission({ ...selection, route: snapshot.route });
        if (!isCurrent() || !admissionMatchesResolution(admission, currentResolution, snapshot.productId, snapshot.variantId)) throw new Error(messages.profileStale);
        await prepareEntryWording(continuation, admission, isCurrent);
        if (!isCurrent()) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
        if (!entryUsesPhoto(continuation) && !entryWordingUpdate && !reuseUpload) { persistDraft(); return false; }
        const record = entryUsesPhoto(continuation) ? await sourceIntake.getCandidate() : await getImageRecord(originalImageKey);
        if (!record || !isCurrent()) throw new Error(messages.sourceExpired || messages.uploadStorageError);
        if (record.sessionId !== imageSessionId || !profileClient.isUnexpired(record) || (record.source === 'generated' && !generatedSourceCompatible(record))) throw new Error(messages.sourceExpired || messages.uploadStorageError);
        const validation = validateStoredUploadRecord(record);
        if (validation) throw validation;
        await inspectImageBlob(record.blob, { expectedMime: record.mimeType, maximumBytes: uploadRule().maximumBytes });
        if (!isCurrent()) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
        const rebindUpload = entryUsesPhoto(continuation) || reuseUpload && (record.productId !== snapshot.productId
          || !profileClient.exactIdentity(record.resolution, currentResolution));
        const bound = rebindUpload ? { ...record, id: `${imageSessionId}:${snapshot.productId}:${createIdentifier()}`, productId: snapshot.productId, resolution: structuredClone(currentResolution) } : record;
        let prepared = null, stored = false;
        try {
          if (rebindUpload) { await putImageRecord(bound); stored = true; }
          if (!isCurrent()) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
          const restoredState = entryWordingUpdate || reuseUpload && !rebindUpload ? editorState : editorState?.textLayers
            ? { textSchemaVersion: editorState.textSchemaVersion, textRendererVersion: editorState.textRendererVersion,
              textLayers: structuredClone(editorState.textLayers) } : null;
          prepared = await prepareReplacementEditor(bound, snapshot, restoredState);
          if (!isCurrent()) throw new Error(messages.sourceStorageError || messages.uploadStorageError);
          const finalAdmission = await unifiedRuntime.requireAdmission({ ...selection, route: snapshot.route });
          if (!isCurrent() || !admissionMatchesResolution(finalAdmission, currentResolution, snapshot.productId, snapshot.variantId)
            || JSON.stringify(finalAdmission.textPlacement) !== JSON.stringify(admission.textPlacement)) throw new Error(messages.profileStale);
          const committedSelection = await commitSourceReplacement({ record: bound, snapshot, preparedEditor: prepared, retainUpload: bound.source === 'upload',
            description: bound.source === 'generated' ? descriptionUsed : '', expectedSourceRevision: entry.revision });
          if (!selectionMatches(committedSelection)) return true;
          setUploadStatus('success', messages.uploadEditorReady); setResult('success', messages.uploadEditorReady, '');
          updateContext(); updateProductLinks(); return true;
        } catch (error) {
          discardReplacementEditor(prepared);
          if (stored && bound.id !== originalImageKey) void deleteImageRecord(bound.id).catch(() => {});
          throw error;
        }
      };
      const bootstrapResolvedState = async () => {
        if (successorRestoreBlocked) { updateContext(); setUploadStatus('error', restoreDraftError); return; }
        updateProductLinks();
        updateContext();
        clearDraftImage({ forgetStored: false });
        if (restoreDraftError && !canIntakeSource()) setUploadStatus('error', restoreDraftError);
        if (sourceContinuation()) {
          try {
            const entry = sourceIntake.read();
            if ((entry.continuationStatus === 'expired' || entry.continuation.candidateDraft?.expiresAt <= Date.now())
              && entry.continuation.prior?.expiresAt > Date.now()) {
              const restored = await sourceIntake.rollback({ expectedRevision: entry.revision });
              sourceMode = !restored.payload.productId;
              restoreDraft(); populateVariantChoices(); if (variantSelect) variantSelect.value = restored.payload.variantId || '';
              if (restored.payload.productId && await resolveSelectedProduct({ restoreVariantId: restored.payload.variantId })) await restoreDraftImage();
              setUploadStatus('error', 'The new source expired. Your previous design has been restored; Preview approval must be checked again.');
              return;
            }
            if (canIntakeSource()) { await restoreEntryCandidate(); return; }
            if (!validHints || !currentRoute) {
              setUploadStatus('', initialContinuation?.sourceIntent === 'words'
                ? 'Words only is not available for this product yet. Choose a picture or create an illustration to continue.'
                : 'Choose Upload for your picture, or Create to make an illustration.'); return;
            }
            const resolved = await resolveSelectedProduct({ restoreVariantId: hintedVariant });
            if (!resolved) return;
            if (retainEntryOriginal && !entryWordingUpdate) {
              await sourceIntake.rollback();
              await restoreDraftImage();
            } else await restoreEntryCandidate();
          } catch (error) { if (!departed) setUploadStatus('error', error.message || messages.uploadPrepareError); }
          return;
        }
        if (canIntakeSource()) { await restoreUnboundSource(); return; }
        if (!validHints || requestedRouteUnavailable) {
          if (requestedRouteUnavailable && validHints) setProfileStatus(messages.routeUnavailable, true);
          return;
        }
        setResult('empty', messages.emptyHeading, messages.emptyText);
        const resolved = await resolveSelectedProduct({ restoreVariantId: hintedVariant });
        if (resolved) await restoreDraftImage();
      };
      // Signing in refreshes selection/profile state or resumes the customer's
      // already-selected handoff. Replacement still needs its existing
      // confirmation; only its explicit Generate action may request an image.
      // This never submits native artwork.
      const onRuntimeAuthenticated = async () => {
        if (departed || successorRestoreBlocked || busy || replacementPending || forwardBusy || guidedHandoffInFlight) return;
        if (guidedPendingRaw && window.sessionStorage.getItem(sceneStorage.keys.pending) === guidedPendingRaw) {
          try { await acceptGuidedDesign(JSON.parse(guidedPendingRaw), guidedPendingRaw); }
          catch (error) { reportGuidedFailure(error); }
          return;
        }
        if (!hasProduct()) return;
        const resolved = await resolveSelectedProduct({ restoreVariantId: variantSelect?.value || hintedVariant });
        if (resolved && !departed) {
          if (sourceContinuation()) { try { await restoreEntryCandidate(); } catch (error) { setUploadStatus('error', error.message || messages.uploadPrepareError); } }
          else await restoreDraftImage();
        }
      };
      document.addEventListener('dim:runtime-authenticated', onRuntimeAuthenticated);
      const resumeGuidedGeneration = () => {
        const pending = guidedGeneration;
        if (!pending) return;
        if (departed || Date.now() >= pending.expiresAt || draftRevision !== pending.draftRevision
          || storage.get() !== pending.header || !selectionMatches(pending.selection)
          || (pending.kind === 'direct' ? originalImageKey !== pending.original || descriptionText(activeDescriptionInput()) !== pending.description
            : originalImageKey || designOrigin !== 'guided')
          || currentRoute !== 'create' || !generationAllowed()) { guidedGeneration = null; return; }
        if (busy || replacementPending || forwardBusy) return;
        if (studio.querySelector('.cf-turnstile') && !getTurnstileToken()) {
          // The deliberate action is waiting, not failed or generating yet.
          setResult('pending', messages.emptyHeading, messages.security);
          return;
        }
        // Consume before entering the paid owner. Neither failure nor a late
        // challenge callback can automatically dispatch this intention again.
        guidedGeneration = null;
        void generateImage(pending.action);
      };
      const onGuidedHumanCheck = () => { window.setTimeout(resumeGuidedGeneration, 0); };
      document.addEventListener('dim:studio-human-check', onGuidedHumanCheck);
      const onHumanCheckExpired = () => {
        if (securityPanel?.hidden || departed || busy) return;
        setResult('security', messages.error, 'Human Check expired. Please complete it again before continuing.', true);
      };
      const onHumanCheckFailed = () => {
        if (securityPanel?.hidden || departed || busy) return;
        setResult('security', messages.error, 'Human Check could not be completed. Please try it again.', true);
      };
      document.addEventListener('dim:studio-human-check-expired', onHumanCheckExpired);
      document.addEventListener('dim:studio-human-check-failed', onHumanCheckFailed);
      const acceptGuidedDesign = async (handoff, pendingRaw = '') => {
        if (!sceneStorage || !unifiedRuntime?.enabled || unifiedRuntime.features?.text !== true || busy || replacementPending || forwardBusy || guidedHandoffInFlight) {
          throw new Error('Finish the current action before choosing a new design.');
        }
        if (unifiedRuntime.paid?.pending()) {
          if (paidStatusButton) paidStatusButton.hidden = false;
          throw new Error('A previous request still needs a status check. Your saved artwork is unchanged; no new request has been sent.');
        }
        const previousRaw = storage.get();
        const previousDraft = previousRaw ? JSON.parse(previousRaw) : null;
        const previousRevision = previousDraft?.draftRevision || 0;
        const operationRevision = selectionRevision;
        let adopted = false;
        guidedHandoffInFlight = true;
        guidedImportPending = true;
        updateContext();
        const current = () => !departed && selectionRevision === operationRevision && storage.get() === previousRaw
          && (!pendingRaw || window.sessionStorage.getItem(sceneStorage.keys.pending) === pendingRaw);
        try {
          const targetRoute = handoff.action?.kind === 'upload' ? 'upload' : 'create';
          const admission = await unifiedRuntime.requireAdmission({ ...handoff.selection, route: targetRoute });
          if (!current()) throw new Error('The saved design changed. Choose the idea again.');
          const selected = admission.selection;
          const design = window.DIMGuidedHandoff.validate(handoff, {
            draftRevision: previousRevision, origin: window.location.origin, runtime: unifiedRuntime.runtime,
            context: admission.context, nowMs: Date.now()
          }, value => JSON.stringify(value) === JSON.stringify(selected));
          const product = Array.from(productSelect.options).find(option => option.value === selected.productId && !option.disabled
            && (option.dataset.routes || '').split(',').includes(targetRoute)
            && productVariants(option).some(variant => String(variant.id) === selected.variantId && variant.available === true));
          if (!product) throw new Error('This product is no longer available for that image choice.');
          const resolution = profileClient.validateResolution((await profileClient.resolveProfile(endpoint, selected.variantId, targetRoute)).resolution);
          if (!admissionMatchesResolution(admission, resolution, selected.productId, selected.variantId)) throw new Error('The product preparation changed. Choose the idea again.');
          const continuation = sourceContinuation();
          const priorDraft = previousDraft?.entryContinuation?.candidateDraft || previousDraft?.entryContinuation?.prior?.payload || previousDraft;
          // An owned, accepted upload is source material, not a request to
          // generate or replace it. Keep its lifetime and revalidate it against
          // the selected target before the existing protected adoption.
          let reusableUpload = null;
          if (targetRoute === 'upload' && sourceIntake) {
            if (continuation?.source) reusableUpload = await sourceIntake.getCandidate();
            if (!reusableUpload) {
              const key = priorDraft?.retainedUploadKey || (priorDraft?.route === 'upload' ? priorDraft.originalImageKey : '');
              if (key) reusableUpload = await getImageRecord(key);
            }
            if (reusableUpload && (reusableUpload.source !== 'upload' || reusableUpload.sessionId !== imageSessionId
              || !profileClient.isUnexpired(reusableUpload) || validateStoredUploadRecord(reusableUpload, product))) reusableUpload = null;
          }
          if (!current()) throw new Error('The saved design changed. Choose the idea again.');
          const priorLayers = priorDraft?.editorState?.textLayers || [];
          const ownsPriorText = (priorDraft?.originalImageKey || priorDraft?.composition) && Array.isArray(priorDraft?.editorState?.textLayers);
          const chosenLayers = continuation && Object.hasOwn(continuation, 'wording') ? design.textLayers
            : ownsPriorText || priorLayers.some(layer => String(layer.text || '').trim()) ? priorLayers : design.textLayers;
          const manifest = unifiedRuntime.fontCatalogue;
          const origins = [...new Set(Object.values(manifest.fonts).map(font => new URL(font.url, window.location.href).origin))];
          const fonts = window.DIMTextFonts.createFontLoader(manifest, { allowedOrigins: origins, baseUrl: window.location.href });
          await Promise.all(chosenLayers.map(layer => fonts.ensure(layer.fontId, layer.fontRevision, { text: layer.text })));
          if (!current()) throw new Error('The saved design changed. Choose the idea again.');
          if (targetRoute === 'create' && previousDraft?.originalImageKey && !(await confirmImageReplacement())) return false;
          if (!current()) throw new Error('The saved design changed. Choose the idea again.');
          // Fonts and the customer decision can outlive a publication/profile.
          // Re-admit at the commit boundary, never mix their earlier identities.
          const finalAdmission = await unifiedRuntime.requireAdmission({ ...selected, route: targetRoute });
          if (!current() || !admissionMatchesResolution(finalAdmission, resolution, selected.productId, selected.variantId)
            || JSON.stringify(finalAdmission.textPlacement) !== JSON.stringify(admission.textPlacement)) throw new Error('The product preparation changed. Choose the idea again.');
          window.DIMGuidedHandoff.validate(handoff, {
            draftRevision: previousRevision, origin: window.location.origin, runtime: unifiedRuntime.runtime,
            context: finalAdmission.context, nowMs: Date.now()
          }, value => JSON.stringify(value) === JSON.stringify(finalAdmission.selection));
          const now = Date.now();
          const expiresAt = Math.min(design.expiresAt, previousDraft?.expiresAt || now + draftTtl, reusableUpload?.expiresAt || Infinity, now + draftTtl);
          const guidedIntent = JSON.parse(design.creativeBrief);
          if (typeof guidedIntent.subject !== 'string' || !guidedIntent.subject.trim() || guidedIntent.subject.length > 1000
            || typeof guidedIntent.style !== 'string' || !guidedIntent.style.trim() || guidedIntent.style.length > 80
            || typeof guidedIntent.compositionIntent !== 'string' || !guidedIntent.compositionIntent.trim() || guidedIntent.compositionIntent.length > 1000) throw new Error('The illustration description is not supported.');
          const candidate = {
            version: 7, draftRevision: previousRevision + 1, createdAt: expiresAt - draftTtl, expiresAt,
            productId: selected.productId, variantId: selected.variantId, route: targetRoute, resolution,
            idea: guidedIntent.subject, guidedDetails: { style: guidedIntent.style, compositionIntent: guidedIntent.compositionIntent }, prompt: '', helperPromptContext: '', descriptionUsed: '',
            originalImageKey: reusableUpload?.id || '', originalRevision: reusableUpload?.createdAt || 0, retainedUploadKey: reusableUpload?.id || previousDraft?.retainedUploadKey || '',
            imageKey: '', imageSource: '', imageName: '', editorDirty: true,
            editorState: { ...(reusableUpload && priorDraft?.originalImageKey === reusableUpload.id
                && reusableUpload.productId === selected.productId && profileClient.exactIdentity(reusableUpload.resolution, resolution) ? priorDraft.editorState : {}),
              textSchemaVersion: design.textSchemaVersion, textRendererVersion: design.textRendererVersion,
              textLayers: structuredClone(chosenLayers) }, designOrigin: 'guided',
            ...(design.action?.kind === 'generate' ? { guidedActionRequired: true } : {})
          };
          // No selection events or vendor listeners see a speculative selection.
          // One protected header write commits the staged idea after every check.
          if (design.action) {
            const actionKey = `${sceneStorage.keys.pending}:consumed-action`;
            const claim = JSON.stringify({ id: design.action.id, draftRevision: previousRevision, expiresAt: design.expiresAt });
            let previous;
            try { previous = JSON.parse(window.sessionStorage.getItem(actionKey) || 'null'); } catch (_) { throw new Error('The selected action could not be confirmed. Choose the idea again.'); }
            if (previous?.id === design.action.id) throw new Error('This idea action was already opened. Check the current image or request status.');
            window.sessionStorage.setItem(actionKey, claim);
            if (window.sessionStorage.getItem(actionKey) !== claim) throw new Error('The selected action could not be saved. Your previous design is unchanged.');
          }
          if (sourceIntake) {
            sourceIntake.continue({ intent: targetRoute, rawText: guidedIntent.subject });
            sourceIntake.stage({ candidateDraft: candidate, ...(targetRoute === 'create' || reusableUpload && reusableUpload.productId !== '' ? { source: null } : {}) });
          } else sceneStorage.writeHeader(candidate, candidate.expiresAt);
          adopted = true;
          stagedSourceEntry = null;
          guidedImportPending = false;
          cancelInFlightRequests(); selectionRevision += 1;
          imageEditor?.unload?.(); setEditorVisible(false);
          restoreDraft(); populateVariantChoices(); variantSelect.value = candidate.variantId;
          currentResolution = resolution;
          ensureImageEditor().configureText(candidate.editorState.textLayers);
          updateContext(); updateProductLinks();
          if (reusableUpload) await restoreEntryCandidate({ reuseUpload: true });
          setUploadStatus('', '');
          setResult('empty', messages.emptyHeading, targetRoute === 'upload' ? '' : design.action?.kind === 'generate' ? '' : 'Your idea and editable wording are ready. Choose Create to make the illustration.');
          if (pendingRaw && window.sessionStorage.getItem(sceneStorage.keys.pending) === pendingRaw) window.sessionStorage.removeItem(sceneStorage.keys.pending);
          guidedPendingRaw = '';
          queueFocus(() => reusableUpload ? editorRoot : targetRoute === 'upload' ? uploadChooseButton : studio.querySelector('[data-guided-chosen-heading]'));
          if (design.action?.kind === 'generate') {
            guidedGeneration = { selection: captureSelection(), draftRevision, header: storage.get(), expiresAt: design.expiresAt,
              action: { id: design.action.id, createdAt: design.createdAt } };
            resumeGuidedGeneration();
          }
          return true;
        } finally {
          guidedImportPending = false;
          guidedHandoffInFlight = false;
          updateContext();
          if (!adopted && current() && previousDraft) {
            restoreDraft(); populateVariantChoices(); variantSelect.value = previousDraft.variantId;
            updateContext(); updateProductLinks();
            if (originalImageKey) void restoreDraftImage();
          }
        }
      };
      const reportGuidedFailure = error => {
        if (departed) return;
        if (error?.code === 'DEV_SIGN_IN_REQUIRED') {
          guidedImportPending = false;
          updateContext();
          setUploadStatus('error', error.message);
          return;
        }
        // A failed handoff never becomes an automatic paid retry on reload or
        // sign-in. Keep the artwork and paid-operation record, clearing only
        // this page's still-owned incoming choice.
        if (guidedPendingRaw && window.sessionStorage.getItem(sceneStorage.keys.pending) === guidedPendingRaw) {
          window.sessionStorage.removeItem(sceneStorage.keys.pending);
        }
        guidedPendingRaw = '';
        guidedImportPending = false;
        if (!storage.get()) showRoute(requestedRoute || '', false);
        updateContext(); updateProductLinks();
        setUploadStatus('error', error.message || 'This idea could not be opened. Your saved artwork is unchanged. Choose the idea again when you are ready.');
      };
      studio.acceptGuidedDesign = acceptGuidedDesign;
      studio.addEventListener('dim:guided-design-pending', event => { event.preventDefault(); void acceptGuidedDesign(event.detail).catch(reportGuidedFailure); });
      if (guidedPendingRaw) {
        try { void acceptGuidedDesign(JSON.parse(guidedPendingRaw), guidedPendingRaw).catch(reportGuidedFailure); }
        catch (error) { guidedImportPending = false; reportGuidedFailure(error); }
      } else void bootstrapResolvedState().finally(() => {
        if (!unifiedRuntime?.paid || !paidStatusButton) return;
        try {
          if (unifiedRuntime.paid.pending()) {
            paidStatusButton.hidden = false;
            setResult('error', messages.error, 'A previous request still needs a status check. Your saved artwork is available; no new request has been sent.', true);
          }
        } catch (_) {
          paidStatusButton.hidden = false;
          setResult('error', messages.error, 'The previous request record could not be read. Your saved artwork has been kept; no new request has been sent.', true);
        }
      });
      // Import is a deliberate action available only to the production primary
      // successor. The legacy key/database remain available to old open pages.
      if (sceneStorage?.keys.legacyImportKey && sceneStorage.readHeader().status === 'empty') {
        let legacyAvailable = false;
        try { legacyAvailable = Boolean(window.sessionStorage.getItem(sceneStorage.keys.legacyImportKey)); } catch (_) {}
        if (legacyAvailable) {
          const importButton = document.createElement('button');
          importButton.type = 'button'; importButton.className = 'button button--secondary';
          importButton.textContent = 'Open my earlier design';
          studio.prepend(importButton);
          importButton.addEventListener('click', async () => {
            if (importButton.disabled || busy || replacementPending || originalImageKey) return;
            importButton.disabled = true;
            const owner = selectionRevision;
            let sourceFingerprint = ''; let importedSession = '';
            try {
              if (unifiedRuntime.hasJourneyDataReuse !== true) await unifiedRuntime.verify({ force: true });
              await sceneStorage.importLegacy({
                confirmed: () => true,
                isCurrent: () => !departed && selectionRevision === owner && sceneStorage.readHeader().status === 'empty',
                verifyOriginalSource: async (record, legacy) => {
                  if (legacy.version !== 6 || record.id !== legacy.originalImageKey || !['upload', 'generated'].includes(record.source)
                    || record.source !== (legacy.route === 'create' ? 'generated' : 'upload') || !(record.blob instanceof Blob)
                    || !record.sessionId || !profileClient.isUnexpired(record)) return false;
                  const inspected = await window.DreamItImageInspector.inspect(record.blob, { expectedMime: record.mimeType });
                  if (inspected.width !== record.width || inspected.height !== record.height) return false;
                  sourceFingerprint = await window.DIMSceneIdentity.fingerprint(record.blob);
                  importedSession = record.sessionId;
                  return !departed && selectionRevision === owner;
                },
                verifyCopiedOriginal: async copied => copied.sessionId === importedSession
                  && await window.DIMSceneIdentity.fingerprint(copied.blob) === sourceFingerprint
              });
              window.sessionStorage.setItem(`${draftKey}:image-session`, importedSession);
              // Normal restoration re-resolves the exact product and regenerates
              // its composite; no legacy preview or approval is imported.
              window.location.reload();
            } catch (error) { setUploadStatus('error', error.message || 'The earlier design could not be opened. It has been kept unchanged.'); }
            finally { importButton.disabled = false; }
          });
        }
      }
      const flushForNavigation = () => {
        if (departed || successorRestoreBlocked) return true;
        // In-memory candidates must finish or use their existing recovery before
        // departure. Saving the prior draft is not proof that a candidate is saved.
        if (uploadBusy || replacementPending || forwardBusy || editorLoading || guidedImportPending
          || descriptionReplacement || generatedRecovery?.hasPending()) return false;
        // The unchanged prior is saved, but a newly typed source description is
        // still provisional until the existing Continue/replacement action.
        if (stagedSourceEntry) return !ideaInput?.value.trim() && !promptOutput?.value.trim();
        if (!draftExpiresAt || draftExpiresAt <= Date.now()) return true;
        if (['upload', 'create'].includes(currentRoute) && imageEditor?.ready) {
          editorState = imageEditor.getEditorState();
          editorDirty = imageEditor.dirty === true;
        }
        window.clearTimeout(saveTimer);
        return persistDraft();
      };
      const onJourneyLeave = event => event.detail?.waitUntil?.(flushForNavigation());
      document.addEventListener('dim:before-journey-leave', onJourneyLeave);
      const persistDraftOnPagehide = () => {
        if (departed) return;
        clearUploadProgress();
        document.removeEventListener('dim:runtime-authenticated', onRuntimeAuthenticated);
        document.removeEventListener('dim:studio-human-check', onGuidedHumanCheck);
        document.removeEventListener('dim:studio-human-check-expired', onHumanCheckExpired);
        document.removeEventListener('dim:studio-human-check-failed', onHumanCheckFailed);
        guidedGeneration = null;
        cancelInFlightRequests();
        if (['upload', 'create'].includes(currentRoute) && imageEditor?.ready) {
          editorState = imageEditor.getEditorState();
          editorDirty = imageEditor.dirty === true;
        }
        window.clearTimeout(saveTimer);
        persistDraft();
        imageEditor?.unload?.();
        if (sourceImage) sourceImage.removeAttribute('src');
        if (generatedImage) generatedImage.removeAttribute('src');
        if (streamingPreviewImage) streamingPreviewImage.removeAttribute('src');
        if (sourceObjectUrl) {
          URL.revokeObjectURL(sourceObjectUrl);
          sourceObjectUrl = '';
        }
        if (currentImageObjectUrl) {
          URL.revokeObjectURL(currentImageObjectUrl);
          currentImageObjectUrl = '';
        }
        departed = true;
        selectionRevision += 1;
        resolutionRevision += 1;
        exportSnapshot = null;
      };
      const warnBeforeUnload = (event) => {
        if (!activePaidRequest && !generatedRecovery?.hasPending()) return;
        event.preventDefault();
        event.returnValue = '';
        return '';
      };
      const restoreOnPageshow = (event) => {
        if (event.persisted) window.location.reload();
      };
      const handleSectionUnload = (event) => {
        if (!event.target?.contains(studio)) return;
        persistDraftOnPagehide();
        generatedRecovery?.destroy();
        imageEditor?.destroy?.();
        if (currentImageObjectUrl) URL.revokeObjectURL(currentImageObjectUrl);
        if (sourceObjectUrl) URL.revokeObjectURL(sourceObjectUrl);
        window.clearTimeout(uploadStatusTimer);
        window.removeEventListener('pagehide', persistDraftOnPagehide);
        window.removeEventListener('pageshow', restoreOnPageshow);
        window.removeEventListener('beforeunload', warnBeforeUnload);
        document.removeEventListener('dim:before-journey-leave', onJourneyLeave);
        document.removeEventListener('shopify:section:unload', handleSectionUnload);
        studio.dataset.initialised = 'false';
      };
      window.addEventListener('pagehide', persistDraftOnPagehide);
      window.addEventListener('pageshow', restoreOnPageshow);
      window.addEventListener('beforeunload', warnBeforeUnload);
      document.addEventListener('shopify:section:unload', handleSectionUnload);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('dim:runtime-authenticated', initialise);
})();
